import Phaser from "phaser";
import {
  BACKGROUND_PRESETS,
  DEBUG_FAST_FORWARD,
  DEFAULT_BACKGROUND_ID,
  DEFAULT_DEBUG_MODE,
  GAMEPLAY,
  NATIVE_HEIGHT,
  NATIVE_WIDTH,
  PALETTE,
  TILE_SIZE,
  UGC_CYAN,
  backgroundPresetById,
} from "../config";
import type { InventoryItemKind, Skill, Variant } from "../config";
import type {
  AuthorIdentity,
  AuthoredSidequest,
  InventoryItem,
  PuzzleKind,
  SessionConfig,
  SessionSummary,
  SkillId,
} from "../types";
import { InputService } from "../services/InputService";
import { TelemetryService } from "../services/TelemetryService";
import {
  Parallax,
  FOREST_LAYER_KEYS,
  PIXEL_FOREST_KEYS,
  PIXEL_FOREST_FACTORS,
  SNOWY_FOREST_KEYS,
  SNOWY_FOREST_FACTORS,
} from "../services/Parallax";
import { AudioService } from "../services/AudioService";
import {
  AuthorIdentityService,
  mainLevelAuthorSeed,
  subLevelAuthorSeed,
} from "../services/AuthorIdentityService";
import { ShopService } from "../services/ShopService";
import { makeSeededRng } from "../services/seededRng";
import { ProfileTooltipService } from "../services/ProfileTooltipService";
import { AuthoredSidequestService } from "../services/AuthoredSidequestService";
import { SET_PIECE_CATALOG, setPieceById, spawnSetPiece } from "../services/setPieceCatalog";
import {
  RunnerWallet,
  WalletIncomeTracker,
  type RunnerWalletHost,
  type Wallet,
} from "../services/Wallet";
import { AUTHOR_BADGE_HEIGHT, AUTHOR_BADGE_WIDTH, AuthorBadge } from "../ui/AuthorBadge";
import { AuthorTowerOverlay } from "../ui/AuthorTowerOverlay";
import { PauseMenuButton } from "../ui/PauseMenuButton";
import { ProfileTooltipCard } from "../ui/ProfileTooltipCard";
import { WalletHud } from "../ui/WalletHud";
import { t } from "../i18n";
import type { StringKey } from "../i18n";

/** Forest pack rotation for nested sub-levels. Depth 0 reuses the
 *  Mysterious Forest (the main loop); depth 1 swaps to the lush
 *  Pixel Art Forest; depth 2 swaps to the cold Snowy Forest; deeper
 *  cycles. Each entry is a (keys, scrollFactors) pair the Parallax
 *  service can rebuild from. */
const FOREST_PACKS: ReadonlyArray<{
  keys: ReadonlyArray<string>;
  factors: ReadonlyArray<number>;
  label: string;
  /** Pack's signature colour — used to tint the sub-level end-gradient
   *  and the trailing fog rectangle so the "level ending" mood matches
   *  the level's own palette (deep navy for the mysterious main loop,
   *  forest green for the lush forrest sub-level, icy white for snow). */
  tint: number;
}> = [
  { keys: FOREST_LAYER_KEYS, factors: [0.04, 0.1, 0.18, 0.3, 0.45, 0.62, 0.78], label: "Mysterious", tint: 0x1a2540 },
  { keys: PIXEL_FOREST_KEYS, factors: PIXEL_FOREST_FACTORS, label: "Forrest", tint: 0x2f5028 },
  { keys: SNOWY_FOREST_KEYS, factors: SNOWY_FOREST_FACTORS, label: "Snowy", tint: 0xc6d8e8 },
];

/** Width in display pixels of the gradient sprite's fade region. The
 *  trailing fog rectangle starts at this offset and the auto-exit
 *  triggers when this offset crosses the hero. */
const GRADIENT_FADE_WIDTH = 144;
/** How wide the trailing fog rectangle should be (must cover the
 *  remainder of the viewport once the gradient's fade region is past
 *  the hero). */
const FOG_WIDTH = 480;

interface SidequestTowerSprite extends Phaser.Physics.Arcade.Sprite {
  /** Stable id of the sidequest the tower represents. */
  sidequestId?: string;
  /** True once the participant interacted with it (so we don't double-fire the banner). */
  accepted?: boolean;
  /**
   * When set, this tower was spawned by `AuthoredSidequestService` from
   * a player-authored sidequest. `acceptSidequest` branches on this to
   * enter the authored sub-level (custom name + biome + puzzle + set
   * pieces) instead of the procedural one.
   */
  authoredSidequestId?: string;
  /**
   * When true, this gate exits the current sub-level instead of
   * entering a new one. Only set on gates spawned inside sub-levels.
   */
  isExitGate?: boolean;
  /** True for puzzle-lever props; interacting with one ticks the
   *  current sub-level's puzzle goal. */
  isLever?: boolean;
}

/**
 * Active puzzle goal inside a sub-level. Three kinds:
 *   - kills:    defeat N gegner (any kind, including birds)
 *   - crystals: collect N crystal pickups
 *   - lever:    interact with a single lever prop (target = 1)
 * Until `progress >= target`, exit gates spawned in this sub-level
 * are tinted red and ignore the Y interact press. Solving the puzzle
 * unlocks every active and future exit gate in this sub-level.
 */
interface PuzzleState {
  kind: "kills" | "crystals" | "lever";
  target: number;
  progress: number;
  /** i18n key for the human-readable label; resolved via t() on render. */
  labelKey: StringKey;
  solved: boolean;
}

interface ScrollSprite extends Phaser.Physics.Arcade.Sprite {
  kind?: InventoryItemKind;
}

interface PickupSprite extends Phaser.Physics.Arcade.Sprite {
  kind?: "heart" | "crystal" | "boot" | "feather";
}

/**
 * Bird-Gegner variant. Same physics group as ground Gegner so all the
 * existing collision + cull plumbing works, but tagged `isBird` so the
 * per-frame loop knows to override `y` with a sine wave rather than
 * letting `translateGroup` leave it pinned to spawn height. Dive
 * bombs are spawned via `lastDiveBombMs` cadence.
 */
interface BirdSprite extends Phaser.Physics.Arcade.Sprite {
  isBird?: boolean;
  birdBaseY?: number;
  birdAmplitude?: number;
  birdFrequencyHz?: number;
  birdPhase?: number;
  /** ms since session start (performance.now scale) of the last dive bomb. */
  lastDiveBombMs?: number;
}

/**
 * Falling-rock obstacle (Jonathan 2026-05-21). Lifecycle:
 *   - `drifting` — suspended near the tree-line, scrolling left with the
 *     world until it reaches a lead position ahead of the hero.
 *   - `shaking` — jitters in place for a *constant* delay
 *     (`rockShakeDurationMs`) so the player can learn the timing.
 *   - `falling` — x locks and it drops straight down onto the hero's
 *     column; dodge with the existing backstep (hold ← / A).
 * A faint ground `shadow` telegraphs the impact column the whole time.
 */
interface FallingRock {
  obj: Phaser.GameObjects.Image;
  shadow: Phaser.GameObjects.Ellipse;
  state: "drifting" | "shaking" | "falling";
  shakeStartMs: number;
  /** Scroll-anchored x while drifting / shaking; locked at fall-start so
   *  the drop is vertical. Shake jitter renders as `baseX + jitter` so it
   *  never accumulates into the scroll position. */
  baseX: number;
}

/**
 * One-way floating platform. Custom fields capture optional oscillation
 * around a (scroll-anchored) base position. The collider's process
 * callback already enforces the one-way semantics for the moving case
 * the same way it does for static platforms — falling-from-above only,
 * with `dropThroughUntilMs` to escape downward.
 *
 * `basePosX` is decremented by the world scroll each frame; the
 * sprite's `x` for the current frame is `basePosX + amplitude *
 * sin(...)` when oscillating horizontally. For vertical oscillation,
 * `basePosY` is the centre-line and the sprite's `y` is the offset
 * version.
 */
interface PlatformSprite extends Phaser.Physics.Arcade.Sprite {
  oscillateAxis?: "x" | "y";
  basePosX?: number;
  basePosY?: number;
  oscAmplitude?: number;
  oscPhase?: number;
  oscPeriodSec?: number;
  /** True for the wide invisible canopy platforms spawned by
   *  `spawnCanopyCluster`. Excluded from the moving-platform
   *  oscillation logic and culled with the rest of the cluster when
   *  the level changes. */
  isCanopy?: boolean;
  /** Stable cluster id shared by the ascent vine, descent vine, and
   *  canopy platform of a single canopy cluster. */
  clusterId?: number;
}

/**
 * Climbable vine. Two flavors per canopy cluster:
 *   - `isAscent: true` on the left end — grabbed from the ground while
 *     the hero is airborne to auto-climb up to the canopy.
 *   - `isAscent: false` on the right end — grabbed from the canopy to
 *     auto-slide back down (graceful exit).
 * The sprite scrolls left with the world (`scrollVines`); when one
 * passes off the left edge it's destroyed. Vines are decorative +
 * grab-trigger only — there's no physics collision against them; the
 * grab check in `tryGrabVine` reads positions directly.
 */
interface VineSprite extends Phaser.Physics.Arcade.Sprite {
  isAscent?: boolean;
  clusterId?: number;
}

/**
 * Snapshot of a level's transient state, pushed onto the stack when
 * the player enters a sub-level and popped when they exit. Persistent
 * state (HP, geo, inventory, skills, cumulative stats) is NOT captured
 * — it carries across levels so the sub-level run feels like a detour
 * rather than a reset.
 */
interface SavedLevelContext {
  backgroundId: string;
  elapsedSec: number;
  durationLimitSec: number;
  chunkIndex: number;
  isTutorial: boolean;
  nextSignIdx: number;
  scrollAccum: number;
  groundScrollAccum: number;
  /** Per-level scroll distance accumulated while inside this level (px).
   *  Saved when entering a sub-level so we can restore the parent's
   *  position when popping back, and so the map screen can show how
   *  far the player had explored at the moment they dived deeper. */
  levelDistance: number;
}

/** A single door-portal traversal logged in `portalEntries`. */
interface PortalEntry {
  /** The depth the player was IN when they dove deeper. */
  depth: number;
  /** Per-level distance (px) within that parent depth at dive time. */
  distance: number;
  /** Resulting depth after the dive (= depth + 1 when this was logged). */
  targetDepth: number;
}

/**
 * View 3 — Main Runner HUD (ui-sketches §3, PRD §5).
 *
 * v0.3 game-loop polish:
 *   - Jump model: coyote time (~110 ms after walking off a ledge a jump still
 *     fires), jump buffer (~120 ms before landing a queued jump fires on
 *     contact), variable-height (releasing the button mid-jump cuts the
 *     upward velocity), and a single mid-air double-jump unlocked after the
 *     participant collects `doubleJumpUnlockGeo` geo.
 *   - Attack (X / J): hero spawns a short-lived sword sprite slightly ahead
 *     of itself; overlap with a Gegner destroys it and bumps the kill counter.
 *     Cooldown prevents spam.
 *   - Interact (Y / K): if a sidequest-tower is within reach, the tower is
 *     "accepted" — banner appears, telemetry emits accept event, tower flips
 *     to a "done" tint. Scroll items overlap directly into inventory.
 *   - Skills HUD shows four icons (jump, double-jump, attack, interact);
 *     locked skills tint to PALETTE.skillLocked.
 *   - Inventory HUD shows three slots filled left-to-right from scroll/gem
 *     pickups. Decorative for v0.3.
 *   - Pause (Start / Esc) routes to launcher (clean abort); EndScene only
 *     fires on timeup or hp_down so the summary screen represents a real
 *     completed run (PRD §10 — telemetry export tied to completed sessions).
 *   - Background-Upload UGC: SessionConfig.backgroundId is resolved to a
 *     BACKGROUND_PRESETS entry and handed to Parallax. A cyan UGC corner-mark
 *     is rendered by Parallax for non-default selections (PRD §3).
 */
export class RunnerScene extends Phaser.Scene {
  private cfg!: SessionConfig;
  private controls!: InputService;
  private telemetry!: TelemetryService;
  private parallax!: Parallax;
  private audio!: AudioService;
  /** Runtime debug flag — resolves from `cfg.debugMode`, falling back to
   *  the env-derived `DEFAULT_DEBUG_MODE`. Read by all `DEBUG_MODE`
   *  former-call-sites (key handlers, fast-forward). */
  private debugMode = DEFAULT_DEBUG_MODE;

  // World state.
  private hero!: Phaser.Physics.Arcade.Sprite;
  private groundGroup!: Phaser.Physics.Arcade.StaticGroup;
  private gegnerGroup!: Phaser.Physics.Arcade.Group;
  private geoGroup!: Phaser.Physics.Arcade.Group;
  private scrollGroup!: Phaser.Physics.Arcade.Group;
  private towerGroup!: Phaser.Physics.Arcade.Group;
  private pickupGroup!: Phaser.Physics.Arcade.Group;
  /** Elevated one-way platforms the hero can jump onto and drop through. */
  private platformGroup!: Phaser.Physics.Arcade.StaticGroup;
  private swordSprite?: Phaser.Physics.Arcade.Sprite;

  /** Unbounded cumulative world-scroll distance. Used as a stable
   *  coordinate to position abysses + platform spawns deterministically. */
  private worldDistTravelled = 0;
  /** Distance scrolled (px) inside the *current* level. Resets to 0 on
   *  sub-level entry, restored from the saved context on exit. Drives
   *  the per-level mini-map strips on the parchment map screen. */
  private levelDistance = 0;
  /** Per-level dead-hole (abyss) position as an offset (px) from the
   *  current level's origin, or `null` for no hole this level. Jonathan
   *  2026-05-21: at most one pit per level, never right after a swamp.
   *  Recomputed by `planLevelAbyss` on every level (re)entry. */
  private levelAbyssOffsetPx: number | null = null;
  /** Width (px) of the single dead hole. */
  private readonly abyssWidthPx = 32;
  /** Live falling-rock obstacles (see `FallingRock`). */
  private fallingRocks: FallingRock[] = [];
  private rockTimerSec = 0;
  /** Session-wide log of door-portal entries. One row per dive; the
   *  map screen filters by depth to render markers on each ancestor
   *  level's strip. The current depth shows no markers because the
   *  player hasn't dived deeper from here yet. */
  private portalEntries: PortalEntry[] = [];
  /** Time (ms) until platform drop-through input lapses. While `>now`,
   *  the hero ignores platform collisions so down-arrow drops through. */
  private dropThroughUntilMs = 0;
  /** White-flash overlay shown briefly during resurrection. */
  private resurrectionFlash?: Phaser.GameObjects.Rectangle;
  // Ground geometry. The play stage is fixed at the ground top; we recycle
  // tile sprites instead of streaming a wider world. Each tile is 16x16.
  private readonly groundTopY = NATIVE_HEIGHT - 32; // 2 tiles tall
  private readonly tileCount = Math.ceil(NATIVE_WIDTH / TILE_SIZE) + 2;
  private tiles: Phaser.Physics.Arcade.Sprite[] = [];
  private scrollAccum = 0;
  /** Shared sub-pixel ground-scroll accumulator. Tile positions are
   *  derived deterministically from this value each frame, eliminating
   *  the float drift between adjacent tiles that previously produced a
   *  1-px gap whenever two neighbouring tiles rounded apart. */
  private groundScrollAccum = 0;
  /** Spawn timer for elevated platforms (seconds). */
  private platformTimerSec = 0;
  /** Hero is currently inside a swamp segment — slows scroll + cuts jump height. */
  private inSwamp = false;
  /** Translucent swamp bands anchored to world-x ranges. Spawned on first
   *  appearance via `maybeSpawnSwampBand`, translated left each frame
   *  with the world, destroyed when fully off-screen left. */
  private swampBands: Phaser.GameObjects.Rectangle[] = [];
  /** Highest swamp-segment index spawned so far. Pure-function gate so a
   *  segment never spawns twice. */
  private lastSwampSegSpawned = -1;

  // Spawn timers.
  private gegnerTimerSec = 0;
  private geoTimerSec = 0;
  private towerTimerSec = 0;
  private scrollTimerSec = 0;
  private heartTimerSec = 0;
  private crystalTimerSec = 0;
  private bootTimerSec = 0;
  private featherTimerSec = 0;
  /** Stored escape-feather charges. One charge = one "U press pops up
   *  a level". Cap = `GAMEPLAY.featherMaxCharges`. */
  private escapeCharges = 0;
  private birdTimerSec = 0;
  /** Active bird dive-bomb projectiles. Plain sprites (not in a physics
   *  group) — we run our own per-frame fall + overlap check so the
   *  collision math stays tight. */
  private birdProjectiles: Phaser.GameObjects.Rectangle[] = [];
  private layer3SampleSec = 0;
  /** Active speed-boost expiry (ms since session start, performance.now scale). */
  private speedBoostUntilMs = 0;

  // HUD state.
  private hp: number = GAMEPLAY.heroMaxHp;
  private geoCollected = 0;
  private chunkIndex = 1;
  private hpRects: Phaser.GameObjects.Rectangle[] = [];
  private geoText!: Phaser.GameObjects.Text;
  private bottomRailText!: Phaser.GameObjects.Text;
  private tutorialTag?: Phaser.GameObjects.Container;
  private tutorialTagBg?: Phaser.GameObjects.Rectangle;
  private tutorialTagText?: Phaser.GameObjects.Text;
  /** Mini-map (lookahead radar) — strip above the bottom rail showing
   *  what's coming in the next ~500 px of world. Two layers: a panel
   *  rectangle for the frame, and a Graphics buffer that's cleared and
   *  redrawn each frame with markers per entity / abyss / swamp band. */
  private miniMapBg?: Phaser.GameObjects.Rectangle;
  private miniMapMarkers?: Phaser.GameObjects.Graphics;
  /**
   * True while the *outer* level (depth 0) is a tutorial run. Stays true
   * even while the player is nested inside a sub-level. The tutorial tag
   * uses this to decide whether to show a breadcrumb at all.
   */
  private inTutorialContext = false;

  // Skills HUD.
  private skills: Record<Skill, boolean> = {
    jump: true,
    doubleJump: false,
    attack: true,
    interact: true,
  };
  private skillIcons: Partial<Record<Skill, Phaser.GameObjects.Image>> = {};
  /** Boot/speed-boost indicator in the HUD strip; lit while boost is active. */
  private bootIcon?: Phaser.GameObjects.Image;
  /** Tooltip text + background — single pair reused for every HUD hover. */
  private tooltipBg?: Phaser.GameObjects.Rectangle;
  private tooltipText?: Phaser.GameObjects.Text;
  /** Continuous alpha pulse on the boot icon while a Boot speed boost is in flight. */
  private bootIconPulse?: Phaser.Tweens.Tween;
  /** Boost wind-streak spawn timer (ms accumulator). */
  private windStreakAccumMs = 0;

  // Inventory HUD.
  private inventory: InventoryItem[] = [];
  /** Shared HUD layout (built in `buildHud`); the rest of the file keeps
   *  using the existing `hpRects`/`geoText`/etc. fields, which after
   *  construction simply alias the WalletHud's owned objects so the
   *  market scene and runner render the exact same visuals. */
  private walletHud!: WalletHud;

  // Jump feel state.
  private lastGroundedAtMs = 0;
  private bufferedJumpAtMs = -Infinity;
  private hasDoubleJump = true; // Recharged each time we touch ground.
  private wasOnGround = false;

  // Attack state.
  private lastAttackAtMs = -Infinity;
  private attackHitsThisSwing = new Set<Phaser.Physics.Arcade.Sprite>();
  /** Sword motion-trail echo spawn timer (ms accumulator). */
  private swordEchoAccumMs = 0;

  // Hit invulnerability.
  private invulnUntilMs = 0;

  // Banner state.
  private banner?: Phaser.GameObjects.Container;
  private bannerHideAtMs = 0;

  // Backstep state. Pressing A/← lets the hero drift up to
  // `backstepBudgetPx` (10% of NATIVE_WIDTH) leftward to grab a missed
  // pickup, then glides back to the auto-run anchor when released.
  private readonly backstepBudgetPx = Math.round(NATIVE_WIDTH * 0.1);
  private readonly backstepGoSpeed = 130; // px/sec while holding left
  private readonly backstepReturnSpeed = 200; // px/sec when released
  private prevBackstepHeld = false;

  // Tutorial signage. Spawn schedule + rolling list of live signs.
  // `arrow` paints a chunky stylized chevron on the parallax wall band
  // alongside the signpost, so the affordance reads as graffiti on a
  // building rather than only a roadside sign (Visual Brief §5 — the
  // world looms behind the runner; we paint instructions onto it).
  //
  // `keys` renders as a row of elevated parchment keycaps under the
  // sign's title. `combo: true` swaps the keycap separator from "/"
  // ("alternative key") to "+" ("tap in sequence"), so DOUBLE-JUMP
  // reads as SPACE + SPACE rather than SPACE / SPACE.
  private readonly tutorialSigns: ReadonlyArray<{
    atSec: number;
    /** i18n key for the title; resolved via t() at spawn time. */
    titleKey: StringKey;
    keys: ReadonlyArray<string>;
    combo?: boolean;
    /** Sign post height above the ground (px). 0 = sign sits at ground. */
    height: number;
    /** Optional wall-arrow direction painted near the sign. */
    arrow?: "up" | "double-up" | "right" | "left";
    /** Vertical offset from `groundTopY` for the wall arrow. */
    arrowY?: number;
  }> = [
    // All signs now planted at ground level (height: 0). Wall arrows
    // above each sign convey the affordance's vertical direction
    // (jump up, collect by jumping, slash sideways, etc.); the sign
    // itself doesn't need to float.
    { atSec: 3, titleKey: "tutorial.jump", keys: ["SPACE", "A"], height: 0, arrow: "up", arrowY: 60 },
    { atSec: 12, titleKey: "tutorial.collect", keys: ["SPACE", "A"], height: 0, arrow: "up", arrowY: 70 },
    { atSec: 22, titleKey: "tutorial.attack", keys: ["J", "X"], height: 0, arrow: "right", arrowY: 38 },
    // Sign precedes the t=30 s tutorial canopy cluster — gives the player
    // ~4 s to read "SPACE + Y to climb" before the ascent vine arrives at
    // the hero's auto-run anchor.
    { atSec: 26, titleKey: "tutorial.climb", keys: ["SPACE", "Y"], combo: true, height: 0, arrow: "up", arrowY: 90 },
    { atSec: 32, titleKey: "tutorial.interact", keys: ["K", "Y"], height: 0, arrow: "right", arrowY: 46 },
    { atSec: 42, titleKey: "tutorial.backstep", keys: ["←", "A"], height: 0, arrow: "left", arrowY: 38 },
    { atSec: 52, titleKey: "tutorial.double_jump", keys: ["SPACE", "SPACE"], combo: true, height: 0, arrow: "double-up", arrowY: 96 },
  ];
  private nextSignIdx = 0;
  private signs: Phaser.GameObjects.Container[] = [];

  // Run-summary counters.
  private gegnerSeen = 0;
  private gegnerDefeated = 0;
  private attacksLanded = 0;
  private doubleJumpsPerformed = 0;
  private sidequestsAccepted = 0;
  private sidequestsCleared = 0; // v0.3: accepting === clearing in the absence of bespoke tower scenes.
  private inventoryItemsCollected = 0;
  private heartsCollected = 0;
  private crystalsCollected = 0;
  private bootsCollected = 0;
  private feathersCollected = 0;
  private escapesUsed = 0;

  /**
   * Variant-B Shop / per-level author state. Fields are populated in
   * `create()` only when `cfg.variant === "B"`; Variant A runs the same
   * scene with everything below disabled.
   */
  private isUgcVariant = false;
  /** Selling the escape-feather skill drops charges to 0 and re-locks the
   *  U-press capability. Re-unlocks on the next feather pickup. */
  private escapeFeatherUnlocked = false;
  /** Counter that controls the double-jump re-unlock after a sale. Equal
   *  to lifetime `geoCollected` until the first sale; after a sale it
   *  resets to 0 and the re-unlock check switches to this field. */
  private geoTowardDoubleJumpReUnlock = 0;
  private wasDoubleJumpEverSold = false;
  private runnerWallet?: RunnerWallet;
  private incomeTracker?: WalletIncomeTracker;
  private authorIdentityService?: AuthorIdentityService;
  private authorBadge?: AuthorBadge;
  /** F8 debug overlay (top-right, monospace text). Built lazily on first F8
   *  press; toggle via `toggleDebugOverlay`. Only available when debugMode
   *  is true — never wired up in participant builds. */
  private debugOverlay?: Phaser.GameObjects.Text;
  /** Stable seed for the current level's author. Derived from main-run
   *  seed at scene boot; re-derived on each sub-level push. */
  private currentLevelAuthorSeed = 0;

  /** Pass cfg + optional flags via scene.start(). */
  isTutorial = false;
  durationLimitSec: number = GAMEPLAY.mainLoopDurationSec;
  private elapsedSec = 0;

  /**
   * Hero state machine. Scene starts the hero in `idle` — the Knight
   * stands still, world doesn't scroll, timers don't tick, spawners
   * sleep. First gameplay input (jump / attack / interact / move)
   * flips state to `running` and the auto-run loop kicks in. `climbing`
   * is a brief (~600 ms) transient state while the hero is animating
   * up or down a canopy vine; the world is fully frozen for the
   * duration so the vine stays under the hero. Pause (Esc / Start)
   * is always honoured.
   */
  private heroState: "idle" | "running" | "climbing" = "idle";

  // Canopy detour state.
  /** Liana sprites — both ascent (left of cluster) and descent (right
   *  of cluster). Plain group; we manage scroll + cull manually in
   *  `scrollVines`. No physics collisions; grab is a manual proximity
   *  check in `tryGrabVine`. */
  private vineGroup!: Phaser.Physics.Arcade.Group;
  /** Cadence accumulator for canopy clusters in the depth-0 Mysterious
   *  Forest pack. Resets after each spawn to enforce
   *  `canopyClusterIntervalSecMain` between appearances. */
  private canopyTimerSec = 0;
  /** One-shot guard so the tutorial spawns exactly one cluster around
   *  `canopyClusterTutorialAtSec`. Flips true on spawn; never resets
   *  within the tutorial scene's lifetime. */
  private tutorialCanopySpawned = false;
  /** Monotonic counter assigned to each canopy cluster spawn. Stored
   *  on every cluster member so the grab check can confirm the hero
   *  is interacting with the right cluster. */
  private nextClusterId = 1;

  /**
   * Sidequest sub-level depth. 0 = main loop. Each gate-interact pushes
   * a `SavedLevelContext` onto `levelStack` and increments `levelDepth`;
   * each exit-gate-interact pops the top context and restores it. The
   * stack supports arbitrary nesting (sub-sub-sub-…-levels).
   */
  private levelDepth = 0;
  private levelStack: SavedLevelContext[] = [];
  /** Periodic exit-gate spawn cadence while inside a sub-level. */
  private exitGateTimerSec = 0;
  /** Seconds spent in the current sub-level (resets on enter/exit). */
  private subLevelTimerSec = 0;
  /** Sub-level "end" black-gradient sprite (transparent → opaque
   *  left-to-right). Spawned once `subLevelTimerSec` crosses the
   *  configured duration. When its fully-opaque right edge crosses the
   *  hero, the sub-level auto-exits. */
  private subLevelEndGradient?: Phaser.GameObjects.Image;
  /** Active puzzle in the current sub-level (or null if depth==0 / no puzzle rolled). */
  private puzzle: PuzzleState | null = null;
  /** Already-spawned lever prop count this sub-level (cap at 1 so the puzzle stays clean). */
  private leverSpawnedThisLevel = 0;
  /** True while the parchment-style map overlay is open. While open the
   *  update loop short-circuits to keep the world paused. */
  private mapOpen = false;
  private mapContainer?: Phaser.GameObjects.Container;
  /** True while the player has explicitly paused (P key / HUD button).
   *  Distinct from the existing Esc / Start which **aborts** the run
   *  to the launcher — pause freezes the world and waits for resume. */
  private paused = false;
  private pauseOverlay?: Phaser.GameObjects.Container;
  private pauseBars: Phaser.GameObjects.Rectangle[] = [];
  private pausePlay?: Phaser.GameObjects.Triangle;
  /** Pause-menu buttons. Built inside `showPauseOverlay`, torn down on
   *  `hidePauseOverlay`. Variant A shows [PROFILE]; Variant B shows
   *  [AUTHOR SIDEQUEST, MARKET, PROFILE]. */
  private pauseButtons: { btn: PauseMenuButton; action: () => void }[] = [];
  /** Index of the focused pause-menu button. Edge-tracked via input. */
  private pauseFocusIdx = 0;
  /** Edge-trigger guards for pause-menu directional input. */
  private prevPauseMoveY = 0;
  /** Author-tower overlays — avatar+name chips floating above each
   *  authored portal. Tracked separately from `towerGroup` so we can
   *  destroy + re-translate them in lockstep with the tower. */
  private authorTowerOverlays = new Map<SidequestTowerSprite, AuthorTowerOverlay>();
  private authoredService?: AuthoredSidequestService;
  /** Solid tinted rectangle that trails the gradient, occluding the
   *  parent world to the right of the fade so nothing peeks through. */
  private subLevelEndFog?: Phaser.GameObjects.Rectangle;
  /** Wallclock ms at which the most recent sub-level dive started.
   *  While `nowMs - subLevelEntryAtMs < subLevelEntryBlinkMs` the world
   *  is frozen and the hero blinks; during the slow-ramp window after
   *  that, scroll multiplier eases from `subLevelEntryStartSpeedMul`
   *  back up to 1.0. Initialised to `-Infinity` so the freeze is
   *  inactive at scene start. */
  private subLevelEntryAtMs = -Infinity;

  constructor() {
    super("runner");
  }

  init(data: SessionConfig & { tutorial?: boolean; durationSec?: number }): void {
    this.cfg = {
      variant: data.variant,
      participantId: data.participantId,
      startedAt: data.startedAt,
      backgroundId: data.backgroundId ?? DEFAULT_BACKGROUND_ID,
      debugMode: data.debugMode ?? DEFAULT_DEBUG_MODE,
    };
    this.debugMode = this.cfg.debugMode === true;
    this.isTutorial = data.tutorial === true;
    this.durationLimitSec = data.durationSec ?? GAMEPLAY.mainLoopDurationSec;
    // Reset all mutable state — scenes can be re-entered.
    this.hp = GAMEPLAY.heroMaxHp;
    this.geoCollected = 0;
    this.chunkIndex = 1;
    this.gegnerTimerSec = 0;
    this.geoTimerSec = 0;
    this.towerTimerSec = 0;
    this.scrollTimerSec = 0;
    this.layer3SampleSec = 0;
    this.scrollAccum = 0;
    this.groundScrollAccum = 0;
    this.elapsedSec = 0;
    this.tiles = [];
    this.hpRects = [];
    this.tutorialTag = undefined;
    this.skills = {
      jump: true,
      doubleJump: false,
      attack: true,
      interact: true,
    };
    this.skillIcons = {};
    this.inventory = [];
    this.lastGroundedAtMs = 0;
    this.bufferedJumpAtMs = -Infinity;
    this.hasDoubleJump = false; // gets enabled on ground touch _if_ skill unlocked.
    this.wasOnGround = false;
    this.lastAttackAtMs = -Infinity;
    this.attackHitsThisSwing = new Set();
    this.swordEchoAccumMs = 0;
    this.invulnUntilMs = 0;
    this.banner = undefined;
    this.bannerHideAtMs = 0;
    this.swordSprite = undefined;
    this.prevBackstepHeld = false;
    this.nextSignIdx = 0;
    this.signs = [];
    this.heartTimerSec = 0;
    this.crystalTimerSec = 0;
    this.bootTimerSec = 0;
    this.heartsCollected = 0;
    this.crystalsCollected = 0;
    this.bootsCollected = 0;
    this.feathersCollected = 0;
    this.escapesUsed = 0;
    this.featherTimerSec = 0;
    this.escapeCharges = 0;
    this.speedBoostUntilMs = 0;
    this.bootIcon = undefined;
    this.bootIconPulse = undefined;
    this.windStreakAccumMs = 0;
    this.heroState = "idle";
    this.tooltipBg = undefined;
    this.tooltipText = undefined;
    this.levelDepth = 0;
    this.levelStack = [];
    this.exitGateTimerSec = 0;
    this.subLevelTimerSec = 0;
    this.subLevelEndGradient = undefined;
    this.subLevelEndFog = undefined;
    this.subLevelEntryAtMs = -Infinity;
    this.worldDistTravelled = 0;
    this.levelDistance = 0;
    this.fallingRocks = [];
    this.rockTimerSec = 0;
    this.planLevelAbyss();
    this.portalEntries = [];
    this.platformTimerSec = 0;
    this.dropThroughUntilMs = 0;
    this.resurrectionFlash = undefined;
    this.inSwamp = false;
    this.swampBands = [];
    this.lastSwampSegSpawned = -1;
    this.birdTimerSec = 0;
    this.birdProjectiles = [];
    this.puzzle = null;
    this.leverSpawnedThisLevel = 0;
    this.miniMapBg = undefined;
    this.miniMapMarkers = undefined;
    this.mapOpen = false;
    this.mapContainer = undefined;
    this.paused = false;
    this.pauseOverlay = undefined;
    this.pauseBars = [];
    this.pausePlay = undefined;
    this.tutorialTagBg = undefined;
    this.tutorialTagText = undefined;
    // Tutorial context is set once at scene start from the `tutorial`
    // flag and persists across sub-levels (where local `isTutorial`
    // gets toggled to false).
    this.inTutorialContext = data.tutorial === true;
    this.gegnerSeen = 0;
    this.gegnerDefeated = 0;
    this.attacksLanded = 0;
    this.doubleJumpsPerformed = 0;
    this.sidequestsAccepted = 0;
    this.sidequestsCleared = 0;
    this.inventoryItemsCollected = 0;

    // Carry over any Launcher-side Market trades — geo, hp, feather charges,
    // inventory contents, and owned skills. Applied AFTER the default reset
    // block so it overrides the standard fresh-session values.
    if (data.startingWallet) {
      const sw = data.startingWallet;
      this.hp = Math.max(0, Math.min(GAMEPLAY.heroMaxHp, sw.hp));
      this.geoCollected = Math.max(0, sw.geo);
      this.escapeCharges = Math.max(0, Math.min(GAMEPLAY.featherMaxCharges, sw.featherCharges));
      this.inventory = sw.inventory
        .slice(0, GAMEPLAY.inventorySlots)
        .map((it) => ({ kind: it.kind, pickedUpAtMs: it.pickedUpAtMs }));
      this.skills.doubleJump = sw.doubleJump;
      this.hasDoubleJump = sw.doubleJump;
      this.escapeFeatherUnlocked = sw.escapeFeather;
      this.wasDoubleJumpEverSold = false;
      this.geoTowardDoubleJumpReUnlock = 0;
    }
  }

  create(): void {
    this.controls = new InputService(this);
    this.telemetry = new TelemetryService(this.cfg.participantId);
    this.audio = new AudioService(this);
    // Browsers require a user gesture before audio can start; resume the
    // AudioContext on the first key / pointer event in this scene.
    const resume = (): void => this.audio.resume();
    this.input.keyboard?.once("keydown", resume);
    this.input.once("pointerdown", resume);
    this.input.gamepad?.once("down", resume);

    // M toggles the parchment-style world map overlay. Available in all
    // builds (it's a player affordance, not a debug shortcut).
    this.input.keyboard?.on("keydown-M", () => this.toggleMap());
    // U expends one stored Escape-Feather charge, popping the hero up
    // one sub-level. Quiet no-op when the player is in the main loop
    // or has no charges — the HUD readout tells them why.
    this.input.keyboard?.on("keydown-U", () => this.useEscapeCharge());
    // P toggles the real pause (freeze + resume). Distinct from Esc /
    // Start which **abort** the run to the launcher. The same toggle
    // is wired to the ⏸ button in the HUD via `buildPauseButton`.
    this.input.keyboard?.on("keydown-P", () => this.togglePause());

    // Debug shortcuts — number keys jump to that depth (key N = depth N).
    // Off in participant builds. The runtime flag is set on the Launcher
    // checkbox; default state comes from VITE_DEBUG_MODE (PRD §10 — never
    // expose debug affordances during study runs).
    if (this.debugMode) {
      const digitKeys = [
        "ZERO", "ONE", "TWO", "THREE", "FOUR",
        "FIVE", "SIX", "SEVEN", "EIGHT", "NINE",
      ];
      digitKeys.forEach((name, depth) => {
        this.input.keyboard?.on(`keydown-${name}`, () => this.debugGotoDepth(depth));
      });
      // F8 toggles the dev overlay (shop service state + a few scene stats).
      // Gated behind debugMode so participant builds never see it.
      this.input.keyboard?.on("keydown-F8", () => this.toggleDebugOverlay());
      // F9 refills HP to full — handy when testing late-game encounters.
      this.input.keyboard?.on("keydown-F9", () => this.debugRefillHp());
    }
    const preset = backgroundPresetById(this.cfg.backgroundId);
    this.parallax = new Parallax(this, preset);

    this.cameras.main.setBackgroundColor(preset.skyFar);

    this.buildGround();
    this.buildHero();

    this.gegnerGroup = this.physics.add.group({ allowGravity: false });
    this.geoGroup = this.physics.add.group({ allowGravity: false });
    this.scrollGroup = this.physics.add.group({ allowGravity: false });
    this.towerGroup = this.physics.add.group({ allowGravity: false });
    this.pickupGroup = this.physics.add.group({ allowGravity: false });
    this.platformGroup = this.physics.add.staticGroup();
    this.vineGroup = this.physics.add.group({ allowGravity: false });

    // Ground collider with elevated-tile drop-through: tiles whose top sits
    // ABOVE the baseline (y < baseY) can be phased through during the
    // `dropThroughUntilMs` window, just like the floating one-way platforms.
    // Baseline tiles (y >= baseY) stay solid — the player can never fall
    // off the world. Used so Down-on-an-elevated-step drops the hero to the
    // tile underneath instead of forcing them to walk to the edge.
    this.physics.add.collider(
      this.hero,
      this.groundGroup,
      undefined,
      (_heroObj, groundObj) => {
        if (this.timeMs() >= this.dropThroughUntilMs) return true;
        const tile = groundObj as Phaser.GameObjects.Image;
        const baseY = NATIVE_HEIGHT - 32;
        // Elevated → allow phase-through. Baseline → keep solid.
        return tile.y >= baseY;
      },
      this,
    );
    // One-way collision for elevated platforms: collide only when the
    // hero is falling onto a platform from above (velocity.y > 0 and
    // the hero's bottom is at or above the platform's top). The
    // `dropThroughUntilMs` window lets the down-arrow phase the hero
    // through. Process callback returns false to skip the contact.
    this.physics.add.collider(
      this.hero,
      this.platformGroup,
      undefined,
      (heroObj, platformObj) => {
        if (this.timeMs() < this.dropThroughUntilMs) return false;
        const heroBody = (heroObj as Phaser.Physics.Arcade.Sprite).body as Phaser.Physics.Arcade.Body | null;
        if (!heroBody) return false;
        const platformSprite = platformObj as Phaser.Physics.Arcade.Sprite;
        const platformTop = platformSprite.body
          ? (platformSprite.body as Phaser.Physics.Arcade.Body).y
          : platformSprite.y;
        const heroBottom = heroBody.y + heroBody.height;
        return heroBody.velocity.y > 0 && heroBottom <= platformTop + 4;
      },
      this,
    );
    this.physics.add.overlap(this.hero, this.geoGroup, (_h, g) =>
      this.onGeoOverlap(g as Phaser.Physics.Arcade.Sprite),
    );
    this.physics.add.overlap(this.hero, this.scrollGroup, (_h, g) =>
      this.onScrollOverlap(g as ScrollSprite),
    );
    this.physics.add.overlap(this.hero, this.gegnerGroup, (_h, g) =>
      this.onGegnerOverlap(g as Phaser.Physics.Arcade.Sprite),
    );
    this.physics.add.overlap(this.hero, this.pickupGroup, (_h, p) =>
      this.onPickupOverlap(p as PickupSprite),
    );

    this.buildHud();
    this.buildMiniMap();
    this.buildPauseButton();
    if (this.inTutorialContext) {
      this.buildTutorialTag();
    }

    this.telemetry.emit(1, "scene.enter", {
      scene: "runner",
      variant: this.cfg.variant,
      tutorial: this.isTutorial,
      backgroundId: this.cfg.backgroundId ?? DEFAULT_BACKGROUND_ID,
    });

    // Variant B UGC plumbing — shop service, wallet, author identity + badge.
    this.isUgcVariant = this.cfg.variant === "B";
    if (this.isUgcVariant) {
      this.setupUgcFeatures();
    }
    // Double-jump is intentionally NOT pre-unlocked in the tutorial.
    // The player earns it by collecting `doubleJumpUnlockGeo` geo
    // during the main loop (or sooner via a Crystal pickup, which
    // grants 5 geo at once and can cross the threshold in a single
    // pickup). The first time they cross the threshold, a "NEW SKILL
    // UNLOCKED" banner + audio cue fires from `unlockSkill`.
  }

  override update(_t: number, dtMs: number): void {
    const dt = dtMs / 1000;
    const nowMs = this.timeMs();
    const state = this.controls.read();

    if (state.pausePressed) {
      // Map open: either key closes the map (less catastrophic than
      // aborting the whole session).
      if (this.mapOpen) {
        this.toggleMap();
        return;
      }
      // From pause: Esc → launcher (the old abort behaviour), Start →
      // resume (mirrors the P key). From not-paused: either key opens
      // the pause overlay so the player gets the menu before
      // committing to abort.
      if (this.paused) {
        if (state.escPressed) {
          this.telemetry.emit(1, "input.pause");
          this.abortToLauncher();
        } else {
          // Start (or any other pause-class key) resumes.
          this.togglePause();
        }
      } else {
        this.telemetry.emit(1, "input.pause");
        this.togglePause();
      }
      return;
    }

    // World is paused while the map overlay is open. We still honour
    // the pause-press check above so Esc can close it.
    if (this.mapOpen) return;
    // Explicit player pause (P / ⏸ button). Same short-circuit — the
    // pause button stays interactive because it sits above the
    // overlay at depth 40. In Variant B the pause overlay hosts two
    // action buttons (AUTHOR SIDEQUEST + MARKET) — let those receive
    // directional + activate input before short-circuiting the rest of
    // the update loop.
    if (this.paused) {
      this.handlePauseMenuInput(state);
      return;
    }

    // Climb state freezes the world: scroll, spawners, parallax, AI all
    // pause for the ~600 ms climb tween so the vine stays under the
    // hero. The tween itself flips state back to "running" via
    // `finalizeClimb`.
    if (this.heroState === "climbing") return;

    // Idle gate: world doesn't scroll, timers don't tick, spawners
    // sleep until the player presses anything actionable. First input
    // edge (jump / attack / interact / move) flips to running.
    if (this.heroState === "idle") {
      const anyInput =
        state.jumpPressed ||
        state.attackPressed ||
        state.interactPressed ||
        Math.abs(state.moveX) > 0.2 ||
        Math.abs(state.moveY) > 0.2;
      if (anyInput) {
        this.transitionToRunning();
      }
      return;
    }

    // Sub-level entry blink-in: hold the world still and pulse the
    // hero's alpha so the dive into a new subworld reads as a
    // materialisation beat, not an instant jump-cut. Slow-start ramp
    // (below, near the scroll computation) eases back up to full speed
    // after the freeze ends.
    const entryAgeMs = nowMs - this.subLevelEntryAtMs;
    if (entryAgeMs >= 0 && entryAgeMs < GAMEPLAY.subLevelEntryBlinkMs) {
      const blinkOn = Math.floor(entryAgeMs / 90) % 2 === 0;
      this.hero.setAlpha(blinkOn ? 0.25 : 1);
      return;
    }
    if (this.hero.alpha !== 1 && entryAgeMs >= GAMEPLAY.subLevelEntryBlinkMs) {
      this.hero.setAlpha(1);
    }

    this.elapsedSec += dt;

    const onGround = this.heroOnGround();
    if (onGround) {
      this.lastGroundedAtMs = nowMs;
      if (this.skills.doubleJump) this.hasDoubleJump = true;
    }
    if (!this.wasOnGround && onGround && this.bufferedJumpAtMs >= nowMs - GAMEPLAY.jumpBufferMs) {
      // Just-landed-with-queued-jump: fire the buffered jump.
      this.bufferedJumpAtMs = -Infinity;
      this.performGroundJump(nowMs);
    }
    this.wasOnGround = onGround;

    if (state.jumpPressed) {
      this.handleJumpPress(nowMs);
    }
    // Variable-height jump: if the participant releases the jump button
    // while still ascending, cut the remaining upward velocity.
    if (!this.jumpHeld() && this.hero.body && this.hero.body.velocity.y < 0) {
      this.hero.setVelocityY(this.hero.body.velocity.y * GAMEPLAY.jumpCutMultiplier);
    }

    if (state.attackPressed) {
      this.handleAttackPress(nowMs);
    }
    if (state.interactPressed) {
      this.handleInteractPress(nowMs);
    }
    // Vine grab — interact (Y/K) or pulling Up while airborne / on the
    // canopy. Cheap proximity check; only succeeds when the hero is
    // adjacent to a vine in its appropriate y-range (ascent: below the
    // canopy; descent: on or near the canopy).
    if (state.interactPressed || state.moveY < -0.4) {
      this.tryGrabVine();
    }
    this.advanceAttack(nowMs, dtMs);

    // Backstep affordance — hero drifts back up to `backstepBudgetPx` while
    // A / ← is held, glides back to the auto-run anchor when released.
    this.updateBackstep(state.moveX, dt);

    // World scroll. Tiles + entities translate left; hero stays at its
    // backstep-adjusted x. Signs (tutorial-only) scroll alongside.
    // Boot pickup applies a temporary speed multiplier; debug right-arrow
    // adds an extra fast-forward multiplier; expired boost falls back
    // silently.
    const boosted = this.timeMs() < this.speedBoostUntilMs;
    let speedMul = boosted ? GAMEPLAY.speedBoostMultiplier : 1.0;
    // Swamp slows the world while the hero is standing inside one of
    // its world-x ranges. Boot boost still applies — they cancel out
    // partially, which reads as "the boost is fighting the muck".
    const heroWorldX = this.worldDistTravelled + this.hero.x;
    const wasInSwamp = this.inSwamp;
    this.inSwamp = this.isWorldXInSwamp(heroWorldX);
    if (!wasInSwamp && this.inSwamp) this.audio.swampEnter();
    if (this.inSwamp) speedMul *= GAMEPLAY.swampSpeedMultiplier;
    if (this.debugMode && state.moveX > 0.5) speedMul *= DEBUG_FAST_FORWARD;
    // Slow-start ramp after the sub-level entry blink — eases the world
    // from `subLevelEntryStartSpeedMul` back to 1.0 so play resumes
    // gently instead of snapping to full auto-run.
    const slowRampAgeMs = entryAgeMs - GAMEPLAY.subLevelEntryBlinkMs;
    if (slowRampAgeMs >= 0 && slowRampAgeMs < GAMEPLAY.subLevelEntrySlowRampMs) {
      const t = slowRampAgeMs / GAMEPLAY.subLevelEntrySlowRampMs;
      const start = GAMEPLAY.subLevelEntryStartSpeedMul;
      speedMul *= start + (1 - start) * t;
    }
    const scrollPx = GAMEPLAY.baseScrollSpeed * speedMul * dt;
    this.worldDistTravelled += scrollPx;
    this.levelDistance += scrollPx;
    this.scrollAccum += scrollPx;
    this.parallax.step(scrollPx);
    this.recycleGround(scrollPx);
    this.translateGroup(this.gegnerGroup, scrollPx);
    this.translateGroup(this.geoGroup, scrollPx);
    this.translateGroup(this.scrollGroup, scrollPx);
    this.translateGroup(this.towerGroup, scrollPx, -64);
    this.translateGroup(this.pickupGroup, scrollPx);
    this.syncAuthorTowerOverlays(scrollPx);
    this.translateSigns(scrollPx);
    if (this.isTutorial) this.maybeSpawnSign();

    // Chunk counter ticks every TILE_SIZE * 16 px scrolled (~1 chunk worth).
    const chunkPx = TILE_SIZE * 16;
    while (this.scrollAccum > chunkPx) {
      this.scrollAccum -= chunkPx;
      this.chunkIndex += 1;
      this.telemetry.emit(2, "world.chunk_advance", { chunk: this.chunkIndex });
    }

    // Spawners.
    this.gegnerTimerSec += dt;
    if (this.gegnerTimerSec >= GAMEPLAY.gegnerSpawnIntervalSec) {
      this.gegnerTimerSec = 0;
      this.spawnGegner();
    }
    this.geoTimerSec += dt;
    if (this.geoTimerSec >= GAMEPLAY.geoSpawnIntervalSec) {
      this.geoTimerSec = 0;
      this.spawnGeo();
    }
    this.towerTimerSec += dt;
    if (this.towerTimerSec >= GAMEPLAY.towerSpawnIntervalSec) {
      this.towerTimerSec = 0;
      this.spawnTower();
    }
    this.scrollTimerSec += dt;
    if (this.scrollTimerSec >= GAMEPLAY.scrollSpawnIntervalSec) {
      this.scrollTimerSec = 0;
      this.spawnScroll();
    }
    this.heartTimerSec += dt;
    if (this.heartTimerSec >= GAMEPLAY.heartSpawnIntervalSec) {
      this.heartTimerSec = 0;
      this.spawnHeart();
    }
    this.crystalTimerSec += dt;
    if (this.crystalTimerSec >= GAMEPLAY.crystalSpawnIntervalSec) {
      this.crystalTimerSec = 0;
      this.spawnCrystal();
    }
    this.bootTimerSec += dt;
    if (this.bootTimerSec >= GAMEPLAY.bootSpawnIntervalSec) {
      this.bootTimerSec = 0;
      this.spawnBoot();
    }
    this.featherTimerSec += dt;
    if (this.featherTimerSec >= GAMEPLAY.featherSpawnIntervalSec) {
      this.featherTimerSec = 0;
      this.spawnFeather();
    }
    this.birdTimerSec += dt;
    if (this.birdTimerSec >= GAMEPLAY.birdSpawnIntervalSec) {
      this.birdTimerSec = 0;
      this.spawnBird();
    }
    // Birds + dive-bomb projectiles — sine motion and per-bird dive
    // cooldown, then fall the projectiles.
    this.updateBirds(nowMs, dtMs);
    this.updateBirdProjectiles(scrollPx, dt);

    // Falling rocks — drift in, shake for a constant delay, then drop.
    // Skipped in the tutorial so the 60 s intro stays gentle (the
    // tutorial.backstep sign teaches the dodge input first).
    if (!this.inTutorialContext) {
      this.rockTimerSec += dt;
      if (this.rockTimerSec >= GAMEPLAY.rockSpawnIntervalSec) {
        this.rockTimerSec = 0;
        this.spawnRock();
      }
    }
    this.updateRocks(nowMs, scrollPx, dt);

    // Elevated platforms: spawn every ~5 s. Translate left with the world
    // scroll and cull when off-screen. Hero collides one-way (only when
    // falling onto a platform from above).
    this.platformTimerSec += dt;
    if (this.platformTimerSec >= GAMEPLAY.platformSpawnIntervalSec) {
      this.platformTimerSec = 0;
      this.spawnPlatform();
    }
    this.scrollPlatforms(scrollPx, dt);

    // Tree-canopy clusters — rare ascent vine + invisible canopy +
    // pickup band + descent vine. Main-loop cadence is purely
    // time-based; sub-levels get exactly one cluster around their
    // midpoint (guard flips true after spawn, resets on enter/exit).
    this.canopyTimerSec += dt;
    if (this.shouldSpawnCanopyCluster()) {
      this.spawnCanopyCluster();
    }
    this.scrollVines(scrollPx);

    // Swamp visual bands — spawn-once-per-segment, scroll left with
    // the world. The gameplay effect (slowed speed + cut jump) is
    // driven by `isWorldXInSwamp(heroWorldX)` above; the band is
    // purely the cue so the player can SEE the slow-down zone coming.
    this.maybeSpawnSwampBand();
    this.scrollSwampBands(scrollPx);

    // Sub-level timers: track time-in-sub-level and a periodic exit-gate
    // cadence. Once the sub-level duration elapses, spawn the "end of
    // level" black gradient at the right edge of the world; the player
    // runs into it and the sub-level auto-pops.
    if (this.levelDepth > 0) {
      this.subLevelTimerSec += dt;
      this.exitGateTimerSec += dt;
      if (this.exitGateTimerSec >= 12) {
        this.exitGateTimerSec = 0;
        this.spawnExitGate();
      }
      // Lever puzzles need one (and only one) lever prop in the
      // sub-level. Spawn it ~4 s after entry so it doesn't clash
      // with the initial banner copy.
      if (this.subLevelTimerSec >= 4 && this.leverSpawnedThisLevel === 0) {
        this.maybeSpawnLever();
      }
      if (
        this.subLevelTimerSec >= GAMEPLAY.subLevelDurationSec &&
        !this.subLevelEndGradient
      ) {
        this.spawnSubLevelEndGradient();
      }
      if (this.subLevelEndGradient) {
        // Scroll both the gradient and the trailing fog with the world
        // (1.0× factor — they ARE the world's end, not a parallax layer).
        this.subLevelEndGradient.x -= scrollPx;
        if (this.subLevelEndFog) {
          this.subLevelEndFog.x -= scrollPx;
        }
        // Auto-exit when the fade's right edge passes the hero. At
        // that point the hero is fully inside the solid fog and the
        // parent level is occluded; popping back here makes the
        // transition feel like emerging from a tunnel.
        const fadeRightEdge = this.subLevelEndGradient.x + GRADIENT_FADE_WIDTH;
        if (fadeRightEdge <= this.hero.x) {
          this.exitSubLevel(null);
        }
      }
    }

    // Boost wind streaks — small motion lines trailing behind the hero
    // while the speed boost is active. Spawn cadence is independent of
    // scroll speed; the visual effect itself drives the "going fast" read.
    if (this.timeMs() < this.speedBoostUntilMs) {
      this.windStreakAccumMs += dtMs;
      if (this.windStreakAccumMs >= 75) {
        this.windStreakAccumMs = 0;
        this.spawnBoostWindStreak();
      }
    } else {
      this.windStreakAccumMs = 0;
    }

    // Fall-into-abyss → resurrection. If the hero drops below the
    // screen the abyss claimed them — respawn at the run anchor with
    // a -3 HP penalty + a brief white-flash. End the session if HP
    // hits zero.
    if (this.hero.y > NATIVE_HEIGHT + 32) {
      this.resurrectHero();
    }

    // Drop-through window: holding down while standing on a platform
    // opens a 200 ms collision-skip so the hero falls off it.
    if (
      state.moveY > 0.5 &&
      (this.hero.body as Phaser.Physics.Arcade.Body | null)?.blocked.down
    ) {
      this.dropThroughUntilMs = nowMs + 200;
    }

    // Hit invuln visual: blink the hero with a red tint instead of an
    // alpha flicker — the character stays fully visible (legibility
    // priority from Visual Brief §1) while the red overlay reinforces
    // "you got hurt" alongside the screen pulse.
    if (nowMs < this.invulnUntilMs) {
      if (Math.floor(nowMs / 80) % 2 === 0) {
        this.hero.setTint(0xff4040);
      } else {
        this.hero.clearTint();
      }
    } else {
      this.hero.clearTint();
    }

    // Banner timeout.
    if (this.banner && nowMs > this.bannerHideAtMs) {
      this.banner.destroy();
      this.banner = undefined;
      // If a profile tooltip was opened from this banner's hover hit zone,
      // the destroyed bg can no longer emit pointerout — dismiss explicitly.
      ProfileTooltipService.get(this.game)?.hide("forced");
    }

    // Layer-3 per-second sample (PRD §10).
    this.layer3SampleSec += dt;
    if (this.layer3SampleSec >= 1.0) {
      this.layer3SampleSec -= 1.0;
      this.telemetry.emit(3, "derived.sample", {
        skill: Math.min(1, (this.geoCollected + this.attacksLanded * 2) / 60),
        challenge: Math.min(1, this.elapsedSec / 60),
        flow: 0.5,
      });
    }

    this.paintHud();

    if (this.elapsedSec >= this.durationLimitSec) {
      this.telemetry.emit(2, "session.timeup");
      if (this.isTutorial) {
        this.advanceToMainLoop();
      } else {
        this.endSession("timeup");
      }
    }
  }

  private transitionToRunning(): void {
    if (this.heroState !== "idle") return;
    this.heroState = "running";
    if (this.anims.exists("knight.run")) {
      this.hero.play("knight.run", true);
    }
    this.telemetry.emit(1, "hero.state.transition", { from: "idle", to: "running" });
  }

  /**
   * Tutorial → main loop transition. Tutorial telemetry is discarded
   * (PRD §10 — tutorial events are not study data) and a fresh
   * RunnerScene is started with `tutorial: false` and the full main-loop
   * duration. Phaser re-enters the same scene key, so `init()` resets all
   * mutable state via the existing reset block.
   */
  private advanceToMainLoop(): void {
    this.telemetry.emit(1, "scene.exit", { scene: "runner", reason: "tutorial.complete" });
    this.scene.start("runner", {
      variant: this.cfg.variant,
      participantId: this.cfg.participantId,
      startedAt: this.cfg.startedAt,
      backgroundId: this.cfg.backgroundId,
      tutorial: false,
      durationSec: GAMEPLAY.mainLoopDurationSec,
    });
  }

  // --- jump model ------------------------------------------------------

  private handleJumpPress(nowMs: number): void {
    const grounded = this.heroOnGround();
    const withinCoyote = nowMs - this.lastGroundedAtMs <= GAMEPLAY.coyoteTimeMs;

    if (grounded || withinCoyote) {
      this.performGroundJump(nowMs);
      return;
    }
    if (this.skills.doubleJump && this.hasDoubleJump) {
      this.hasDoubleJump = false;
      this.doubleJumpsPerformed += 1;
      this.hero.setVelocityY(-GAMEPLAY.doubleJumpVelocity);
      this.telemetry.emit(1, "input.double_jump");
      this.flashHero(PALETTE.geo);
      this.spawnDoubleJumpBurst();
      this.audio.doubleJump();
      return;
    }
    // Neither grounded nor double-jump available — buffer the press in case
    // the participant pressed slightly too early before landing.
    this.bufferedJumpAtMs = nowMs;
    this.telemetry.emit(1, "input.jump.buffered");
  }

  private performGroundJump(_nowMs: number): void {
    const mul = this.inSwamp ? GAMEPLAY.swampJumpMultiplier : 1;
    this.hero.setVelocityY(-GAMEPLAY.jumpVelocity * mul);
    this.lastGroundedAtMs = -Infinity; // Spend the coyote window.
    this.telemetry.emit(1, "input.jump", { swamp: this.inSwamp ? 1 : 0 });
    this.audio.jump();
  }

  private jumpHeld(): boolean {
    // The InputService only exposes edge-triggered jump; checking held state
    // via the raw key/button is fine for the jump-cut polish. Falls back to
    // "not held" if input plugins are missing (keyboard-less env).
    const kb = this.input.keyboard;
    if (kb?.checkDown(kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE), 0)) return true;
    const pad = this.input.gamepad?.pad1;
    if (pad?.buttons[0]?.pressed === true) return true;
    return false;
  }

  // --- attack ---------------------------------------------------------

  private handleAttackPress(nowMs: number): void {
    if (nowMs - this.lastAttackAtMs < GAMEPLAY.attackCooldownMs) return;
    this.lastAttackAtMs = nowMs;
    this.attackHitsThisSwing = new Set();
    this.swordEchoAccumMs = 0;
    this.telemetry.emit(1, "input.attack");
    this.audio.attack();

    if (this.swordSprite) {
      this.swordSprite.destroy();
    }
    const handX = this.hero.x + 4;
    const handY = this.hero.y - 16;
    this.swordSprite = this.physics.add.sprite(handX, handY, "sword");
    this.swordSprite.setOrigin(0, 0.5);
    (this.swordSprite.body as Phaser.Physics.Arcade.Body | null)?.setAllowGravity(false);
    this.swordSprite.setDepth(5);
    this.swordSprite.setAngle(-70);
    this.swordSprite.setScale(0.9, 1.1);
    this.tweens.add({
      targets: this.swordSprite,
      angle: 60,
      duration: GAMEPLAY.attackDurationMs,
      ease: "Cubic.easeOut",
    });

    // Re-enabled the Knight attack animation now that the loader uses
    // the correct 144×64 frame size (22 frames). The animated source
    // already shows the sword sweep + lunge — so we hide our
    // standalone sword glyph (it's only kept around as an invisible
    // collision rectangle). When the animation completes the hero
    // returns to running (or idle if state was reverted mid-swing).
    const knightAttackAvailable = this.anims.exists("knight.attack");
    if (knightAttackAvailable) {
      this.swordSprite.setAlpha(0);
      this.hero.play("knight.attack", true);
      this.hero.once(
        Phaser.Animations.Events.ANIMATION_COMPLETE_KEY + "knight.attack",
        () => {
          if (this.heroState === "running" && this.anims.exists("knight.run")) {
            this.hero.play("knight.run", true);
          } else if (this.anims.exists("knight.idle")) {
            this.hero.play("knight.idle", true);
          }
        },
      );
    }
  }

  private advanceAttack(nowMs: number, dtMs: number): void {
    if (!this.swordSprite) return;
    if (nowMs - this.lastAttackAtMs > GAMEPLAY.attackDurationMs) {
      this.swordSprite.destroy();
      this.swordSprite = undefined;
      return;
    }
    // Sword hilt rides at the hero's hand each frame (origin is 0, 0.5
    // so the swing arc pivots correctly when angle tweens).
    this.swordSprite.setPosition(this.hero.x + 4, this.hero.y - 16);

    // Motion-trail echo — only when the Knight attack animation is NOT
    // available (placeholder mode). The animated Knight sheet already
    // has a sword sweep baked in, so we skip the echo to avoid
    // double-imaging when the real anim is playing.
    if (!this.anims.exists("knight.attack")) {
      this.swordEchoAccumMs += dtMs;
      if (this.swordEchoAccumMs >= 50) {
        this.swordEchoAccumMs = 0;
        const echo = this.add
          .image(this.swordSprite.x, this.swordSprite.y, "sword")
          .setOrigin(0, 0.5)
          .setAngle(this.swordSprite.angle)
          .setScale(this.swordSprite.scaleX, this.swordSprite.scaleY)
          .setAlpha(0.45)
          .setDepth(4);
        this.tweens.add({
          targets: echo,
          alpha: 0,
          duration: 220,
          ease: "Quad.easeOut",
          onComplete: () => echo.destroy(),
        });
      }
    }

    // Manual overlap so we don't have to set up a one-shot collider per swing.
    const swordBounds = this.swordSprite.getBounds();
    const targets = this.gegnerGroup.getChildren().slice();
    for (const child of targets) {
      const g = child as Phaser.Physics.Arcade.Sprite | undefined;
      if (!g || !g.active) continue;
      if (this.attackHitsThisSwing.has(g)) continue;
      if (Phaser.Geom.Intersects.RectangleToRectangle(swordBounds, g.getBounds())) {
        this.attackHitsThisSwing.add(g);
        this.killGegner(g);
      }
    }
  }

  private killGegner(g: Phaser.Physics.Arcade.Sprite): void {
    this.attacksLanded += 1;
    this.gegnerDefeated += 1;
    this.advancePuzzle("kills");
    this.telemetry.emit(2, "gegner.defeated", {
      x: Math.round(g.x),
      total: this.gegnerDefeated,
    });
    this.blastAt(g.x, g.y);
    this.audio.gegnerDefeated();
    // Soft "thump" — half the budget so consecutive kills feel
    // rhythmic without piling on.
    this.shakeCamera(1, 60);
    g.destroy();
    if (this.gegnerDefeated === GAMEPLAY.attackMasteredKills) {
      this.telemetry.emit(2, "skill.mastered", { skill: "attack" });
    }
  }

  /**
   * Positive impact burst — used when an attack destroys a Gegner.
   *   - Center flash: bright yellow rectangle that scales up and fades.
   *   - Ring expansion: outlined rectangle that scales to ~4.5× with a
   *     swordEdge stroke, reading as a shockwave.
   *   - 8 radiating sparks: small geo-coloured rects that fly outward
   *     and shrink, suggesting fragments + a celebratory "ding".
   */
  private blastAt(x: number, y: number): void {
    const burst = this.add
      .rectangle(x, y, 12, 12, PALETTE.bannerText, 0.95)
      .setDepth(6);
    this.tweens.add({
      targets: burst,
      scaleX: 2.8,
      scaleY: 2.8,
      alpha: 0,
      duration: 240,
      ease: "Quad.easeOut",
      onComplete: () => burst.destroy(),
    });

    const ring = this.add
      .rectangle(x, y, 8, 8, 0x000000, 0)
      .setStrokeStyle(2, PALETTE.swordEdge)
      .setDepth(6)
      .setAlpha(0.9);
    this.tweens.add({
      targets: ring,
      scaleX: 4.5,
      scaleY: 4.5,
      alpha: 0,
      duration: 320,
      ease: "Cubic.easeOut",
      onComplete: () => ring.destroy(),
    });

    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const dx = Math.cos(angle) * 18;
      const dy = Math.sin(angle) * 18;
      const spark = this.add
        .rectangle(x, y, 3, 3, PALETTE.geo, 1)
        .setDepth(7);
      this.tweens.add({
        targets: spark,
        x: x + dx,
        y: y + dy,
        scaleX: 0.2,
        scaleY: 0.2,
        alpha: 0,
        duration: 320,
        ease: "Quad.easeOut",
        onComplete: () => spark.destroy(),
      });
    }
  }

  // --- interact -------------------------------------------------------

  private handleInteractPress(_nowMs: number): void {
    this.telemetry.emit(1, "input.interact");
    // Pick the nearest unaccepted tower within reach.
    let target: SidequestTowerSprite | undefined;
    let bestDist = Infinity;
    const towers = this.towerGroup.getChildren().slice();
    for (const child of towers) {
      const t = child as SidequestTowerSprite | undefined;
      if (!t || !t.active || t.accepted) continue;
      const dx = t.x - this.hero.x;
      const dy = t.y - this.hero.y;
      const d = Math.hypot(dx, dy);
      if (d < 28 && d < bestDist) {
        bestDist = d;
        target = t;
      }
    }
    if (target) {
      this.acceptSidequest(target);
    }
  }

  private acceptSidequest(tower: SidequestTowerSprite): void {
    if (tower.isLever) {
      // Pulling a lever ticks the puzzle goal. The lever prop stays on
      // screen (still scrolls left + culls naturally) but flips its tint
      // green so the player gets a clear "activated" cue. We mark it
      // `accepted` so the interact loop doesn't re-target it.
      tower.accepted = true;
      tower.setTint(PALETTE.leverHandleArmed);
      this.advancePuzzle("lever");
      this.telemetry.emit(2, "puzzle.lever.pulled", { depth: this.levelDepth });
      return;
    }
    if (tower.isExitGate) {
      // Refuse interaction while the puzzle is still locked. Quick
      // banner reminds the player what they need to do.
      if (this.puzzleLocksExit()) {
        this.showBanner(
          t("banner.gate_locked.title"),
          this.puzzle ? t(this.puzzle.labelKey) : t("banner.gate_locked.fallback"),
        );
        this.telemetry.emit(1, "puzzle.exit_blocked", {
          kind: this.puzzle?.kind ?? "unknown",
          progress: this.puzzle?.progress ?? 0,
          target: this.puzzle?.target ?? 0,
        });
        return;
      }
      this.exitSubLevel(tower);
      return;
    }
    // Player-authored portals branch into their custom sub-level path.
    if (tower.authoredSidequestId && this.authoredService) {
      const sidequest = this.authoredService.list().find((s) => s.id === tower.authoredSidequestId);
      if (sidequest) {
        const overlay = this.authorTowerOverlays.get(tower);
        if (overlay) {
          overlay.destroy();
          this.authorTowerOverlays.delete(tower);
        }
        this.enterAuthoredSubLevel(sidequest, tower);
        return;
      }
    }
    this.enterSubLevel(tower);
  }

  /**
   * Push the current level's transient state onto the stack and switch
   * the scene into a sub-level. Visual cue: parallax gets a depth-based
   * tint; banner announces the new depth; geo / HP / inventory persist
   * (the sub-level is a detour, not a reset). Exit gates spawn
   * periodically inside the sub-level until the player interacts with
   * one to pop back.
   */
  private enterSubLevel(tower: SidequestTowerSprite | null): void {
    const id = tower?.sidequestId ?? `debug-d${this.levelDepth + 1}`;
    if (tower) tower.accepted = true;

    // Record the dive on the session log BEFORE incrementing depth so
    // the entry is attributed to the parent (the level the player was
    // actually IN when they interacted with the gate).
    this.portalEntries.push({
      depth: this.levelDepth,
      distance: this.levelDistance,
      targetDepth: this.levelDepth + 1,
    });
    this.audio.portalEnter();

    this.levelStack.push({
      backgroundId: this.cfg.backgroundId ?? DEFAULT_BACKGROUND_ID,
      elapsedSec: this.elapsedSec,
      durationLimitSec: this.durationLimitSec,
      chunkIndex: this.chunkIndex,
      isTutorial: this.isTutorial,
      nextSignIdx: this.nextSignIdx,
      scrollAccum: this.scrollAccum,
      groundScrollAccum: this.groundScrollAccum,
      levelDistance: this.levelDistance,
    });
    this.levelDepth += 1;
    this.sidequestsAccepted += 1;

    this.clearLevelEntities();

    // Sub-level state: fresh elapsed/chunk timers, tutorial signs paused.
    this.elapsedSec = 0;
    this.chunkIndex = 1;
    this.scrollAccum = 0;
    this.groundScrollAccum = 0;
    this.levelDistance = 0;
    this.gegnerTimerSec = 0;
    this.geoTimerSec = 0;
    this.towerTimerSec = 0;
    this.scrollTimerSec = 0;
    this.heartTimerSec = 0;
    this.crystalTimerSec = 0;
    this.bootTimerSec = 0;
    this.exitGateTimerSec = 0;
    this.isTutorial = false; // restored on exit
    // Sub-level cap: long enough for the player to take their time, but
    // exit gates spawn every 8 s so they're never far from a way out.
    this.durationLimitSec = GAMEPLAY.mainLoopDurationSec;
    this.fallingRocks = [];
    this.rockTimerSec = 0;
    this.planLevelAbyss();

    // Swap the parallax to a different pack so the sub-level is
    // visually unmistakable. Pack rotates by depth modulo the
    // available packs. Clear any old tint since the new pack has
    // its own intrinsic palette.
    this.applyPackForDepth(this.levelDepth);
    this.refreshTutorialBreadcrumb();
    this.subLevelTimerSec = 0;
    this.leverSpawnedThisLevel = 0;
    this.puzzle = this.rollPuzzleForSubLevel();
    this.subLevelEntryAtMs = this.timeMs();

    this.telemetry.emit(2, "sidequest.entered", {
      id,
      depth: this.levelDepth,
      puzzle: this.puzzle?.kind ?? "none",
    });
    if (this.isUgcVariant) {
      const biome = this.packLabelForDepth(this.levelDepth);
      const biomeDisplay = this.packDisplayLabelForDepth(this.levelDepth);
      const levelSeed = this.chunkIndex + this.levelDepth * 1000;
      const displayName = this.levelDepth === 1
        ? t("banner.sidequest.biome", { biome: biomeDisplay })
        : t("banner.sublevel.depth", { depth: this.levelDepth });
      this.announceLevelAuthor(`sublevel:${id}`, biome, levelSeed, displayName);
    } else {
      this.showBanner(
        this.levelDepth === 1
          ? t("banner.sidequest.enter")
          : t("banner.sublevel.depth", { depth: this.levelDepth }),
        this.puzzle ? t(this.puzzle.labelKey) : `${id} · ${this.packDisplayLabelForDepth(this.levelDepth)}`,
      );
    }
    this.audio.skillUnlock();

    tower?.destroy();
  }

  /**
   * Pop the saved context off the stack and restore the parent level —
   * background, scroll accumulators, tutorial sign index, durationLimit,
   * etc. Persistent state (HP, geo, inventory, skills) carries through
   * unchanged. Sub-level entities are cleared so the parent picks up
   * clean.
   */
  private exitSubLevel(tower: SidequestTowerSprite | null): void {
    if (this.levelDepth === 0 || this.levelStack.length === 0) {
      tower?.destroy();
      return;
    }
    const context = this.levelStack.pop();
    if (!context) {
      tower?.destroy();
      return;
    }
    const exitedFromDepth = this.levelDepth;
    this.levelDepth -= 1;
    this.sidequestsCleared += 1;
    this.audio.portalEnter();

    this.clearLevelEntities();

    this.cfg.backgroundId = context.backgroundId;
    this.elapsedSec = context.elapsedSec;
    this.durationLimitSec = context.durationLimitSec;
    this.chunkIndex = context.chunkIndex;
    this.isTutorial = context.isTutorial;
    this.nextSignIdx = context.nextSignIdx;
    this.scrollAccum = context.scrollAccum;
    this.groundScrollAccum = context.groundScrollAccum;
    this.levelDistance = context.levelDistance;
    this.exitGateTimerSec = 0;
    this.rockTimerSec = 0;
    this.planLevelAbyss();

    // Restore the parent level's parallax pack (and clear any tint).
    this.applyPackForDepth(this.levelDepth);
    this.refreshTutorialBreadcrumb();
    this.subLevelTimerSec = 0;
    // Puzzle state lives inside one sub-level; clear on exit so the
    // parent isn't suddenly puzzle-locked.
    this.puzzle = null;
    this.leverSpawnedThisLevel = 0;
    if (this.subLevelEndGradient) {
      this.subLevelEndGradient.destroy();
      this.subLevelEndGradient = undefined;
    }
    if (this.subLevelEndFog) {
      this.subLevelEndFog.destroy();
      this.subLevelEndFog = undefined;
    }
    // Surfacing back to a parent level is the same kind of world-swap
    // as diving — re-use the blink-in freeze so the player gets a
    // moment to re-orient before the auto-run picks back up.
    this.subLevelEntryAtMs = this.timeMs();

    this.telemetry.emit(2, "sidequest.cleared", {
      id: tower?.sidequestId ?? "auto.gradient",
      fromDepth: exitedFromDepth,
      toDepth: this.levelDepth,
    });
    this.showBanner(
      this.levelDepth === 0
        ? t("banner.sidequest.cleared")
        : t("banner.sublevel.returned", { depth: this.levelDepth }),
      tower?.sidequestId ?? "auto-exit",
    );
    if (this.isUgcVariant) {
      if (this.levelDepth === 0) {
        this.restoreMainAuthor();
      } else if (this.authorIdentityService) {
        // Surfaced parent: pick a stable author for the depth we just returned to.
        const biome = this.packLabelForDepth(this.levelDepth);
        const levelSeed = this.chunkIndex + this.levelDepth * 1000;
        this.currentLevelAuthorSeed = subLevelAuthorSeed(this.levelDepth, biome, levelSeed);
        const identity = this.authorIdentityService.forSeed(this.currentLevelAuthorSeed);
        this.authorBadge?.setIdentity(identity);
      }
    }
    this.audio.skillUnlock();

    tower?.destroy();
  }

  /** Destroy every collectible / enemy / sign currently in the world. */
  private clearLevelEntities(): void {
    this.geoGroup.clear(true, true);
    this.gegnerGroup.clear(true, true);
    this.towerGroup.clear(true, true);
    this.scrollGroup.clear(true, true);
    this.pickupGroup.clear(true, true);
    this.platformGroup.clear(true, true);
    this.vineGroup.clear(true, true);
    // Authored-tower overlays live outside the tower group; tear them
    // down explicitly so they don't dangle when the level changes.
    this.authorTowerOverlays.forEach((o) => o.destroy());
    this.authorTowerOverlays.clear();
    for (const s of this.signs) s.destroy();
    this.signs = [];
    for (const b of this.swampBands) b.destroy();
    this.swampBands = [];
    this.lastSwampSegSpawned = -1;
    // Dive-bomb projectiles spawned by birds.
    for (const p of this.birdProjectiles) p.destroy();
    this.birdProjectiles = [];
    // Falling-rock obstacles.
    this.clearRocks();
    // Reset canopy state — abort any in-flight climb and let the
    // depth-0 cadence start fresh after a level change.
    this.canopyTimerSec = 0;
    if (this.heroState === "climbing") {
      this.abortClimb();
    }
  }

  /**
   * Swap the parallax to whichever forest pack matches this depth.
   * Pack rotation cycles modulo `FOREST_PACKS.length`. Tint is cleared
   * because each pack has its own intrinsic palette — no need to
   * colour-shift a green pack to look "cooler" when we can just hand
   * the player a real snowy forest.
   */
  private applyPackForDepth(depth: number): void {
    const pack = FOREST_PACKS[depth % FOREST_PACKS.length];
    if (!pack) return;
    this.parallax.swapForestPack(this, pack.keys, pack.factors);
    this.parallax.clearTint();
  }

  /** Canonical (English) biome identifier. Used for telemetry, identity seeds,
   *  and anywhere a stable string is needed across languages. Do not show
   *  this to the participant directly — use `packDisplayLabelForDepth` for UI. */
  private packLabelForDepth(depth: number): string {
    return FOREST_PACKS[depth % FOREST_PACKS.length]?.label ?? "?";
  }

  /** Translated biome label for participant-facing surfaces (banners, map,
   *  HUD breadcrumb). Maps the canonical label through the i18n catalog;
   *  falls back to the raw label if a new biome lands without a translation. */
  private packDisplayLabelForDepth(depth: number): string {
    const raw = this.packLabelForDepth(depth);
    switch (raw) {
      case "Mysterious":
        return t("biome.mysterious");
      case "Forrest":
        return t("biome.forrest");
      case "Snowy":
        return t("biome.snowy");
      default:
        return raw;
    }
  }

  /** Debug: jump directly to a target sub-level depth. Pushes / pops
   *  the level stack as needed by invoking the regular enter / exit
   *  flow without a backing tower sprite. */
  private debugGotoDepth(target: number): void {
    if (!this.debugMode) return;
    const clamped = Math.max(0, Math.min(target, 9));
    // Safety cap — avoid pathological loops if invariants break.
    for (let i = 0; i < 16 && this.levelDepth < clamped; i++) {
      this.enterSubLevel(null);
    }
    for (let i = 0; i < 16 && this.levelDepth > clamped; i++) {
      this.exitSubLevel(null);
    }
    this.telemetry.emit(1, "debug.goto_depth", { target: clamped, depth: this.levelDepth });
  }

  /** Debug: refill HP to max. Routes through setHp() so the HUD repaints
   *  via the regular paint loop and telemetry captures the jump. */
  private debugRefillHp(): void {
    if (!this.debugMode) return;
    if (this.hp >= GAMEPLAY.heroMaxHp) return;
    this.setHp(GAMEPLAY.heroMaxHp, "debug");
    this.audio.hpGain();
  }

  /**
   * Spawn the sub-level's end-of-level gradient + trailing fog at the
   * right edge of the world. Both are tinted to the current level's
   * pack colour so the "fading out" mood matches the biome (green for
   * Forrest, icy for Snowy, navy for Mysterious). As the world
   * scrolls, the fade slides into view and the solid fog occludes
   * everything past it — the auto-exit fires when the fade's right
   * edge crosses the hero.
   */
  private spawnSubLevelEndGradient(): void {
    if (!this.textures.exists("blackGradient")) return;
    if (this.subLevelEndGradient) return;

    const tint = this.tintForCurrentDepth();
    const grad = this.add
      .image(NATIVE_WIDTH, 0, "blackGradient")
      .setOrigin(0, 0)
      .setDepth(8);
    grad.setTint(tint);
    // Fog rectangle starts where the gradient ends — the world to the
    // right of the fade is fully covered by solid tinted colour. Same
    // depth as the gradient so they stack as one composite occluder.
    // Overlap the fog 1 px back into the gradient so the gradient's
    // rightmost (fully opaque) column and the fog's leftmost column
    // share a pixel — eliminates the seam that appeared at the
    // boundary between the two sprites.
    const fog = this.add
      .rectangle(NATIVE_WIDTH + GRADIENT_FADE_WIDTH - 1, 0, FOG_WIDTH, NATIVE_HEIGHT, tint, 1.0)
      .setOrigin(0, 0)
      .setDepth(8);

    this.subLevelEndGradient = grad;
    this.subLevelEndFog = fog;
    this.telemetry.emit(2, "sidequest.end_gradient.spawn", {
      depth: this.levelDepth,
      tint,
    });
  }

  /** Resolve the tint colour for the currently-active sub-level pack. */
  private tintForCurrentDepth(): number {
    const pack = FOREST_PACKS[this.levelDepth % FOREST_PACKS.length];
    return pack?.tint ?? 0x000000;
  }

  /** Spawn an exit gate inside the current sub-level. Cyan-tinted to read as "way out". */
  private spawnExitGate(): void {
    if (this.levelDepth === 0) return;
    const x = NATIVE_WIDTH + 16;
    const locked = this.puzzleLocksExit();
    // Exit gates use the "tower.up" texture so the inner shading reads
    // as a shaft climbing back to the parent level. When locked, swap
    // to a kind-specific variant that bakes in a brass lock plate with
    // a keyhole shaped like the item the puzzle needs.
    const textureKey = locked && this.puzzle
      ? this.lockedExitTextureKey(this.puzzle.kind)
      : "tower.up";
    const t = this.towerGroup.create(x, this.groundTopY, textureKey) as SidequestTowerSprite;
    t.setOrigin(0.5, 1);
    t.setScale(0.5);
    // Locked exit gates render in warning red until the puzzle is solved;
    // unlocked ones in cyan to read as "way out".
    t.setTint(locked ? 0xff7a7a : 0x9ad8ff);
    t.sidequestId = `exit-d${this.levelDepth}-${Math.floor(Math.random() * 1000)}`;
    t.accepted = false;
    t.isExitGate = true;
    this.telemetry.emit(2, "sidequest.exit_gate.spawn", {
      depth: this.levelDepth,
      locked: locked ? 1 : 0,
    });
  }

  /**
   * Roll a random puzzle for the sub-level just entered. Returns null
   * (no puzzle — free exit) `1 - puzzleChance` of the time so not every
   * detour turns into a goal-grind. Lever puzzles spawn at most one
   * lever prop; the cap is enforced inside `maybeSpawnLever`.
   */
  private rollPuzzleForSubLevel(): PuzzleState | null {
    if (this.levelDepth === 0) return null;
    if (Math.random() > GAMEPLAY.puzzleChance) return null;
    const kinds: PuzzleState["kind"][] = ["kills", "crystals", "lever"];
    const kind = kinds[Math.floor(Math.random() * kinds.length)] ?? "kills";
    if (kind === "kills") {
      return {
        kind,
        target: GAMEPLAY.puzzleGoalKills,
        progress: 0,
        labelKey: "puzzle.label.kills",
        solved: false,
      };
    }
    if (kind === "crystals") {
      return {
        kind,
        target: GAMEPLAY.puzzleGoalCrystals,
        progress: 0,
        labelKey: "puzzle.label.crystals",
        solved: false,
      };
    }
    return {
      kind,
      target: 1,
      progress: 0,
      labelKey: "puzzle.label.lever",
      solved: false,
    };
  }

  /** True when the active puzzle's solved flag is false — exit gates
   *  refuse interaction while this is true. */
  private puzzleLocksExit(): boolean {
    return this.puzzle !== null && !this.puzzle.solved;
  }

  /** Texture key for a locked exit gate, picking the variant whose
   *  baked-in keyhole matches the item the puzzle needs. */
  private lockedExitTextureKey(kind: PuzzleState["kind"]): string {
    return `tower.up.locked.${kind}`;
  }

  /** Tick the active puzzle's progress; mark solved + show banner on completion. */
  private advancePuzzle(kind: PuzzleState["kind"], by: number = 1): void {
    const p = this.puzzle;
    if (!p || p.solved || p.kind !== kind) return;
    p.progress = Math.min(p.target, p.progress + by);
    this.telemetry.emit(2, "puzzle.progress", {
      kind: p.kind,
      progress: p.progress,
      target: p.target,
    });
    if (p.progress >= p.target) {
      p.solved = true;
      this.audio.skillUnlock();
      this.showBanner(t("banner.puzzle.solved.title"), t("banner.puzzle.solved.subtitle"));
      // Re-tint any already-spawned exit gates so the player sees them
      // turn cyan immediately rather than waiting for the next spawn.
      // Also swap them back to the plain (no-keyhole) texture so the
      // brass lock plate disappears in lockstep with the colour change.
      this.towerGroup.getChildren().forEach((obj) => {
        const t = obj as SidequestTowerSprite | undefined;
        if (t?.isExitGate) {
          t.setTint(0x9ad8ff);
          t.setTexture("tower.up");
        }
      });
      this.telemetry.emit(2, "puzzle.solved", { kind: p.kind });
    }
  }

  /**
   * Drop a single brass-handled lever prop into the world for a
   * lever-kind puzzle. Capped at 1 per sub-level so the player has
   * exactly one objective to find. Re-uses the tower placement so
   * the existing interact-when-near pickup behaviour applies.
   */
  private maybeSpawnLever(forceX?: number): void {
    if (!this.puzzle || this.puzzle.kind !== "lever") return;
    if (this.leverSpawnedThisLevel >= 1) return;
    const x = forceX ?? NATIVE_WIDTH + 16;
    const t = this.towerGroup.create(x, this.groundTopY, "lever") as SidequestTowerSprite;
    t.setOrigin(0.5, 1);
    t.setScale(1);
    t.sidequestId = `lever-d${this.levelDepth}`;
    t.accepted = false;
    t.isLever = true;
    this.leverSpawnedThisLevel = 1;
    this.telemetry.emit(2, "puzzle.lever.spawn", { depth: this.levelDepth });
  }

  // --- inventory + skills --------------------------------------------

  private addInventory(kind: InventoryItemKind, nowMs: number): boolean {
    if (this.inventory.length >= GAMEPLAY.inventorySlots) return false;
    this.inventory.push({ kind, pickedUpAtMs: nowMs });
    this.inventoryItemsCollected += 1;
    this.telemetry.emit(2, "inventory.added", {
      kind,
      size: this.inventory.length,
    });
    this.refreshInventoryHud();
    this.audio.inventoryAdd();
    return true;
  }

  // -- RunnerWalletHost surface ------------------------------------------
  //
  // Public methods consumed by RunnerWallet via the RunnerWalletHost
  // interface. They forward to the scene's existing fields and trigger the
  // same HUD / telemetry side-effects pickup handlers do, so shop trades
  // look identical to real pickups from the player's perspective.

  getGeo(): number {
    return this.geoCollected;
  }
  addGeo(delta: number, source: string): void {
    this.geoCollected = Math.max(0, this.geoCollected + delta);
    if (delta > 0) this.incomeTracker?.recordGeo(delta);
    this.telemetry.emit(2, "geo.delta", { delta, total: this.geoCollected, source });
    this.pulseGeoCounter();
    // Re-unlock check for double-jump if the player crossed the threshold
    // through a shop trade just like they would via a pickup.
    if (delta > 0 && !this.skills.doubleJump) {
      this.maybeReUnlockDoubleJump("shop");
    }
  }
  getHp(): number {
    return this.hp;
  }
  getMaxHp(): number {
    return GAMEPLAY.heroMaxHp;
  }
  setHp(value: number, source: string): void {
    const before = this.hp;
    this.hp = Math.max(0, Math.min(GAMEPLAY.heroMaxHp, value));
    if (this.hp > before) this.incomeTracker?.recordHp(this.hp - before);
    this.telemetry.emit(2, "hp.delta", { from: before, to: this.hp, source });
    // HP slot rects are repainted by the existing paintHud() loop on every update;
    // no explicit refresh call needed here.
  }
  getFeatherCharges(): number {
    return this.escapeCharges;
  }
  setFeatherCharges(value: number, source: string): void {
    const before = this.escapeCharges;
    this.escapeCharges = Math.max(0, Math.min(GAMEPLAY.featherMaxCharges, value));
    if (this.escapeCharges > before) this.incomeTracker?.recordFeather(this.escapeCharges - before);
    this.telemetry.emit(2, "feather.delta", { from: before, to: this.escapeCharges, source });
  }
  getInventory(): readonly InventoryItem[] {
    return this.inventory;
  }
  addInventoryItem(itemId: string, source: string): boolean {
    const ok = this.addInventory(itemId as InventoryItemKind, this.timeMs());
    if (ok) this.telemetry.emit(2, "inventory.added.via", { itemId, source });
    return ok;
  }
  removeInventoryItem(itemId: string, source: string): boolean {
    const idx = this.inventory.findIndex((it) => it.kind === itemId);
    if (idx < 0) return false;
    this.inventory.splice(idx, 1);
    this.telemetry.emit(2, "inventory.removed.via", { itemId, source });
    this.refreshInventoryHud();
    return true;
  }
  isSkillUnlocked(id: SkillId): boolean {
    if (id === "doubleJump") return this.skills.doubleJump;
    return this.escapeFeatherUnlocked;
  }
  relockSkill(id: SkillId, source: "shop"): void {
    if (id === "doubleJump") {
      if (!this.skills.doubleJump) return;
      this.skills.doubleJump = false;
      this.hasDoubleJump = false;
      this.wasDoubleJumpEverSold = true;
      this.geoTowardDoubleJumpReUnlock = 0;
      this.tintSkillIcon("doubleJump", PALETTE.skillLocked);
      this.telemetry.emit(2, "skill.relocked", { id, source });
    } else {
      if (!this.escapeFeatherUnlocked) return;
      this.escapeFeatherUnlocked = false;
      this.escapeCharges = 0;
      this.telemetry.emit(2, "skill.relocked", { id, source });
    }
  }
  reunlockSkill(id: SkillId, source: "shop" | "geo_threshold" | "pickup"): void {
    if (id === "doubleJump") {
      if (this.skills.doubleJump) return;
      this.unlockSkill("doubleJump", source);
      this.telemetry.emit(2, "skill.reunlocked", { id, source });
    } else {
      if (this.escapeFeatherUnlocked) return;
      this.escapeFeatherUnlocked = true;
      this.telemetry.emit(2, "skill.reunlocked", { id, source });
    }
  }
  private maybeReUnlockDoubleJump(source: "shop" | "pickup" | "geo_threshold"): void {
    if (this.skills.doubleJump) return;
    const counter = this.wasDoubleJumpEverSold
      ? this.geoTowardDoubleJumpReUnlock
      : this.geoCollected;
    if (counter >= GAMEPLAY.doubleJumpUnlockGeo) {
      this.reunlockSkill("doubleJump", source);
    }
  }
  private tintSkillIcon(skill: Skill, color: number): void {
    const icon = this.skillIcons[skill];
    if (icon) icon.setTint(color);
  }

  private unlockSkill(skill: Skill, reason: string): void {
    if (this.skills[skill]) return;
    this.skills[skill] = true;
    if (skill === "doubleJump") this.hasDoubleJump = true;
    this.telemetry.emit(2, "skill.unlocked", { skill, reason });
    this.audio.skillUnlock();
    const icon = this.skillIcons[skill];
    if (icon) {
      icon.clearTint();
      this.tweens.add({
        targets: icon,
        scale: { from: 1.6, to: 1.0 },
        duration: 260,
        ease: "Back.easeOut",
      });
    }
    this.showBanner(t("banner.skill.unlocked"), this.skillLabel(skill));
  }

  private skillLabel(skill: Skill): string {
    switch (skill) {
      case "jump":
        return t("skill.jump");
      case "doubleJump":
        return t("skill.double_jump");
      case "attack":
        return t("skill.attack");
      case "interact":
        return t("skill.interact");
    }
  }

  // --- construction helpers ------------------------------------------

  private buildGround(): void {
    this.groundGroup = this.physics.add.staticGroup();
    for (let i = 0; i < this.tileCount; i++) {
      const x = i * TILE_SIZE;
      const top = this.groundGroup
        .create(x, this.groundTopY, "ground")
        .setOrigin(0, 0) as Phaser.Physics.Arcade.Sprite;
      const bottom = this.groundGroup
        .create(x, this.groundTopY + TILE_SIZE, "ground")
        .setOrigin(0, 0) as Phaser.Physics.Arcade.Sprite;
      top.refreshBody();
      bottom.refreshBody();
      this.tiles.push(top, bottom);
    }
  }

  private recycleGround(scrollPx: number): void {
    // Deterministic positioning from a single shared accumulator. Each
    // column index `i` is placed at `(i * TILE_SIZE - accum)` modulo
    // the total cycle width, then shifted by -TILE_SIZE so the
    // off-screen wrap-around tile sits just left of x=0. The integer
    // floor at the end keeps adjacent tiles flush at the same pixel
    // boundary — fixes the 1-px gap that appeared when each tile was
    // decremented independently and rounded apart.
    this.groundScrollAccum += scrollPx;
    const cycle = this.tileCount * TILE_SIZE;
    if (this.groundScrollAccum >= cycle) this.groundScrollAccum -= cycle;
    for (let idx = 0; idx < this.tiles.length; idx++) {
      const t = this.tiles[idx]!;
      const i = Math.floor(idx / 2);
      const isBottomRow = idx % 2 === 1;
      let x = i * TILE_SIZE - this.groundScrollAccum;
      x = ((x % cycle) + cycle) % cycle - TILE_SIZE;
      const tileX = Math.floor(x);
      const tileWorldX = this.worldDistTravelled + tileX;
      // Per-column ground top — terraced elevation pattern lifted
      // from `groundYForWorldX`. Each segment of ~6 tiles sits at
      // its own height so the basic floor "goes up and down with
      // ascents, hills, valleys" (Visual Brief §5 motion variety)
      // without needing slope physics.
      const columnTopY = this.groundYForWorldX(tileWorldX);
      t.x = tileX;
      t.y = columnTopY + (isBottomRow ? TILE_SIZE : 0);
      // Abyss check: stable function of world-x; if true, hide the
      // whole column so the gap reads as a real pit.
      const inAbyss = this.isWorldXInAbyss(tileWorldX);
      t.setVisible(!inAbyss);
      if (t.body) (t.body as Phaser.Physics.Arcade.StaticBody).enable = !inAbyss;
      t.refreshBody();
    }
  }

  /**
   * Terraced ground elevation profile (Option D). Pure function of
   * world-x — every segment of `TERRACE_SEGMENT_LEN` tiles sits at a
   * fixed elevation offset relative to the baseline. Adjacent segments
   * with different elevations form a step that the hero either jumps
   * up (rising step) or falls off (descending step). No slope physics
   * required — the Arcade tile collision handles the steps naturally.
   *
   * Pattern is hand-tuned so the level alternates between baseline
   * (offset 0), single-tile rises (offset -16), and one occasional
   * double-tile rise (offset -32). Max rise stays ≤ 32 px so a single
   * jump always clears it — the level reads as undulating terrain,
   * not as a tower-climb challenge.
   */
  private groundYForWorldX(worldX: number): number {
    const baseY = NATIVE_HEIGHT - 32;
    // Safe baseline stretch at the start of the run so the hero never
    // starts a session next to a step or abyss.
    if (worldX < TILE_SIZE * 16) return baseY;
    const segmentLen = 6 * TILE_SIZE; // 96 px per terrace
    const segmentIdx = Math.floor(worldX / segmentLen);
    // Pattern length is coprime with the abyss period (320 px / 5 tiles)
    // so the two effects don't overlap rhythmically.
    const pattern = [0, 0, -16, 0, -32, -16, 0, -16];
    const offset = pattern[((segmentIdx % pattern.length) + pattern.length) % pattern.length] ?? 0;
    return baseY + offset;
  }

  /**
   * Periodic swamp pattern in world space — every `SWAMP_PERIOD` px,
   * a `SWAMP_WIDTH`-wide stretch slows the hero. Coprime period with
   * the abyss so swamps and pits don't lock-step. Safe stretch at the
   * very start so the player isn't slowed in the first few seconds.
   */
  private isWorldXInSwamp(worldX: number): boolean {
    if (worldX < TILE_SIZE * 38) return false;
    const period = 480;
    const swampStart = 200;
    const swampWidth = 80;
    const phase = ((worldX % period) + period) % period;
    return phase >= swampStart && phase < swampStart + swampWidth;
  }

  /**
   * Spawn a translucent green band over any swamp segment that just
   * came within the player's lookahead window. Each band is anchored
   * to a world-x and slides left with the scroll; culled on the left
   * side via `scrollSwampBands`.
   */
  private maybeSpawnSwampBand(): void {
    const period = 480;
    const swampStart = 200;
    const swampWidth = 80;
    const lookaheadEnd = this.worldDistTravelled + NATIVE_WIDTH + 64;
    const maxSeg = Math.floor(lookaheadEnd / period);
    for (let s = this.lastSwampSegSpawned + 1; s <= maxSeg; s++) {
      const swampStartWorldX = s * period + swampStart;
      if (swampStartWorldX < TILE_SIZE * 38) {
        this.lastSwampSegSpawned = s;
        continue;
      }
      const screenX = swampStartWorldX - this.worldDistTravelled;
      // Murky water band sits in the bottom 18 px (across both ground
      // rows). Two stacked rectangles: a darker base + a lighter ripple
      // ridge near the top so the swamp reads as standing water, not
      // a flat painted square.
      const baseY = NATIVE_HEIGHT - 24;
      const dark = this.add
        .rectangle(screenX, baseY, swampWidth, 24, PALETTE.swampDark, 0.55)
        .setOrigin(0, 0)
        .setDepth(2);
      const ridge = this.add
        .rectangle(screenX, baseY, swampWidth, 2, PALETTE.swampMist, 0.7)
        .setOrigin(0, 0)
        .setDepth(2);
      this.swampBands.push(dark, ridge);
      this.lastSwampSegSpawned = s;
    }
  }

  private scrollSwampBands(scrollPx: number): void {
    if (this.swampBands.length === 0) return;
    const live: Phaser.GameObjects.Rectangle[] = [];
    for (const b of this.swampBands) {
      if (!b.active) continue;
      b.x -= scrollPx;
      if (b.x + b.displayWidth < -16) {
        b.destroy();
        continue;
      }
      live.push(b);
    }
    this.swampBands = live;
  }

  /**
   * Pick at most one dead hole (abyss) for the current level and store its
   * position as `levelAbyssOffsetPx` (offset from the live level origin),
   * or `null` for no hole. Jonathan 2026-05-21: massively reduce pits — one
   * per level max, and never immediately after a swamp (the hero exits a
   * swamp slowed and can't reliably clear a pit). Deterministic from the
   * level origin, recomputed on every level (re)entry.
   */
  private planLevelAbyss(): void {
    const origin = this.worldDistTravelled - this.levelDistance;
    const levelLenPx = this.durationLimitSec * GAMEPLAY.baseScrollSpeed;
    const safeStartPx = TILE_SIZE * 24; // warm-up before the first hazard
    const endMarginPx = TILE_SIZE * 8; // never strand a pit at the exit
    const usablePx = levelLenPx - safeStartPx - endMarginPx;
    // Short levels (e.g. the 60 s tutorial detours) get no pit at all.
    if (usablePx < TILE_SIZE * 6) {
      this.levelAbyssOffsetPx = null;
      return;
    }
    // Deterministic position from the level origin so a given level always
    // places its single hole in the same spot.
    const rng = makeSeededRng((Math.floor(origin) | 0) + this.levelDepth * 99991 + 1);
    let abs = origin + safeStartPx + rng.next() * usablePx;
    // Never immediately after a swamp: shove the hole out of the swamp band
    // and the short recovery window just past it (period / phase mirror
    // `isWorldXInSwamp`: swamp occupies phase [200, 280) of a 480 px cycle).
    const swampPeriod = 480;
    const guardEnd = 280 + 96; // ~one jump past the swamp's trailing edge
    const phase = ((abs % swampPeriod) + swampPeriod) % swampPeriod;
    if (phase >= 200 && phase < guardEnd) abs += guardEnd - phase;
    this.levelAbyssOffsetPx = abs - origin;
  }

  /**
   * True iff the given world-x falls inside this level's single dead hole.
   * The stored offset is relative to the live level origin
   * (`worldDistTravelled - levelDistance`) so it stays aligned with the
   * recycled ground tiles even though `worldDistTravelled` is monotonic
   * across sub-level dives.
   */
  private isWorldXInAbyss(worldX: number): boolean {
    if (this.levelAbyssOffsetPx === null) return false;
    const start = this.worldDistTravelled - this.levelDistance + this.levelAbyssOffsetPx;
    return worldX >= start && worldX < start + this.abyssWidthPx;
  }

  private translateGroup(
    group: Phaser.Physics.Arcade.Group,
    scrollPx: number,
    leftCullPx = -32,
  ): void {
    // Snapshot before iterating so destroys mid-loop don't shift the Set
    // and hand `iterate` an undefined entry (regression seen on Phaser
    // 3.90.x where `Set.iterate` no longer guards null children).
    const children = group.getChildren().slice();
    for (const child of children) {
      const sprite = child as Phaser.Physics.Arcade.Sprite | undefined;
      if (!sprite || !sprite.active) continue;
      sprite.x -= scrollPx;
      if (sprite.x < leftCullPx) sprite.destroy();
    }
  }

  private buildHero(): void {
    const useKnight = this.textures.exists("knight.idle");
    const texKey = useKnight ? "knight.run" : "hero";
    // Anchor at bottom-center (origin 0.5, 1) so the character's feet
    // stay pinned to sprite.y across every animation frame. The 96-wide
    // Knight strips have the character drawn near the frame's centre
    // but the surrounding empty pixels shift between attack frames —
    // with a centre origin the character would appear to "jump back"
    // mid-swing. Bottom-center origin removes that artifact: only the
    // sword visually moves; the feet stay locked.
    const spawnY = this.groundTopY;
    this.hero = this.physics.add.sprite(GAMEPLAY.heroX, spawnY, texKey);
    this.hero.setCollideWorldBounds(false);
    if (useKnight) {
      this.hero.setOrigin(0.5, 1);
      // Runtime scaling — 0.5 brings the 96×64 sprite to 48×32.
      this.hero.setScale(0.5);
      // Body sizes + offsets are set per-animation by the listener
      // below (each strip uses a different frame width: 64 idle,
      // 96 run, 144 attack). Start with the idle body so the
      // initial Knight idle pose has correct collision.
      this.applyHeroBodyForAnim("knight.idle");
      this.hero.on(
        Phaser.Animations.Events.ANIMATION_START,
        (anim: Phaser.Animations.Animation) => {
          this.applyHeroBodyForAnim(anim.key);
        },
      );
    } else {
      this.hero.setOrigin(0.5, 1);
      this.hero.body!.setSize(10, 14, true);
    }
    // Idle until first input — the world-scroll, timers, and spawners
    // are gated on `heroState === "running"` in update().
    if (useKnight && this.anims.exists("knight.idle")) {
      this.hero.play("knight.idle");
    }
    this.hero.setMaxVelocity(200, 800);
    this.physics.world.gravity.y = GAMEPLAY.gravity;
    this.hero.setDepth(4);
  }

  /**
   * Apply the body size + offset that matches a Knight animation's
   * source frame width. Each strip is 64-tall but a different width:
   *   - knight.idle:   64 wide,  character at source x≈23–25 (compact)
   *   - knight.run:    96 wide,  character at source x≈41–44 (mid)
   *   - knight.attack: 144 wide, character lunges from x≈44 to x≈100+
   * Body bottom is always pinned to source y=44 (the visible feet);
   * width and X-offset compensate for the different character positions
   * inside each frame.
   */
  private applyHeroBodyForAnim(animKey: string): void {
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (!body) return;
    if (animKey === "knight.idle") {
      body.setSize(24, 32, false);
      this.hero.setOffset(20, 12);
    } else if (animKey === "knight.run") {
      body.setSize(30, 32, false);
      this.hero.setOffset(33, 12);
    } else if (animKey === "knight.attack") {
      // Attack frame: character starts at x≈55 in early frames and
      // lunges to x≈100+ later. Body covers the early position; the
      // hero is invuln-ish during the lunge anyway since the player
      // is on the offensive.
      body.setSize(40, 32, false);
      this.hero.setOffset(50, 12);
    }
  }

  private buildHud(): void {
    // Build the shared HUD layout (HP / geo / skills / inventory). The
    // market scene constructs the same component for identical visuals.
    this.walletHud = new WalletHud(this, {
      attachTooltip: (t, fn) => this.attachTooltip(t, fn),
      tooltipTextFor: (id) => this.tooltipTextForHud(id),
    });
    // Alias the runner's existing private fields onto the WalletHud's
    // owned objects. Existing animation hooks (HP pulse on damage,
    // skill-icon tween on unlock, boot alpha pulse) keep working unchanged.
    this.hpRects = this.walletHud.hpRects;
    this.geoText = this.walletHud.geoText;
    this.skillIcons = this.walletHud.skillIcons;
    this.bootIcon = this.walletHud.bootIcon;

    // Tooltip layer — single text + rect pair reused for every hover.
    this.buildTooltipPanel();

    // Bottom rail (chunks / kills / sidequest count / depth / boost / etc.).
    this.add
      .rectangle(0, NATIVE_HEIGHT - 12, NATIVE_WIDTH, 12, PALETTE.hudPanel)
      .setOrigin(0, 0);
    this.bottomRailText = this.add
      .text(4, NATIVE_HEIGHT - 10, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0);
  }

  /** Resolve the tooltip caption for a given WalletHud panel. */
  private tooltipTextForHud(id: import("../ui/WalletHud").WalletHudTooltipId): string {
    switch (id) {
      case "hp":
        return `HP · ${this.hp} / ${GAMEPLAY.heroMaxHp}`;
      case "geo":
        return `GEO · ${this.geoCollected} collected`;
      case "skill.jump":
        return this.tooltipForSkill("jump");
      case "skill.doubleJump":
        return this.tooltipForSkill("doubleJump");
      case "skill.attack":
        return this.tooltipForSkill("attack");
      case "skill.interact":
        return this.tooltipForSkill("interact");
      case "skill.boot":
        return this.tooltipForBoot();
      case "inventory":
        return `INVENTORY · ${this.inventory.length} / ${GAMEPLAY.inventorySlots} slots`;
    }
  }

  /**
   * Mini-map / level-progress strip. Bottom-right aligned, sits just
   * above the bottom rail. The strip represents the *full* expected
   * level duration (time, not distance — works equally well for the
   * 60 s tutorial, the 540 s main loop, and the 20 s sub-levels);
   * the hero pip's position along the strip = `elapsedSec /
   * durationLimitSec`.
   *
   * Lookahead is intentionally tight (`miniMapLookaheadPx`, ≈one
   * viewport-width) so the strip never spoils what's coming far
   * ahead. Anything beyond the lookahead is clamped out of the
   * marker pass. Behind the hero stays clear — those entities have
   * long despawned.
   */
  private buildMiniMap(): void {
    const w = this.miniMapWidth;
    const h = 9;
    const x = NATIVE_WIDTH - 4 - w;
    const y = NATIVE_HEIGHT - 24;
    this.miniMapBg = this.add
      .rectangle(x, y, w, h, PALETTE.hudPanel, 0.85)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.miniMapMarkers = this.add.graphics().setDepth(1);
    this.attachTooltip(
      this.miniMapBg,
      () => `MINI-MAP · level progress (${Math.round(this.elapsedSec)}s / ${this.durationLimitSec}s)`,
    );
  }

  private readonly miniMapWidth = 120;
  /** Cap on the visible lookahead in world-px so the strip never
   *  reveals entities that are still way off-screen. One viewport
   *  width keeps it honest. */
  private readonly miniMapLookaheadPx = NATIVE_WIDTH;

  private paintMiniMap(): void {
    if (!this.miniMapMarkers || !this.miniMapBg) return;
    const g = this.miniMapMarkers;
    g.clear();
    const x0 = this.miniMapBg.x;
    const y0 = this.miniMapBg.y;
    const w = this.miniMapWidth;
    const h = 9;
    const innerW = w - 6;

    // Hero progress through the level. Time-based so main / tutorial /
    // sub-levels all map onto 0..1 cleanly even though their absolute
    // durations vary by 25×.
    const heroFrac = Phaser.Math.Clamp(this.elapsedSec / this.durationLimitSec, 0, 1);
    const heroStripX = x0 + 3 + heroFrac * innerW;

    // Trail behind the hero — a thin lit bar of "level you've traveled".
    g.fillStyle(PALETTE.hudText, 0.25);
    g.fillRect(x0 + 1, y0 + h - 2, Math.max(0, heroStripX - x0 - 1), 1);

    // World-pixels per level-fraction. Used to project each entity's
    // distance ahead of the hero onto the strip.
    const totalLevelPx = this.durationLimitSec * GAMEPLAY.baseScrollSpeed;
    const lookahead = this.miniMapLookaheadPx;
    const heroWorldX = this.worldDistTravelled + this.hero.x;

    // Terrain row (bottom). Sample upcoming world-x at the strip's
    // resolution but only out to `lookahead` — far-future terrain
    // stays hidden.
    const lookaheadFrac = lookahead / totalLevelPx;
    const stripPxForLookahead = Math.min(innerW, Math.ceil(lookaheadFrac * innerW));
    for (let sxOffset = 0; sxOffset <= stripPxForLookahead; sxOffset++) {
      const relPx = (sxOffset / innerW) * totalLevelPx;
      if (relPx > lookahead) break;
      const wx = heroWorldX + relPx;
      const sx = Math.round(heroStripX + sxOffset);
      if (sx >= x0 + w - 1) break;
      if (this.isWorldXInAbyss(wx)) {
        g.fillStyle(0x000000, 1);
        g.fillRect(sx, y0 + h - 2, 1, 1);
      }
      if (this.isWorldXInSwamp(wx)) {
        g.fillStyle(PALETTE.swampLight, 1);
        g.fillRect(sx, y0 + h - 3, 1, 1);
      }
    }

    // Entity row (top). Each visible entity's relative world distance
    // becomes a fraction-of-level offset from the hero's strip position.
    const drawEntity = (
      sprite: { x: number; active: boolean } | undefined,
      color: number,
    ): void => {
      if (!sprite || !sprite.active) return;
      const relPx = sprite.x - this.hero.x;
      if (relPx < 0 || relPx > lookahead) return;
      const mx = Math.round(heroStripX + (relPx / totalLevelPx) * innerW);
      if (mx < x0 + 1 || mx > x0 + w - 2) return;
      g.fillStyle(color, 1);
      g.fillRect(mx, y0 + 1, 2, 3);
    };
    this.gegnerGroup
      .getChildren()
      .forEach((c) => drawEntity(c as Phaser.Physics.Arcade.Sprite, PALETTE.gegner));
    this.geoGroup
      .getChildren()
      .forEach((c) => drawEntity(c as Phaser.Physics.Arcade.Sprite, PALETTE.geo));
    this.towerGroup.getChildren().forEach((c) => {
      const t = c as SidequestTowerSprite;
      const color = t.isExitGate
        ? this.puzzleLocksExit()
          ? 0xff7a7a
          : 0x9ad8ff
        : t.isLever
          ? PALETTE.leverHandle
          : PALETTE.towerAccent;
      drawEntity(t, color);
    });
    this.pickupGroup.getChildren().forEach((c) => {
      const p = c as PickupSprite;
      const color =
        p.kind === "heart"
          ? PALETTE.heart
          : p.kind === "crystal"
            ? PALETTE.crystal
            : p.kind === "feather"
              ? PALETTE.feather
              : PALETTE.boot;
      drawEntity(p, color);
    });
    this.scrollGroup
      .getChildren()
      .forEach((c) => drawEntity(c as ScrollSprite, PALETTE.scroll));

    // Hero pip — drawn last so it sits on top of any entity dot that
    // happens to share its strip pixel.
    g.fillStyle(PALETTE.heroOutline, 1);
    g.fillRect(Math.round(heroStripX) - 1, y0 + 1, 2, 7);
  }

  /**
   * Toggle the parchment-style map overlay. While open the update
   * loop is paused; Esc / Start / M all close it. Built lazily on
   * first open so the textures (parchment tile) are guaranteed ready.
   */
  private toggleMap(): void {
    if (this.mapOpen) {
      this.closeMapOverlay();
    } else {
      this.openMapOverlay();
    }
  }

  private openMapOverlay(): void {
    if (this.mapContainer) this.mapContainer.destroy();
    const c = this.add.container(0, 0).setDepth(40);
    const margin = 16;
    const w = NATIVE_WIDTH - margin * 2;
    const h = NATIVE_HEIGHT - margin * 2;
    // Tiled parchment fills the page; a darker drop shadow + ink frame
    // sits behind it so the map reads as a slightly raised sheet of
    // paper rather than a flat painted rectangle.
    const shadow = this.add
      .rectangle(margin + 2, margin + 2, w, h, PALETTE.parchmentInk, 0.45)
      .setOrigin(0, 0);
    const page = this.add
      .tileSprite(margin, margin, w, h, "parchment")
      .setOrigin(0, 0);
    const frame = this.add
      .rectangle(margin, margin, w, h, 0x000000, 0)
      .setOrigin(0, 0)
      .setStrokeStyle(2, PALETTE.parchmentInk);
    c.add([shadow, page, frame]);

    // Title.
    const cx = NATIVE_WIDTH / 2;
    const title = this.add
      .text(cx, margin + 14, t("map.title"), {
        fontFamily: "monospace",
        fontSize: "12px",
        color: this.toHex(PALETTE.parchmentInk),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    c.add(title);

    // Tree-style breadcrumb. Each row carries:
    //   (1) An indented label, prefixed with `└─` for sub-levels.
    //   (2) A full-width mini-map strip (uniform across depths) so
    //       door positions are directly comparable between levels.
    //       The strip's extent = the full expected level length
    //       (duration × baseScrollSpeed). Doors land at their true
    //       fraction of the level, not stretched to fit. Current
    //       depth shows no door — the player doesn't know where the
    //       next portal will be yet.
    const labels = [t("map.main_label")];
    for (let d = 1; d <= this.levelDepth; d++) {
      labels.push(this.packDisplayLabelForDepth(d));
    }
    const indentPx = 8;
    const rowHeight = 18;
    const stripHeight = 4;
    // All strips share the same x-range so they're visually a ruler.
    // Label area on the left, strip on the right, distance summary
    // tucked to the very right after the strip.
    const stripX = margin + 132;
    const stripEndX = NATIVE_WIDTH - margin - 36;
    const stripWidth = stripEndX - stripX;
    labels.forEach((seg, i) => {
      const isCurrent = i === this.levelDepth;
      const prefix = i === 0 ? "·" : "└─";
      const xIndent = margin + 14 + i * indentPx;
      const yLine = margin + 32 + i * rowHeight;
      const here = isCurrent ? t("map.here") : "";
      const line = this.add
        .text(xIndent, yLine, `${prefix} ${seg}${here}`, {
          fontFamily: "monospace",
          fontSize: "8px",
          color: this.toHex(PALETTE.parchmentInk),
          fontStyle: isCurrent ? "bold" : "normal",
        })
        .setOrigin(0, 0.5);
      c.add(line);

      // Full level length in px = duration × base scroll speed. For
      // ancestors, the duration is the saved durationLimitSec from
      // when they were the active level; for the current depth, it's
      // the live `this.durationLimitSec`.
      const durationSec = isCurrent
        ? this.durationLimitSec
        : (this.levelStack[i]?.durationLimitSec ?? this.durationLimitSec);
      const fullLevelPx = Math.max(durationSec * GAMEPLAY.baseScrollSpeed, 1);

      const stripY = yLine + 8;
      const bg = this.add
        .rectangle(stripX, stripY, stripWidth, stripHeight, PALETTE.parchmentShadow, 0.35)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE.parchmentInk, 0.7);
      c.add(bg);

      // Where on the strip the player is right now (or where they
      // were in this level when they last left it).
      const heroDistance = isCurrent
        ? this.levelDistance
        : (this.levelStack[i]?.levelDistance ?? 0);
      const heroFrac = Phaser.Math.Clamp(heroDistance / fullLevelPx, 0, 1);
      const heroMx = Math.round(stripX + heroFrac * stripWidth);
      // Trail behind the hero — shows the fraction of the level
      // explored. Highlight cyan for the current depth so the row
      // reads as "active".
      const trailColor = isCurrent ? UGC_CYAN : PALETTE.parchmentInk;
      const trail = this.add
        .rectangle(stripX, stripY + 1, Math.max(0, heroMx - stripX), stripHeight - 2, trailColor, 0.5)
        .setOrigin(0, 0);
      c.add(trail);

      // Door markers — every previous dive from this level. Drawn at
      // the door's true fraction of the full level length. Stem +
      // brown door + brass handle so it reads as a portal.
      const entries = this.portalEntries.filter((e) => e.depth === i);
      entries.forEach((e) => {
        const frac = Phaser.Math.Clamp(e.distance / fullLevelPx, 0, 1);
        const mx = Math.round(stripX + frac * stripWidth);
        const doorY = stripY - 5;
        const door = this.add
          .rectangle(mx, doorY, 4, 7, PALETTE.tower, 1)
          .setOrigin(0.5, 0)
          .setStrokeStyle(1, PALETTE.parchmentInk);
        const handle = this.add
          .rectangle(mx + 1, doorY + 4, 1, 1, PALETTE.leverHandle, 1)
          .setOrigin(0.5, 0);
        const stem = this.add
          .rectangle(mx, stripY, 1, stripHeight, PALETTE.parchmentInk, 1)
          .setOrigin(0.5, 0);
        c.add([stem, door, handle]);
      });

      // "You are here" pip for the current depth (brown triangle just
      // above the strip). Ancestors get a hollow open circle instead
      // so the player can see where they'd resume.
      const pip = this.add
        .text(heroMx, stripY - 1, isCurrent ? "▼" : "◇", {
          fontFamily: "monospace",
          fontSize: "7px",
          color: this.toHex(PALETTE.parchmentInk),
          fontStyle: isCurrent ? "bold" : "normal",
        })
        .setOrigin(0.5, 1);
      c.add(pip);

      // Tiny distance readout at the right of each row.
      const dist = this.add
        .text(stripEndX + 4, stripY + stripHeight / 2, `${Math.round(heroDistance)}/${Math.round(fullLevelPx)}`, {
          fontFamily: "monospace",
          fontSize: "6px",
          color: this.toHex(PALETTE.parchmentShadow),
        })
        .setOrigin(0, 0.5);
      c.add(dist);
    });

    // Compact stats footer — one line near the bottom edge.
    const footerY = NATIVE_HEIGHT - margin - 22;
    const footer = this.add
      .text(
        margin + 14,
        footerY,
        `${t("map.footer.hp")} ${this.hp}/${GAMEPLAY.heroMaxHp}   ${t("map.footer.geo")} ${this.geoCollected}   ${t("map.footer.defeated")} ${this.gegnerDefeated}   ${t("map.footer.sidequests")} ${this.sidequestsAccepted}`,
        {
          fontFamily: "monospace",
          fontSize: "8px",
          color: this.toHex(PALETTE.parchmentInk),
        },
      )
      .setOrigin(0, 0.5);
    c.add(footer);

    // Footer hint.
    const hint = this.add
      .text(cx, NATIVE_HEIGHT - margin - 8, t("map.close_hint"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.parchmentShadow),
      })
      .setOrigin(0.5, 0.5);
    c.add(hint);

    this.mapContainer = c;
    this.mapOpen = true;
    this.telemetry.emit(1, "ui.map.open", { depth: this.levelDepth });
  }

  private closeMapOverlay(): void {
    this.mapContainer?.destroy();
    this.mapContainer = undefined;
    this.mapOpen = false;
    this.telemetry.emit(1, "ui.map.close");
  }

  /**
   * Small ⏸ button to the right of the HP frame. Sits at depth 40 so
   * the pause overlay never covers it — the player can resume by
   * clicking the same spot. The two vertical bars swap to a play
   * triangle while paused so the icon doubles as a state readout.
   */
  private buildPauseButton(): void {
    const x = 84;
    const y = 10;
    const w = 14;
    const h = 14;
    const c = this.add.container(x, y).setDepth(40);
    const bg = this.add
      .rectangle(0, 0, w, h, PALETTE.hudPanel, 0.92)
      .setStrokeStyle(1, PALETTE.hudDim)
      .setOrigin(0.5);
    const bar1 = this.add
      .rectangle(-2, 0, 2, 8, PALETTE.hudText)
      .setOrigin(0.5);
    const bar2 = this.add
      .rectangle(2, 0, 2, 8, PALETTE.hudText)
      .setOrigin(0.5);
    // Right-pointing play triangle (hidden until paused). Vertices use
    // positive coordinates relative to the geometry's top-left so
    // Phaser Shape's bounding-box-based `setOrigin(0.5)` ends up
    // visually centred. With negative vertex coords the shape was
    // drawing ~3 px up-left of the button centre.
    const tri = this.add
      .triangle(0, 0, 0, 0, 0, 8, 7, 4, PALETTE.hudText)
      .setOrigin(0.5);
    tri.setVisible(false);
    c.add([bg, bar1, bar2, tri]);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", () => this.togglePause());
    void c;
    this.pauseBars = [bar1, bar2];
    this.pausePlay = tri;
    this.attachTooltip(bg, () => (this.paused ? t("hud.resume.tooltip") : t("hud.pause.tooltip")));
  }

  private togglePause(): void {
    this.paused = !this.paused;
    if (this.paused) {
      this.showPauseOverlay();
      this.telemetry.emit(1, "ui.pause.open");
    } else {
      this.hidePauseOverlay();
      this.telemetry.emit(1, "ui.pause.close");
    }
    this.paintPauseButton();
  }

  /**
   * F8 dev overlay — surfaces ShopService.debugInfo() plus a few scene
   * stats. Lazy: the text element is created the first time the player
   * presses F8 and then toggled on subsequent presses.
   */
  private toggleDebugOverlay(): void {
    if (!this.debugMode) return;
    if (!this.debugOverlay) {
      this.debugOverlay = this.add
        .text(NATIVE_WIDTH - 4, 36, "", {
          fontFamily: "monospace",
          fontSize: "7px",
          color: this.toHex(PALETTE.parchment),
          backgroundColor: this.toHex(PALETTE.hudPanel),
          padding: { x: 4, y: 2 },
        })
        .setOrigin(1, 0)
        .setDepth(50)
        .setAlpha(0.9);
    }
    this.debugOverlay.setVisible(!this.debugOverlay.visible);
    this.telemetry.emit(1, "ui.debug_overlay.toggle", { visible: this.debugOverlay.visible });
  }

  private paintDebugOverlay(): void {
    if (!this.debugOverlay || !this.debugOverlay.visible) return;
    const lines: string[] = [];
    // Shop service state (Variant B only — service may not exist).
    const shop = this.game.registry.get("shopService") as
      | { debugInfo(): { nextArrivalInMs: number; bufferSize: number; lastTemplateId: string | null } }
      | undefined;
    if (shop) {
      const info = shop.debugInfo();
      lines.push(`shop next:${Math.ceil(info.nextArrivalInMs / 100) / 10}s buf:${info.bufferSize}/20`);
      lines.push(`shop tpl: ${info.lastTemplateId ?? "—"}`);
    } else {
      lines.push("shop: (Variant A)");
    }
    // Runner stats — small selection to spot-check live state.
    lines.push(`hp ${this.hp}/${GAMEPLAY.heroMaxHp}  geo ${this.geoCollected}`);
    lines.push(`depth ${this.levelDepth}  elapsed ${Math.floor(this.elapsedSec)}s`);
    lines.push(`feather ×${this.escapeCharges}${this.escapeFeatherUnlocked ? "" : " (locked)"}`);
    this.debugOverlay.setText(lines.join("\n"));
  }

  private paintPauseButton(): void {
    this.pauseBars.forEach((b) => b.setVisible(!this.paused));
    this.pausePlay?.setVisible(this.paused);
  }

  private showPauseOverlay(): void {
    if (this.pauseOverlay) return;
    const c = this.add.container(0, 0).setDepth(35);
    const dim = this.add
      .rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0);
    c.add(dim);
    this.pauseOverlay = c;
    this.pauseFocusIdx = 0;
    this.prevPauseMoveY = 0;

    // Variant B gets a two-column panel — level/creator info on the left,
    // action buttons on the right. The non-UGC variant keeps the simple
    // single-button (PROFILE) stack.
    if (this.isUgcVariant) this.buildUgcPausePanel(c);
    else this.buildBasicPausePanel(c);

    this.refreshPauseFocus();
  }

  /** Variant B pause panel: a left info column (level name + the creator's
   *  enhanced profile card — bio, tags, stats) and a right action column
   *  (author / market / gallery / profile). */
  private buildUgcPausePanel(c: Phaser.GameObjects.Container): void {
    const cx = NATIVE_WIDTH / 2;

    const title = this.add
      .text(cx, 30, t("hud.paused"), {
        fontFamily: "monospace",
        fontSize: "18px",
        color: this.toHex(PALETTE.hudText),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    const divider = this.add
      .rectangle(cx, 112, 1, 136, PALETTE.hudDim, 0.5)
      .setOrigin(0.5, 0.5);
    const hint = this.add
      .text(cx, 200, t("hud.pause.hint"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    c.add([title, divider, hint]);

    // --- Column 1: which level am I in, and who authored it? ---
    const leftX = 40;
    const levelCaption = this.add
      .text(leftX, 48, t("pause.col.level"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    const levelName = this.add
      .text(leftX, 62, this.currentLevelDisplayName(), {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(PALETTE.hudText),
        fontStyle: "bold",
        wordWrap: { width: 145 },
        lineSpacing: 1,
      })
      .setOrigin(0, 0.5);
    const creatorCaption = this.add
      .text(leftX, 80, t("pause.col.creator"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    c.add([levelCaption, levelName, creatorCaption]);

    // The creator's enhanced profile card (avatar, @handle, bio, stats, tags,
    // member-since), re-parented into the overlay so it's destroyed with it.
    // String formatting mirrors ProfileTooltipService so the card body reads
    // identically to the hover/click tooltip.
    const svc = this.authorIdentityService;
    if (svc) {
      const profile = svc.profileForSeed(this.currentLevelAuthorSeed);
      const card = new ProfileTooltipCard(this, svc);
      card.setProfile(profile, {
        stats: t("profile.stats", {
          levels: profile.levelsAuthored,
          trades: profile.tradesCompleted,
        }),
        memberSince: t("profile.member_since", {
          month: profile.memberSinceMonth.toString().padStart(2, "0"),
          year: profile.memberSinceYear.toString(),
        }),
      });
      card.setPosition(leftX, 88);
      card.setVisible(true);
      c.add(card.container);
    }

    // --- Column 2: actions ---
    const defs: { label: StringKey; action: () => void }[] = [
      { label: "pause.button.author", action: () => this.openAuthorFromPause() },
      { label: "pause.button.market", action: () => this.openMarketFromPause() },
      { label: "pause.button.gallery", action: () => this.openGalleryFromPause() },
      { label: "pause.button.profile", action: () => this.openProfileFromPause() },
    ];
    const cx2 = 274;
    const step = 24;
    const firstY = 78;
    this.pauseButtons = defs.map((d, i) => ({
      btn: new PauseMenuButton(this, cx2, firstY + i * step, t(d.label), d.action),
      action: d.action,
    }));
  }

  /** Non-UGC pause panel: PAUSED title, a single centred PROFILE button,
   *  and the resume hint below. */
  private buildBasicPausePanel(c: Phaser.GameObjects.Container): void {
    const cx = NATIVE_WIDTH / 2;
    const cy = NATIVE_HEIGHT / 2;

    const title = this.add
      .text(cx, cy - 26, t("hud.paused"), {
        fontFamily: "monospace",
        fontSize: "18px",
        color: this.toHex(PALETTE.hudText),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    const hint = this.add
      .text(cx, cy + 22, t("hud.pause.hint"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    c.add([title, hint]);

    const action = (): void => this.openProfileFromPause();
    this.pauseButtons = [
      { btn: new PauseMenuButton(this, cx, cy, t("pause.button.profile"), action), action },
    ];
  }

  /** Display name for the level the player is currently in — mirrors the
   *  banner naming used by enterSubLevel / the main-level intro. */
  private currentLevelDisplayName(): string {
    if (this.levelDepth === 0) return t("banner.main_level");
    if (this.levelDepth === 1) {
      return t("banner.sidequest.biome", { biome: this.packDisplayLabelForDepth(this.levelDepth) });
    }
    return t("banner.sublevel.depth", { depth: this.levelDepth });
  }

  private hidePauseOverlay(): void {
    this.pauseOverlay?.destroy();
    this.pauseOverlay = undefined;
    for (const b of this.pauseButtons) b.btn.destroy();
    this.pauseButtons = [];
  }

  private refreshPauseFocus(): void {
    this.pauseButtons.forEach((b, i) => b.btn.setFocused(i === this.pauseFocusIdx));
  }

  /** Pause-menu input handler — runs while `this.paused === true`. Moves
   *  focus through the button stack and activates the focused button. */
  private handlePauseMenuInput(state: { moveY: number; interactPressed: boolean; attackPressed: boolean; jumpPressed: boolean }): void {
    const n = this.pauseButtons.length;
    if (n === 0) return;
    const dy = state.moveY > 0.5 ? 1 : state.moveY < -0.5 ? -1 : 0;
    if (dy !== this.prevPauseMoveY) {
      this.prevPauseMoveY = dy;
      if (dy !== 0) {
        this.pauseFocusIdx = (this.pauseFocusIdx + dy + n) % n;
        this.refreshPauseFocus();
      }
    }
    if (state.interactPressed || state.attackPressed || state.jumpPressed) {
      this.pauseButtons[this.pauseFocusIdx]?.action();
    }
  }

  private openAuthorFromPause(): void {
    if (!this.isUgcVariant) return;
    this.telemetry.emit(1, "ui.pause.author_button.click");
    this.hidePauseOverlay();
    this.scene.pause();
    this.scene.launch("SidequestAuthorScene", { returnTo: "runner", walletSource: "runner" });
  }

  private openMarketFromPause(): void {
    if (!this.isUgcVariant) return;
    this.telemetry.emit(1, "ui.pause.market_button.click");
    this.hidePauseOverlay();
    this.scene.pause();
    this.scene.launch("ShopScene", { returnTo: "runner", walletSource: "runner" });
  }

  private openProfileFromPause(): void {
    this.telemetry.emit(1, "ui.pause.profile_button.click");
    this.hidePauseOverlay();
    this.scene.pause();
    this.scene.launch("ProfileScene", { returnTo: "runner" });
  }

  private openGalleryFromPause(): void {
    if (!this.isUgcVariant) return;
    this.telemetry.emit(1, "ui.pause.gallery_button.click");
    this.hidePauseOverlay();
    this.scene.pause();
    this.scene.launch("GalleryScene", { returnTo: "runner", walletSource: "runner" });
  }

  private buildTutorialTag(): void {
    const cx = NATIVE_WIDTH / 2;
    const c = this.add.container(cx, 14);
    const bg = this.add
      .rectangle(0, 0, 80, 16, PALETTE.hudPanel)
      .setStrokeStyle(2, PALETTE.heroOutline);
    const text = this.add
      .text(0, 0, t("hud.tutorial"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0.5, 0.5);
    c.add(bg);
    c.add(text);
    this.tutorialTag = c;
    this.tutorialTagBg = bg;
    this.tutorialTagText = text;
    this.refreshTutorialBreadcrumb();
  }

  /**
   * Rebuild the tutorial tag text from the current depth + pack stack.
   * Depth 0 → "TUTORIAL". Depth N → "TUTORIAL > P1 > P2 > … > PN"
   * where Pi is the pack label for that depth (Pixel, Snowy, etc.).
   * Resizes the surrounding panel so longer breadcrumbs still fit.
   */
  private refreshTutorialBreadcrumb(): void {
    if (!this.tutorialTagText || !this.tutorialTagBg) return;
    if (!this.inTutorialContext) {
      this.tutorialTag?.setVisible(false);
      return;
    }
    const segments = [t("hud.tutorial")];
    for (let d = 1; d <= this.levelDepth; d++) {
      segments.push(this.packDisplayLabelForDepth(d).toUpperCase());
    }
    const label = segments.join(" > ");
    this.tutorialTagText.setText(label);
    const padding = 10;
    const w = Math.max(80, Math.ceil(this.tutorialTagText.width) + padding * 2);
    this.tutorialTagBg.setSize(w, 16);
    this.tutorialTag?.setVisible(true);
  }

  // --- backstep -------------------------------------------------------

  private updateBackstep(moveX: number, dt: number): void {
    const wantsBack = moveX < -0.5;
    const targetX = wantsBack
      ? GAMEPLAY.heroX - this.backstepBudgetPx
      : GAMEPLAY.heroX;
    const speed = wantsBack ? this.backstepGoSpeed : this.backstepReturnSpeed;
    const step = speed * dt;
    if (this.hero.x > targetX + 0.1) {
      this.hero.x = Math.max(targetX, this.hero.x - step);
    } else if (this.hero.x < targetX - 0.1) {
      this.hero.x = Math.min(targetX, this.hero.x + step);
    }
    if (wantsBack && !this.prevBackstepHeld) {
      this.telemetry.emit(1, "input.backstep.begin");
    } else if (!wantsBack && this.prevBackstepHeld) {
      this.telemetry.emit(1, "input.backstep.end");
    }
    this.prevBackstepHeld = wantsBack;
  }

  // --- tutorial signage ----------------------------------------------

  private maybeSpawnSign(): void {
    if (this.nextSignIdx >= this.tutorialSigns.length) return;
    const next = this.tutorialSigns[this.nextSignIdx]!;
    if (this.elapsedSec < next.atSec) return;
    this.spawnSignWithKeycaps(t(next.titleKey), next.keys, next.combo === true, next.height);
    if (next.arrow) this.spawnWallArrow(next.arrow, next.arrowY ?? 60);
    this.nextSignIdx += 1;
  }

  private spawnWallArrow(
    dir: "up" | "double-up" | "right" | "left",
    heightAboveGround: number,
  ): void {
    // Paint a large stylized chevron in the parallax wall band so the
    // affordance reads as graffiti on a building. Travels with the world
    // (1.0× scroll, like the sign) so the cue stays anchored to the
    // location of the affordance. Subtle alpha pulse for breath.
    const x = NATIVE_WIDTH + 36;
    const y = this.groundTopY - heightAboveGround;
    const c = this.add.container(x, y).setDepth(1);

    const targets: Phaser.GameObjects.Image[] = [];
    if (dir === "double-up") {
      // Two stacked up-arrows side by side — reads as "jump twice".
      const left = this.add.image(-14, 0, "wallArrow.up").setOrigin(0.5, 0.5).setAlpha(0.35);
      const right = this.add.image(14, 0, "wallArrow.up").setOrigin(0.5, 0.5).setAlpha(0.35);
      c.add([left, right]);
      targets.push(left, right);
    } else {
      const key =
        dir === "up"
          ? "wallArrow.up"
          : dir === "right"
            ? "wallArrow.right"
            : "wallArrow.left";
      const a = this.add.image(0, 0, key).setOrigin(0.5, 0.5).setAlpha(0.35);
      c.add(a);
      targets.push(a);
    }
    this.signs.push(c);
    this.tweens.add({
      targets,
      alpha: 0.55,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
    this.telemetry.emit(1, "tutorial.wall_arrow.shown", { dir });
  }

  private spawnSignWithKeycaps(
    title: string,
    keys: ReadonlyArray<string>,
    combo: boolean,
    height: number,
  ): void {
    const x = NATIVE_WIDTH + 24;
    // Anchor 5 px below the ground surface so the stake reads as
    // driven INTO the soil instead of balancing on top of it (the
    // post itself extends from y=−18 to y=0 inside the container; with
    // baseY shifted down, the bottom 5 px of the post are buried).
    const baseY = this.groundTopY - height + 5;
    const c = this.add.container(x, baseY).setDepth(2);

    // Compact layout — measured upward from the post base (origin = 0,0):
    //   y = -34 .. -28  → keycap row
    //   y = -22 .. -8   → wooden board with title text (14 px)
    //   y = -18 ..   0  → wooden post stake (18 px — 5 of which are
    //                     below ground surface for the "anchored" read)

    const boardWidth = Math.max(48, title.length * 6 + 10);
    const post = this.add.rectangle(0, 0, 4, 18, PALETTE.tower).setOrigin(0.5, 1);
    const board = this.add
      .rectangle(0, -8, boardWidth, 14, PALETTE.scroll)
      .setStrokeStyle(1, PALETTE.ink)
      .setOrigin(0.5, 1);
    const titleText = this.add
      .text(0, -15, title, {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.ink),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);

    c.add([post, board, titleText]);

    // Keycap row sits just above the board.
    this.buildKeycapRow(c, 0, -31, keys, combo);

    this.signs.push(c);
    this.telemetry.emit(1, "tutorial.sign.shown", { title, keys: keys.join("+") });
  }

  /**
   * Pixel-art "elevated keycap" row. Each key is a parchment-toned rect with
   * an ink stroke + a 1-px dark band along the bottom edge to fake the
   * elevation. The row is horizontally centred on (cx, cy) inside `parent`.
   *
   * `combo === true` renders a small "+" between keycaps (DOUBLE-JUMP
   * convention: tap key A then key A); `false` renders a "/" (alternative
   * keys for the same affordance: SPACE or A both jump).
   */
  private buildKeycapRow(
    parent: Phaser.GameObjects.Container,
    cx: number,
    cy: number,
    labels: ReadonlyArray<string>,
    combo: boolean,
  ): void {
    const widths = labels.map((l) => Math.max(14, l.length * 5 + 6));
    const sepText = combo ? "+" : "/";
    const sepWidth = 6;
    const totalWidth =
      widths.reduce((a, b) => a + b, 0) + Math.max(0, labels.length - 1) * sepWidth;
    let cursor = cx - totalWidth / 2;
    labels.forEach((label, i) => {
      const w = widths[i]!;
      this.addKeycap(parent, cursor + w / 2, cy, label, w);
      cursor += w;
      if (i < labels.length - 1) {
        const sep = this.add
          .text(cursor + sepWidth / 2, cy, sepText, {
            fontFamily: "monospace",
            fontSize: "8px",
            color: this.toHex(PALETTE.hudDim),
            fontStyle: "bold",
          })
          .setOrigin(0.5, 0.5);
        parent.add(sep);
        cursor += sepWidth;
      }
    });
  }

  private addKeycap(
    parent: Phaser.GameObjects.Container,
    cx: number,
    cy: number,
    label: string,
    width: number,
  ): void {
    const h = 12;
    const body = this.add
      .rectangle(cx, cy, width, h, PALETTE.scroll)
      .setStrokeStyle(1, PALETTE.ink)
      .setOrigin(0.5, 0.5);
    // Bottom-edge shadow band for the "elevated" feel.
    const shadow = this.add
      .rectangle(cx, cy + h / 2 - 1, width - 2, 2, PALETTE.ink, 0.55)
      .setOrigin(0.5, 0.5);
    // Top-edge highlight pixel row for the same.
    const highlight = this.add
      .rectangle(cx, cy - h / 2 + 1, width - 2, 1, PALETTE.heroOutline, 0.7)
      .setOrigin(0.5, 0.5);
    const text = this.add
      .text(cx, cy - 1, label, {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.ink),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    parent.add([body, shadow, highlight, text]);
  }

  private translateSigns(scrollPx: number): void {
    if (this.signs.length === 0) return;
    const live: Phaser.GameObjects.Container[] = [];
    for (const s of this.signs) {
      if (!s.active) continue;
      s.x -= scrollPx;
      if (s.x < -64) {
        s.destroy();
        continue;
      }
      live.push(s);
    }
    this.signs = live;
  }

  // --- spawners -------------------------------------------------------

  private heroOnGround(): boolean {
    return this.hero.body !== null && (this.hero.body as Phaser.Physics.Arcade.Body).blocked.down;
  }

  private spawnGegner(forceX?: number): void {
    const x = forceX ?? NATIVE_WIDTH + 16;
    const useBat = this.textures.exists("enemy.bat.idle");
    const textureKey = useBat ? "enemy.bat.idle" : "gegner";
    // Bat sheet is 64×64; placeholder Gegner is 14×14. Origin + Y offset
    // compensate so both options sit roughly at the same shoulder line.
    const y = this.groundTopY - (useBat ? 50 : 14);
    const g = this.gegnerGroup.create(x, y, textureKey) as Phaser.Physics.Arcade.Sprite;
    g.setOrigin(0, 0);
    if (useBat) {
      // Runtime scaling (display transform, not asset modification — see
      // public/assets/CREDITS.md). 0.6 × 64 = ~38 px, between the
      // placeholder 14 px and the Visual Brief §4 target 48 px.
      g.setScale(0.6);
      const body = g.body as Phaser.Physics.Arcade.Body | null;
      body?.setSize(28, 28, true);
      // Play the idle-fly loop. The bat flies in place visually; the world
      // scrolls past it via `translateGroup`, so it reads as a hovering
      // ambush rather than a charging Gegner — fine for v0.3.
      if (this.anims.exists("bat.idle")) g.play("bat.idle");
    }
    this.gegnerSeen += 1;
    this.telemetry.emit(2, "gegner.spawn", { sprite: useBat ? "bat" : "placeholder" });
  }

  /**
   * Spawn a flying bird-Gegner. Same physics group as ground Gegner
   * (so the attack swing destroys it like everything else), tagged
   * `isBird` so `updateBirds` overrides its y each frame with a sine
   * wave instead of leaving it pinned at spawn height. Drops dive
   * bombs on a per-bird cooldown (`birdDiveBombIntervalSec`). Spawn
   * height tuned just above hero reach so a single-jump can't tag
   * the bird — the player has to land a double-jump for the kill or
   * attack mid-air.
   */
  private spawnBird(forceX?: number, forceY?: number): void {
    const x = forceX ?? NATIVE_WIDTH + 16;
    const useBat = this.textures.exists("enemy.bat.idle");
    const textureKey = useBat ? "enemy.bat.idle" : "gegner";
    const baseY = forceY ?? this.groundTopY - GAMEPLAY.birdBaseHeightPx;
    const b = this.gegnerGroup.create(x, baseY, textureKey) as BirdSprite;
    b.setOrigin(0, 0);
    if (useBat) {
      b.setScale(0.6);
      const body = b.body as Phaser.Physics.Arcade.Body | null;
      body?.setSize(28, 28, true);
      if (this.anims.exists("bat.idle")) b.play("bat.idle");
    } else {
      // Placeholder tint so birds read as a distinct enemy variant.
      b.setTint(PALETTE.bird);
    }
    b.isBird = true;
    b.birdBaseY = baseY;
    b.birdAmplitude = GAMEPLAY.birdAmplitudePx;
    b.birdFrequencyHz = GAMEPLAY.birdFrequencyHz;
    b.birdPhase = Math.random() * Math.PI * 2;
    b.lastDiveBombMs = this.timeMs();
    this.gegnerSeen += 1;
    this.telemetry.emit(2, "bird.spawn");
  }

  /**
   * Per-frame sine-wave motion for birds + dive-bomb cadence. Walks
   * the gegnerGroup once and reads `isBird` off each child; ground
   * Gegner are left alone (their y stays at spawn). Dive bombs spawn
   * a falling projectile at the bird's current position.
   */
  private updateBirds(nowMs: number, _dtMs: number): void {
    const children = this.gegnerGroup.getChildren();
    for (const child of children) {
      const b = child as BirdSprite | undefined;
      if (!b || !b.active || !b.isBird) continue;
      const baseY = b.birdBaseY ?? this.groundTopY - GAMEPLAY.birdBaseHeightPx;
      const amp = b.birdAmplitude ?? GAMEPLAY.birdAmplitudePx;
      const freq = b.birdFrequencyHz ?? GAMEPLAY.birdFrequencyHz;
      const phase = b.birdPhase ?? 0;
      const tSec = nowMs / 1000;
      b.y = baseY + Math.sin(tSec * 2 * Math.PI * freq + phase) * amp;
      const last = b.lastDiveBombMs ?? 0;
      const cooldownMs = GAMEPLAY.birdDiveBombIntervalSec * 1000;
      // Only dive-bomb while the bird is on-screen — off-screen drops
      // would be wasted projectiles the player never sees.
      if (b.x > -8 && b.x < NATIVE_WIDTH && nowMs - last >= cooldownMs) {
        b.lastDiveBombMs = nowMs;
        this.spawnBirdProjectile(b.x + 8, b.y + 12);
      }
    }
  }

  /**
   * Drop a small dark projectile from a bird. Plain rectangle (no
   * physics body) — `updateBirdProjectiles` runs the simple
   * fall-and-cull logic and a manual hero-overlap check on each
   * frame. One source of truth for the falling motion means we don't
   * have to fight Phaser's gravity for our world-scroll-aware path.
   */
  private spawnBirdProjectile(x: number, y: number): void {
    const proj = this.add
      .rectangle(x, y, 4, 6, PALETTE.bird, 1.0)
      .setOrigin(0.5, 0)
      .setDepth(5);
    proj.setStrokeStyle(1, PALETTE.birdHilite);
    this.birdProjectiles.push(proj);
    this.telemetry.emit(2, "bird.dive_bomb.spawn");
  }

  private updateBirdProjectiles(scrollPx: number, dt: number): void {
    if (this.birdProjectiles.length === 0) return;
    const live: Phaser.GameObjects.Rectangle[] = [];
    const heroBounds = this.hero.body
      ? (this.hero.body as Phaser.Physics.Arcade.Body)
      : null;
    for (const p of this.birdProjectiles) {
      if (!p.active) continue;
      p.x -= scrollPx;
      p.y += GAMEPLAY.birdProjectileFallSpeed * dt;
      // Cull below ground or off the left side.
      if (p.y > NATIVE_HEIGHT - 18 || p.x < -8) {
        p.destroy();
        continue;
      }
      // Manual overlap with the hero — same i-frames as Gegner contact.
      if (heroBounds) {
        const inside =
          p.x >= heroBounds.x &&
          p.x <= heroBounds.x + heroBounds.width &&
          p.y + 6 >= heroBounds.y &&
          p.y <= heroBounds.y + heroBounds.height;
        if (inside) {
          this.onBirdProjectileHit(p);
          continue;
        }
      }
      live.push(p);
    }
    this.birdProjectiles = live;
  }

  private onBirdProjectileHit(p: Phaser.GameObjects.Rectangle): void {
    p.destroy();
    const nowMs = this.timeMs();
    if (nowMs < this.invulnUntilMs) return;
    const drainedIndex = this.hp - 1;
    this.hp = Math.max(0, this.hp - 1);
    this.invulnUntilMs = nowMs + GAMEPLAY.hitInvulnMs;
    this.pulsateHurtScreen();
    this.pulsateHpBar(drainedIndex);
    this.audio.hpLoss();
    this.shakeCamera(2, 80);
    this.telemetry.emit(2, "hero.hit", { hp: this.hp, source: "bird_projectile" });
    if (this.hp <= 0) {
      this.telemetry.emit(2, "hero.down", { cause: "bird_projectile" });
      this.endSession("hp_down");
    }
  }

  /**
   * Spawn a rock suspended near the tree-line just off the right edge. It
   * drifts in (`updateRocks`), starts shaking once it reaches a lead
   * position ahead of the hero, then — after the *constant*
   * `rockShakeDurationMs` — locks its x and drops straight down onto the
   * hero's column. The constant delay makes the dodge timing learnable;
   * the player escapes with the backstep (hold ← / A).
   */
  private spawnRock(): void {
    const x0 = NATIVE_WIDTH + 12;
    const y = GAMEPLAY.rockHangY;
    const obj = this.add.image(x0, y, "rock").setDepth(6);
    const shadow = this.add
      .ellipse(x0, this.groundTopY, 16, 4, PALETTE.rockShadow, 0.22)
      .setOrigin(0.5, 0.5)
      .setDepth(2);
    this.fallingRocks.push({
      obj,
      shadow,
      state: "drifting",
      shakeStartMs: 0,
      baseX: x0,
    });
    this.telemetry.emit(2, "obstacle.rock.spawn");
  }

  /**
   * Drift / shake / fall state machine for every live rock, plus a manual
   * hero-overlap check during the fall (same i-frames as Gegner contact).
   */
  private updateRocks(nowMs: number, scrollPx: number, dt: number): void {
    if (this.fallingRocks.length === 0) return;
    // Lead so a rock that starts shaking here drifts to ~heroX exactly as
    // the constant shake delay elapses, dropping on the hero's column.
    const lead = (GAMEPLAY.baseScrollSpeed * GAMEPLAY.rockShakeDurationMs) / 1000;
    const shakeTriggerX = GAMEPLAY.heroX + lead;
    const heroBody = this.hero.body ? (this.hero.body as Phaser.Physics.Arcade.Body) : null;
    const halfW = this.textures.get("rock").getSourceImage().width / 2;
    const halfH = this.textures.get("rock").getSourceImage().height / 2;
    const live: FallingRock[] = [];
    for (const r of this.fallingRocks) {
      if (!r.obj.active) continue;
      if (r.state === "drifting") {
        r.baseX -= scrollPx;
        r.obj.x = r.baseX;
        r.shadow.x = r.baseX;
        if (r.baseX <= shakeTriggerX) {
          r.state = "shaking";
          r.shakeStartMs = nowMs;
        }
      } else if (r.state === "shaking") {
        r.baseX -= scrollPx;
        const jitter = Phaser.Math.Between(-GAMEPLAY.rockShakeAmplitudePx, GAMEPLAY.rockShakeAmplitudePx);
        r.obj.x = r.baseX + jitter;
        r.shadow.x = r.baseX;
        // Telegraph: the impact shadow darkens as the drop approaches.
        const t = Phaser.Math.Clamp((nowMs - r.shakeStartMs) / GAMEPLAY.rockShakeDurationMs, 0, 1);
        r.shadow.setAlpha(0.22 + 0.4 * t);
        if (nowMs - r.shakeStartMs >= GAMEPLAY.rockShakeDurationMs) {
          r.state = "falling";
          r.obj.x = r.baseX; // lock x — drop is vertical from here
          r.shadow.x = r.baseX;
          this.telemetry.emit(2, "obstacle.rock.fall", { x: Math.round(r.baseX) });
        }
      } else {
        // falling
        r.obj.y += GAMEPLAY.rockFallSpeed * dt;
        // Hero overlap (AABB) — only meaningful mid-air before impact.
        if (heroBody && nowMs >= this.invulnUntilMs) {
          const hit =
            r.obj.x + halfW >= heroBody.x &&
            r.obj.x - halfW <= heroBody.x + heroBody.width &&
            r.obj.y + halfH >= heroBody.y &&
            r.obj.y - halfH <= heroBody.y + heroBody.height;
          if (hit) {
            this.onRockHit(r);
            continue;
          }
        }
        // Ground impact → shatter + clear (a clean dodge).
        if (r.obj.y + halfH >= this.groundTopY) {
          this.telemetry.emit(2, "obstacle.rock.dodged", { x: Math.round(r.obj.x) });
          this.shakeCamera(1, 60);
          r.obj.destroy();
          r.shadow.destroy();
          continue;
        }
      }
      if (r.obj.x < -20) {
        r.obj.destroy();
        r.shadow.destroy();
        continue;
      }
      live.push(r);
    }
    this.fallingRocks = live;
  }

  private onRockHit(r: FallingRock): void {
    r.obj.destroy();
    r.shadow.destroy();
    const nowMs = this.timeMs();
    if (nowMs < this.invulnUntilMs) return;
    const drainedIndex = this.hp - 1;
    this.hp = Math.max(0, this.hp - 1);
    this.invulnUntilMs = nowMs + GAMEPLAY.hitInvulnMs;
    this.pulsateHurtScreen();
    this.pulsateHpBar(drainedIndex);
    this.audio.hpLoss();
    this.shakeCamera(2, 80);
    this.telemetry.emit(2, "hero.hit", { hp: this.hp, source: "rock" });
    if (this.hp <= 0) {
      this.telemetry.emit(2, "hero.down", { cause: "rock" });
      this.endSession("hp_down");
    }
  }

  private clearRocks(): void {
    for (const r of this.fallingRocks) {
      r.obj.destroy();
      r.shadow.destroy();
    }
    this.fallingRocks = [];
  }

  private spawnGeo(): void {
    const x = this.findClearSpawnX();
    const height = Phaser.Math.RND.pick([0, 16, 32]);
    const y = this.groundTopY - 12 - height;
    const g = this.geoGroup.create(x, y, "geo") as Phaser.Physics.Arcade.Sprite;
    g.setOrigin(0, 0);
    this.applyPickupBob(g);
  }

  private spawnTower(forceX?: number): void {
    // Tower asset is now a cropped tree silhouette from the forest
    // pack (32×112 source). Anchor bottom-center on the ground line
    // and scale to a play-friendly display size (16×56). When the
    // file is missing, the procedural fallback "tower" texture
    // (12×32) renders at the same anchor — slightly squat but
    // functional.
    const x = forceX ?? NATIVE_WIDTH + 16;
    // Entry portals use the "tower.down" texture so the inner shading
    // reads as a stairwell descending into a deeper sub-level.
    const t = this.towerGroup.create(x, this.groundTopY, "tower.down") as SidequestTowerSprite;
    t.setOrigin(0.5, 1);
    t.setScale(0.5);
    t.sidequestId = `sq-${this.chunkIndex}-${Math.floor(Math.random() * 100)}`;
    t.accepted = false;
    this.telemetry.emit(2, "sidequest.tower.spawn", { id: t.sidequestId });
  }

  private spawnScroll(): void {
    const x = this.findClearSpawnX();
    const y = this.groundTopY - 22;
    const s = this.scrollGroup.create(x, y, "scroll") as ScrollSprite;
    s.setOrigin(0, 0);
    s.kind = "scroll";
    this.applyPickupBob(s);
  }

  /**
   * Spawn a one-way floating platform off the right edge. The hero
   * can jump up onto it (collision passes through from below) and
   * drop off via the down arrow (the collider's process callback
   * temporarily ignores contacts during `dropThroughUntilMs`).
   * Platforms are recycled (destroyed when off-screen left) rather
   * than tiled / pooled — count is low enough that the overhead is
   * insignificant.
   */
  private spawnPlatform(): void {
    const x = NATIVE_WIDTH + 16;
    // Two elevation tiers — close-tier (~28 px above ground) and high-tier
    // (~56 px above ground). High-tier requires double-jump.
    const elevation = Phaser.Math.RND.pick([28, 56]);
    const width = Phaser.Math.RND.pick([40, 56, 72]);
    const y = this.groundTopY - elevation;
    const plat = this.platformGroup.create(x, y, "ground") as PlatformSprite;
    plat.setOrigin(0, 0);
    plat.setDisplaySize(width, 4);
    const body = plat.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(width, 4);
    body.updateFromGameObject();
    plat.basePosX = x;
    plat.basePosY = y;
    plat.oscPhase = Math.random() * Math.PI * 2;
    plat.oscPeriodSec = GAMEPLAY.movingPlatformPeriodSec;
    // Roll for a moving variant. Bias slightly toward horizontal because
    // vertical platforms eat collision frames as they pass through the
    // hero — keeps the gameplay forgiving.
    if (Math.random() < GAMEPLAY.movingPlatformChance) {
      const axis: "x" | "y" = Math.random() < 0.6 ? "x" : "y";
      plat.oscillateAxis = axis;
      plat.oscAmplitude =
        axis === "x"
          ? GAMEPLAY.movingPlatformAmplitudeX
          : GAMEPLAY.movingPlatformAmplitudeY;
    }
    this.telemetry.emit(2, "platform.spawn", {
      elevation,
      width,
      moving: plat.oscillateAxis ?? "static",
    });
  }

  /**
   * Scroll every platform left with the world; oscillate moving
   * variants around their scroll-anchored base position; destroy
   * off-screen. Static platforms have undefined `oscillateAxis`
   * which short-circuits the oscillation maths to a no-op.
   */
  private scrollPlatforms(scrollPx: number, _dt: number): void {
    const tSec = this.timeMs() / 1000;
    this.platformGroup.getChildren().forEach((obj) => {
      const p = obj as PlatformSprite;
      if (!p.active) return;
      const baseX = (p.basePosX ?? p.x) - scrollPx;
      p.basePosX = baseX;
      const baseY = p.basePosY ?? p.y;
      if (p.oscillateAxis && p.oscAmplitude && p.oscPeriodSec) {
        const wave = Math.sin(
          (tSec * 2 * Math.PI) / p.oscPeriodSec + (p.oscPhase ?? 0),
        );
        if (p.oscillateAxis === "x") {
          p.x = baseX + p.oscAmplitude * wave;
          p.y = baseY;
        } else {
          p.x = baseX;
          p.y = baseY + p.oscAmplitude * wave;
        }
      } else {
        p.x = baseX;
        p.y = baseY;
      }
      if (p.x + p.displayWidth < -16) {
        p.destroy();
        return;
      }
      const body = p.body as Phaser.Physics.Arcade.StaticBody | null;
      body?.updateFromGameObject();
    });
  }

  // --- canopy detour --------------------------------------------------

  /**
   * Cadence gate for the canopy cluster spawn. Restricted to the
   * depth-0 Mysterious Forest pack — the vine texture and canopy
   * altitude are tuned to that pack's green tree-crown line, so they'd
   * look out of place in the Forrest / Snowy sub-level parallax swaps.
   *
   * Two spawn schedules:
   *   - Tutorial: a single one-shot cluster at
   *     `canopyClusterTutorialAtSec` so the teaching run includes the
   *     climb mechanic. The `tutorialCanopySpawned` flag enforces the
   *     "exactly once" guarantee inside the 60-s tutorial budget.
   *   - Main loop: time-based cadence every
   *     `canopyClusterIntervalSecMain` seconds, rare by design.
   *
   * Stacked clusters are prevented by waiting for the previous
   * cluster's vines to fully scroll off-screen left.
   */
  private shouldSpawnCanopyCluster(): boolean {
    if (this.levelDepth !== 0) return false;
    if (this.vineGroup.countActive(true) > 0) return false;
    if (this.isTutorial) {
      if (this.tutorialCanopySpawned) return false;
      return this.elapsedSec >= GAMEPLAY.canopyClusterTutorialAtSec;
    }
    return this.canopyTimerSec >= GAMEPLAY.canopyClusterIntervalSecMain;
  }

  /**
   * Spawn a full canopy cluster off the right edge of the viewport:
   *   - Ascent vine at the left end (grab to climb up).
   *   - Wide invisible canopy platform spanning the cluster width.
   *   - 4–6 pickup goodies (geo, hearts, crystals, feathers, scrolls)
   *     spaced along the canopy length so reaching the top is worth
   *     it.
   *   - One bird-Gegner cruising at canopy height to balance the
   *     reward with a risk.
   *   - Descent vine at the right end (graceful slide back down).
   *
   * All members share a `clusterId` so future checks can correlate
   * them. The world scrolls them left in lock-step, so by the time
   * the ascent vine reaches the hero, the canopy + descent vine are
   * already in view.
   */
  private spawnCanopyCluster(): void {
    const clusterId = this.nextClusterId++;
    const startX = NATIVE_WIDTH + 16;
    const width = GAMEPLAY.canopyClusterWidthPx;
    const canopyY = GAMEPLAY.canopyTopY;

    this.spawnVine(startX, true, clusterId);
    this.spawnVine(startX + width, false, clusterId);
    this.spawnCanopyPlatform(startX, width, canopyY, clusterId);
    this.spawnCanopyPickups(startX, width, canopyY);
    this.spawnCanopyBird(startX + Math.floor(width / 2), canopyY);

    this.canopyTimerSec = 0;
    if (this.isTutorial) this.tutorialCanopySpawned = true;
    this.telemetry.emit(2, "canopy.cluster.spawn", {
      clusterId,
      depth: this.levelDepth,
      width,
      tutorial: this.isTutorial,
    });
  }

  /**
   * Spawn a single liana sprite. Anchored origin (0.5, 1) at the
   * ground line so the vine's foot sits flush with the floor and its
   * canopy-leaf top reaches up to (groundTopY - canopyVineHeightPx).
   * `isAscent` distinguishes the climb-up vine (cluster's left end)
   * from the climb-down vine (cluster's right end); the grab check
   * uses the flag to validate the hero's current altitude.
   */
  private spawnVine(x: number, isAscent: boolean, clusterId: number): void {
    const vine = this.vineGroup.create(x, this.groundTopY, "vine") as VineSprite;
    vine.setOrigin(0.5, 1);
    vine.setDepth(2);
    vine.isAscent = isAscent;
    vine.clusterId = clusterId;
    // Disable the physics body — vines are decorative + grab triggers;
    // grab is a manual proximity check in `tryGrabVine`.
    const body = vine.body as Phaser.Physics.Arcade.Body | null;
    body?.setEnable(false);
  }

  /**
   * Spawn the invisible canopy platform. Reuses the existing
   * `platformGroup` so the one-way collider, scroll loop, and
   * `dropThroughUntilMs` semantics from regular floating platforms
   * apply unchanged — hero falls onto the canopy from the climb,
   * stands on it while it scrolls left, can drop through by holding
   * Down, and falls off when the canopy's right edge passes them.
   * Display alpha is 0 so the platform is hidden visually (the parallax
   * forest canopy is the implicit cue for "you are walking on leaves").
   */
  private spawnCanopyPlatform(
    startX: number,
    width: number,
    canopyY: number,
    clusterId: number,
  ): void {
    const plat = this.platformGroup.create(startX, canopyY, "ground") as PlatformSprite;
    plat.setOrigin(0, 0);
    plat.setDisplaySize(width, 4);
    plat.setAlpha(0);
    plat.isCanopy = true;
    plat.clusterId = clusterId;
    const body = plat.body as Phaser.Physics.Arcade.StaticBody;
    body.setSize(width, 4);
    body.updateFromGameObject();
    plat.basePosX = startX;
    plat.basePosY = canopyY;
  }

  /**
   * Spawn 5 pickups distributed evenly along the canopy length. A
   * weighted pick gives geo most often (cheap, frequent reward),
   * crystals and hearts moderately (mid value), boots and feathers
   * rarely (high value). Each pickup sits ~6 px above the canopy
   * platform top so it visually rests on the leaves and the hero
   * picks it up by auto-running through it.
   */
  private spawnCanopyPickups(startX: number, width: number, canopyY: number): void {
    const count = 5;
    const spacing = Math.floor(width / (count + 1));
    const pickupY = canopyY - 12;
    const kinds: ReadonlyArray<"geo" | "heart" | "crystal" | "boot" | "feather"> = [
      "geo",
      "crystal",
      "geo",
      "heart",
      "feather",
    ];
    for (let i = 0; i < count; i++) {
      const x = startX + spacing * (i + 1);
      const kind = kinds[i] ?? "geo";
      this.spawnCanopyPickup(x, pickupY, kind);
    }
  }

  /** Spawn a single canopy pickup. Routes to the matching group used
   *  by the existing overlap handlers so collection animations and
   *  telemetry are identical to ground spawns. */
  private spawnCanopyPickup(
    x: number,
    y: number,
    kind: "geo" | "heart" | "crystal" | "boot" | "feather",
  ): void {
    if (kind === "geo") {
      const g = this.geoGroup.create(x, y, "geo") as Phaser.Physics.Arcade.Sprite;
      g.setOrigin(0, 0);
      this.applyPickupBob(g);
      return;
    }
    const p = this.pickupGroup.create(x, y, kind) as PickupSprite;
    p.setOrigin(0, 0);
    p.kind = kind;
    this.applyPickupBob(p);
  }

  /**
   * Spawn one canopy-height bird. Same sprite + dive-bomb mechanic as
   * the regular ground-spawn `spawnBird`, just anchored at canopy
   * altitude instead of `birdBaseHeightPx`. The hero is on the canopy
   * platform while passing under the bird's wave, so the dive-bomb
   * actually threatens them — adds tension to the bonus stretch.
   */
  private spawnCanopyBird(x: number, canopyY: number): void {
    const useBat = this.textures.exists("enemy.bat.idle");
    const textureKey = useBat ? "enemy.bat.idle" : "gegner";
    const baseY = canopyY - 12;
    const b = this.gegnerGroup.create(x, baseY, textureKey) as BirdSprite;
    b.setOrigin(0, 0);
    if (useBat) {
      b.setScale(0.6);
      const body = b.body as Phaser.Physics.Arcade.Body | null;
      body?.setSize(28, 28, true);
      if (this.anims.exists("bat.idle")) b.play("bat.idle");
    } else {
      b.setTint(PALETTE.bird);
    }
    b.isBird = true;
    b.birdBaseY = baseY;
    b.birdAmplitude = GAMEPLAY.birdAmplitudePx;
    b.birdFrequencyHz = GAMEPLAY.birdFrequencyHz;
    b.birdPhase = Math.random() * Math.PI * 2;
    b.lastDiveBombMs = this.timeMs();
    this.gegnerSeen += 1;
    this.telemetry.emit(2, "bird.spawn", { tier: "canopy" });
  }

  /** Translate every vine left with the world scroll and cull off-screen. */
  private scrollVines(scrollPx: number): void {
    this.vineGroup.getChildren().forEach((obj) => {
      const v = obj as VineSprite;
      if (!v.active) return;
      v.x -= scrollPx;
      if (v.x < -16) v.destroy();
    });
  }

  /**
   * Proximity check for a vine grab. Fires only on the input edge
   * (interact press or up-axis crossing the 0.4 threshold) — see the
   * caller in `update`. Picks the nearest qualifying vine and starts
   * a climb tween. Ascent vines require the hero to be airborne and
   * below the canopy; descent vines require the hero to be roughly at
   * canopy level (i.e., standing on the canopy platform).
   */
  private tryGrabVine(): void {
    if (this.heroState !== "running") return;
    const heroBody = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (!heroBody) return;
    const heroY = this.hero.y;
    const canopyTop = GAMEPLAY.canopyTopY;
    const reach = GAMEPLAY.canopyGrabReachPx;
    const onCanopy = Math.abs(heroY - canopyTop) <= 8 && this.heroOnGround();
    const airborne = !this.heroOnGround() && heroY > canopyTop + 20;

    let best: VineSprite | undefined;
    let bestDist = Infinity;
    this.vineGroup.getChildren().forEach((obj) => {
      const v = obj as VineSprite;
      if (!v.active) return;
      const dx = Math.abs(v.x - this.hero.x);
      if (dx > reach) return;
      if (v.isAscent && !airborne) return;
      if (!v.isAscent && !onCanopy) return;
      if (dx < bestDist) {
        bestDist = dx;
        best = v;
      }
    });
    if (!best) return;
    this.startClimb(best);
  }

  /**
   * Kick off a climb tween. Hero state flips to "climbing", body
   * gravity is disabled, and the hero is snapped to the vine's x so
   * the animation visibly tracks the vine. World scroll freezes for
   * the duration via the state short-circuit at the top of `update`.
   * The tween runs over `canopyClimbDurationMs`; on complete, control
   * returns to `finalizeClimb`.
   */
  private startClimb(vine: VineSprite): void {
    const ascending = vine.isAscent === true;
    // Hero sprite uses origin (0.5, 1) with a 6-px body offset (see
    // `applyHeroBodyForAnim`); landing target offsets sprite.y by 6 so
    // body.bottom rests exactly on the platform top (matches the
    // resurrect-on-ground placement at `groundTopY - 6`).
    const heroFeetOffset = 6;
    const targetY = ascending
      ? GAMEPLAY.canopyTopY - heroFeetOffset
      : this.groundTopY - heroFeetOffset;
    this.heroState = "climbing";
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (body) {
      body.allowGravity = false;
      body.setVelocity(0, 0);
    }
    this.hero.x = vine.x;
    if (this.anims.exists("knight.idle")) this.hero.play("knight.idle", true);
    this.tweens.add({
      targets: this.hero,
      y: targetY,
      duration: GAMEPLAY.canopyClimbDurationMs,
      ease: "Sine.easeInOut",
      onComplete: () => this.finalizeClimb(ascending),
    });
    this.telemetry.emit(1, "vine.grab", {
      direction: ascending ? "ascent" : "descent",
      clusterId: vine.clusterId ?? 0,
    });
  }

  /**
   * Resolve a climb: re-enable gravity, restore the running state,
   * resume the run animation, fire telemetry. The hero's y has
   * already been tween-set to the target altitude. Gravity will catch
   * a small overshoot on the next frame; for ascent the one-way
   * canopy collider keeps the hero perched on the canopy, for descent
   * the ground collider catches them.
   */
  private finalizeClimb(ascended: boolean): void {
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (body) {
      body.allowGravity = true;
      body.setVelocity(0, 0);
    }
    this.heroState = "running";
    if (this.anims.exists("knight.run")) this.hero.play("knight.run", true);
    this.telemetry.emit(2, "vine.climb.complete", {
      direction: ascended ? "ascent" : "descent",
    });
    if (ascended) {
      this.showBanner(t("banner.canopy.title"), t("banner.canopy.subtitle"));
    }
  }

  /**
   * Cancel an in-flight climb (level change, scene shutdown). Kills
   * any tween targeting the hero, restores gravity, places the hero
   * safely on the ground at the auto-run anchor, and clears the
   * climbing state.
   */
  private abortClimb(): void {
    this.tweens.killTweensOf(this.hero);
    const body = this.hero.body as Phaser.Physics.Arcade.Body | null;
    if (body) {
      body.allowGravity = true;
      body.setVelocity(0, 0);
    }
    this.hero.setPosition(GAMEPLAY.heroX, this.groundTopY - 6);
    this.heroState = "running";
    if (this.anims.exists("knight.run")) this.hero.play("knight.run", true);
  }

  /**
   * Pop the hero back to the auto-run anchor after they fall into
   * an abyss. Costs 3 HP and triggers the white-flash resurrection
   * animation. If HP hits zero, the run ends instead.
   */
  private resurrectHero(): void {
    const drop = 3;
    const newHp = Math.max(0, this.hp - drop);
    this.hp = newHp;
    this.telemetry.emit(2, "hero.abyss_fall", { hpAfter: newHp });
    this.audio.hpLoss();
    // Slightly bigger shake on abyss because the fall is genuinely
    // catastrophic — still inside the 2 px / 80 ms budget cap.
    this.shakeCamera(2, 80);

    if (newHp <= 0) {
      this.telemetry.emit(2, "hero.down", { cause: "abyss" });
      this.endSession("hp_down");
      return;
    }

    // Snap hero back to a safe spot — auto-run anchor x, just above
    // the ground line. Clear velocity so they don't immediately fall
    // again. Brief invuln window so they don't take more damage from
    // a nearby gegner.
    const heroBody = this.hero.body as Phaser.Physics.Arcade.Body | null;
    this.hero.setPosition(GAMEPLAY.heroX, this.groundTopY - 6);
    heroBody?.setVelocity(0, 0);
    this.invulnUntilMs = this.timeMs() + GAMEPLAY.hitInvulnMs * 2;
    this.playResurrectionFlash();
    this.pulsateHurtScreen();
    this.pulsateHpBar(newHp);
  }

  /**
   * White semi-transparent overlay + blinking hero opacity during
   * resurrection. The screen flashes fully white for ~120 ms, then
   * fades over ~500 ms; the hero's alpha oscillates 0 → 1 during the
   * fade so they appear to "rematerialise" inside the flash.
   */
  private playResurrectionFlash(): void {
    if (this.resurrectionFlash) this.resurrectionFlash.destroy();
    const flash = this.add
      .rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, 0xffffff, 1.0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(18);
    this.resurrectionFlash = flash;
    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 620,
      ease: "Quad.easeOut",
      onComplete: () => {
        flash.destroy();
        if (this.resurrectionFlash === flash) {
          this.resurrectionFlash = undefined;
        }
      },
    });
    // Hero re-appears via alpha tween while the flash is fading.
    this.hero.setAlpha(0);
    this.tweens.add({
      targets: this.hero,
      alpha: 1,
      duration: 620,
      ease: "Sine.easeInOut",
    });
  }

  private spawnHeart(forceX?: number, forceY?: number): void {
    const x = forceX ?? this.findClearSpawnX();
    const y = forceY ?? this.groundTopY - Phaser.Math.RND.pick([14, 30, 46]);
    const p = this.pickupGroup.create(x, y, "heart") as PickupSprite;
    p.setOrigin(0, 0);
    p.kind = "heart";
    this.applyPickupBob(p);
    this.telemetry.emit(2, "spawn.heart");
  }

  private spawnCrystal(forceX?: number, forceY?: number): void {
    const x = forceX ?? this.findClearSpawnX();
    const y = forceY ?? this.groundTopY - Phaser.Math.RND.pick([14, 30, 46]);
    const p = this.pickupGroup.create(x, y, "crystal") as PickupSprite;
    p.setOrigin(0, 0);
    p.kind = "crystal";
    this.applyPickupBob(p);
    this.telemetry.emit(2, "spawn.crystal");
  }

  private spawnBoot(forceX?: number, forceY?: number): void {
    const x = forceX ?? this.findClearSpawnX();
    const y = forceY ?? this.groundTopY - 14;
    const p = this.pickupGroup.create(x, y, "boot") as PickupSprite;
    p.setOrigin(0, 0);
    p.kind = "boot";
    this.applyPickupBob(p);
    this.telemetry.emit(2, "spawn.boot");
  }

  /**
   * Spawn an escape feather. Placed at mid-jump height so the player
   * has to commit to a jump (sometimes a double-jump) to grab it —
   * the powerup is potent enough that costing some movement effort
   * feels right.
   */
  private spawnFeather(forceX?: number, forceY?: number): void {
    const x = forceX ?? this.findClearSpawnX();
    const y = forceY ?? this.groundTopY - 36;
    const p = this.pickupGroup.create(x, y, "feather") as PickupSprite;
    p.setOrigin(0, 0);
    p.kind = "feather";
    this.applyPickupBob(p);
    this.telemetry.emit(2, "spawn.feather");
  }

  /**
   * Consume one stored Escape-Feather charge: pop the hero up one
   * sub-level. Quiet no-op in the main loop or when no charges are
   * stored — both states are surfaced via the HUD readout so the
   * player understands why nothing happened.
   */
  private useEscapeCharge(): void {
    if (this.levelDepth === 0) {
      this.telemetry.emit(1, "feather.use.refused", { reason: "at_main" });
      return;
    }
    if (!this.escapeFeatherUnlocked) {
      this.telemetry.emit(1, "feather.use.refused", { reason: "skill_sold" });
      this.showBanner(t("banner.feather.title"), t("banner.feather.no_skill"));
      return;
    }
    if (this.escapeCharges <= 0) {
      this.telemetry.emit(1, "feather.use.refused", { reason: "no_charges" });
      this.showBanner(t("banner.feather.title"), t("banner.feather.no_charges"));
      return;
    }
    this.escapeCharges -= 1;
    this.escapesUsed += 1;
    this.telemetry.emit(2, "feather.used", {
      depthBefore: this.levelDepth,
      remaining: this.escapeCharges,
    });
    // Brief white flash to sell the "wisped upward" feel.
    this.playResurrectionFlash();
    this.audio.skillUnlock();
    this.exitSubLevel(null);
  }

  /**
   * Pick a spawn-x clear of any existing collectible. Enemies are
   * intentionally NOT in the conflict set — the user wants pickups to
   * sit close to enemies (so the player makes a risk/reward call), but
   * not on top of each other. Returns at least `NATIVE_WIDTH + 8`; if a
   * cluster of pickups is already queued just off-screen right, the
   * new spawn is pushed further right until it has `minDistance` of
   * clearance from every other collectible.
   */
  private findClearSpawnX(minDistance = 24): number {
    let candidate = NATIVE_WIDTH + 8;
    const pickups: Phaser.GameObjects.GameObject[] = [
      ...this.geoGroup.getChildren(),
      ...this.scrollGroup.getChildren(),
      ...this.pickupGroup.getChildren(),
    ];
    // Sort the existing right-of-screen pickups by x ascending and shift
    // candidate right past any that are too close. Up to 8 attempts to
    // avoid pathological loops.
    for (let attempt = 0; attempt < 8; attempt++) {
      let collided = false;
      for (const obj of pickups) {
        const s = obj as Phaser.Physics.Arcade.Sprite;
        if (!s.active) continue;
        if (s.x < NATIVE_WIDTH - 32) continue; // already well into the play area
        if (Math.abs(s.x - candidate) < minDistance) {
          candidate = s.x + minDistance;
          collided = true;
          break;
        }
      }
      if (!collided) break;
    }
    return candidate;
  }

  /**
   * Subtle vertical bob so pickups feel alive while sitting on the
   * approach. ±2 px, 700 ms cycle, yoyo+repeat. Cheap (one tween per
   * spawn, auto-destroyed when the sprite is destroyed in
   * `translateGroup`).
   */
  private applyPickupBob(sprite: Phaser.Physics.Arcade.Sprite): void {
    const startY = sprite.y;
    this.tweens.add({
      targets: sprite,
      y: startY - 2,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  /**
   * Single wind streak trailing behind the hero during a Boot speed
   * boost. A short horizontal rectangle starting at the hero's tail
   * (origin 1, 0.5 so it grows leftward), fading + drifting further
   * left over ~280 ms. Called every ~75 ms from `update()` while
   * `speedBoostUntilMs` is in the future.
   */
  private spawnBoostWindStreak(): void {
    // Hero origin is (0.5, 1) — `hero.y` is at the feet. Streaks
    // centred 14–22 px above the feet land roughly on the character's
    // torso/chest, where wind motion lines read naturally.
    const yOffset = Phaser.Math.Between(-22, -10);
    const lengthPx = Phaser.Math.Between(10, 16);
    const streak = this.add
      .rectangle(
        this.hero.x - 6,
        this.hero.y + yOffset,
        lengthPx,
        1,
        PALETTE.heroOutline,
        0.55,
      )
      .setOrigin(1, 0.5)
      .setDepth(3);
    this.tweens.add({
      targets: streak,
      x: this.hero.x - 40,
      alpha: 0,
      duration: 280,
      ease: "Quad.easeOut",
      onComplete: () => streak.destroy(),
    });
  }

  /**
   * One-shot wind burst below the hero on a successful double-jump:
   * five short downward streaks fanning out + fading. Reads as the
   * "push-off" from the mid-air second jump.
   */
  private spawnDoubleJumpBurst(): void {
    const baseY = this.hero.y + 2;
    for (let i = 0; i < 5; i++) {
      const xOffset = (i - 2) * 5; // -10, -5, 0, 5, 10
      const len = i === 2 ? 4 : 8;
      const streak = this.add
        .rectangle(
          this.hero.x + xOffset,
          baseY,
          len,
          1,
          PALETTE.geo,
          0.7,
        )
        .setOrigin(0.5, 0)
        .setDepth(3);
      this.tweens.add({
        targets: streak,
        y: baseY + 14,
        alpha: 0,
        duration: 320,
        ease: "Quad.easeOut",
        onComplete: () => streak.destroy(),
      });
    }
  }

  private onPickupOverlap(p: PickupSprite): void {
    const kind = p.kind;
    const x = p.x;
    const y = p.y;
    p.destroy();
    if (kind === "heart") {
      const restoredIndex = this.hp;
      if (this.hp < GAMEPLAY.heroMaxHp) {
        this.hp = Math.min(GAMEPLAY.heroMaxHp, this.hp + GAMEPLAY.heartHpRestore);
        this.flashHpRestored(restoredIndex);
      }
      this.heartsCollected += 1;
      this.popAt(x, y, PALETTE.heart);
      this.audio.hpGain();
      this.telemetry.emit(2, "pickup.heart", { hp: this.hp, total: this.heartsCollected });
    } else if (kind === "crystal") {
      this.geoCollected += GAMEPLAY.crystalGeoValue;
      this.geoTowardDoubleJumpReUnlock += GAMEPLAY.crystalGeoValue;
      this.incomeTracker?.recordGeo(GAMEPLAY.crystalGeoValue);
      this.crystalsCollected += 1;
      this.advancePuzzle("crystals");
      this.popAt(x, y, PALETTE.crystal);
      this.pulseGeoCounter();
      this.audio.geoCollect();
      this.telemetry.emit(2, "pickup.crystal", {
        geo: this.geoCollected,
        total: this.crystalsCollected,
      });
      this.maybeReUnlockDoubleJump("pickup");
    } else if (kind === "feather") {
      if (this.escapeCharges < GAMEPLAY.featherMaxCharges) {
        this.escapeCharges += 1;
        this.incomeTracker?.recordFeather(1);
        this.feathersCollected += 1;
        // Re-unlock the escape-feather skill if it was sold at the shop.
        if (!this.escapeFeatherUnlocked) {
          this.reunlockSkill("escapeFeather", "pickup");
        }
        this.popAt(x, y, PALETTE.feather);
        this.audio.inventoryAdd();
        this.showBanner(
          t("banner.escape_feather.title"),
          t("banner.escape_feather.subtitle", { stored: this.escapeCharges }),
        );
        this.telemetry.emit(2, "pickup.feather", {
          stored: this.escapeCharges,
          total: this.feathersCollected,
        });
      } else {
        // Capped — still pop a visual but the count doesn't change.
        this.popAt(x, y, PALETTE.feather);
        this.telemetry.emit(2, "pickup.feather.capped");
      }
    } else if (kind === "boot") {
      this.speedBoostUntilMs = this.timeMs() + GAMEPLAY.speedBoostDurationSec * 1000;
      this.bootsCollected += 1;
      this.popAt(x, y, PALETTE.boot);
      this.audio.skillUnlock(); // re-use the triad as a "power-up" beat
      this.showBanner(
        t("banner.speed_boost.title"),
        t("banner.speed_boost.subtitle", { sec: GAMEPLAY.speedBoostDurationSec }),
      );
      if (this.bootIcon) {
        this.bootIcon.clearTint();
        this.bootIcon.setAlpha(1.0);
        this.tweens.add({
          targets: this.bootIcon,
          scale: { from: 1.6, to: 1.0 },
          duration: 260,
          ease: "Back.easeOut",
        });
        // Continuous alpha pulse while the boost is in flight — makes
        // "active temporary skill" visually distinct from the static
        // permanent skills in the same strip. Stopped in paintHud when
        // speedBoostUntilMs expires.
        this.bootIconPulse?.stop();
        this.bootIconPulse = this.tweens.add({
          targets: this.bootIcon,
          alpha: { from: 1.0, to: 0.55 },
          duration: 380,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        });
      }
      this.telemetry.emit(2, "pickup.boot", {
        untilMs: Math.round(this.speedBoostUntilMs),
        total: this.bootsCollected,
      });
    }
  }

  /** Brief green-yellow scale-up on the just-restored HP slot. Counterpart to `pulsateHpBar` for damage. */
  private flashHpRestored(index: number): void {
    const slot = this.hpRects[index];
    if (!slot) return;
    const flash = this.add
      .rectangle(slot.x + 2.5, slot.y + 3, 11, 12, PALETTE.heart, 0.85)
      .setOrigin(0.5, 0.5)
      .setDepth(2);
    this.tweens.add({
      targets: flash,
      scaleX: 1.6,
      scaleY: 1.6,
      alpha: 0,
      duration: 280,
      ease: "Quad.easeOut",
      onComplete: () => flash.destroy(),
    });
  }

  // --- overlap handlers ----------------------------------------------

  private onGeoOverlap(g: Phaser.Physics.Arcade.Sprite): void {
    this.popAt(g.x, g.y, PALETTE.geo);
    g.destroy();
    this.geoCollected += 1;
    this.geoTowardDoubleJumpReUnlock += 1;
    this.incomeTracker?.recordGeo(1);
    this.telemetry.emit(2, "geo.collected", { total: this.geoCollected });
    this.pulseGeoCounter();
    this.audio.geoCollect();
    this.maybeReUnlockDoubleJump("geo_threshold");
  }

  private onScrollOverlap(s: ScrollSprite): void {
    const kind: InventoryItemKind = s.kind ?? "scroll";
    this.popAt(s.x, s.y, PALETTE.scroll);
    s.destroy();
    const accepted = this.addInventory(kind, this.timeMs());
    if (!accepted) {
      this.telemetry.emit(2, "inventory.full", { kind });
    }
  }

  private onGegnerOverlap(g: Phaser.Physics.Arcade.Sprite): void {
    const nowMs = this.timeMs();
    if (nowMs < this.invulnUntilMs) return; // i-frames; pass through harmlessly.
    g.destroy();
    const drainedIndex = this.hp - 1; // slot about to empty (0-indexed)
    this.hp = Math.max(0, this.hp - 1);
    this.invulnUntilMs = nowMs + GAMEPLAY.hitInvulnMs;
    this.pulsateHurtScreen();
    this.pulsateHpBar(drainedIndex);
    this.audio.hpLoss();
    // Capped shake (≤2 px / ≤80 ms) reinforces the hit without
    // breaking the moving-vehicle motion-sickness budget.
    this.shakeCamera(2, 80);
    this.telemetry.emit(2, "hero.hit", { hp: this.hp });
    if (this.hp <= 0) {
      this.telemetry.emit(2, "hero.down");
      this.endSession("hp_down");
    }
  }

  /**
   * Subtle full-screen red pulse on hit — three quick alpha pulses from
   * 0 → ~0.28 → 0 over ~600 ms. Reinforced by the red character tint
   * blink in `update()` so both the world and the avatar share the
   * "you got hurt" beat without using screen shake (Visual Brief §7
   * caps shake at 2 px / 80 ms for the moving-car study population).
   */
  private pulsateHurtScreen(): void {
    const overlay = this.add
      .rectangle(0, 0, NATIVE_WIDTH, NATIVE_HEIGHT, PALETTE.hp, 0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(15);
    this.tweens.add({
      targets: overlay,
      alpha: { from: 0, to: 0.28 },
      duration: 100,
      yoyo: true,
      repeat: 2,
      ease: "Sine.easeInOut",
      onComplete: () => overlay.destroy(),
    });
  }

  /**
   * HP bar pulse on hit. Two synchronised beats:
   *   - All HP rects' alpha pulses 1 → ~0.35 → 1 three times (same
   *     rhythm as `pulsateHurtScreen` so the HUD and the world share
   *     the "ouch" cadence).
   *   - The slot that just emptied gets a brief red flash overlay
   *     that contracts and fades, so the player can SEE which HP
   *     point they lost without staring at the bar.
   *
   * The fillColor swap from `PALETTE.hp` to `PALETTE.hpEmpty` is
   * handled by the per-frame `paintHud()` — we only tween alpha /
   * scale here so the two systems don't fight.
   */
  private pulsateHpBar(drainedIndex: number): void {
    if (this.hpRects.length === 0) return;
    this.tweens.killTweensOf(this.hpRects);
    this.hpRects.forEach((r) => r.setAlpha(1));
    this.tweens.add({
      targets: this.hpRects,
      alpha: { from: 1, to: 0.35 },
      duration: 110,
      yoyo: true,
      repeat: 2,
      ease: "Sine.easeInOut",
      onComplete: () => this.hpRects.forEach((r) => r.setAlpha(1)),
    });

    const slot = this.hpRects[drainedIndex];
    if (!slot) return;
    // HUD HP rects are 5×6 with origin (0, 0); centre the flash on
    // the slot's middle so the scale-down reads as a "drain pulse"
    // collapsing into nothing.
    const flash = this.add
      .rectangle(slot.x + 2.5, slot.y + 3, 11, 12, PALETTE.hp, 0.95)
      .setOrigin(0.5, 0.5)
      .setDepth(2);
    this.tweens.add({
      targets: flash,
      scaleX: 0.3,
      scaleY: 0.3,
      alpha: 0,
      duration: 260,
      ease: "Quad.easeOut",
      onComplete: () => flash.destroy(),
    });
  }

  /**
   * Camera shake with a hard budget cap. The Visual Brief §7 caps
   * camera shake at 2 px amplitude / 80 ms duration because
   * participants may be running the build inside a moving-vehicle
   * simulator and large jolts trigger motion sickness. Call sites can
   * request whatever they want; this helper clamps to the budget
   * before handing the request to Phaser's camera shake.
   *
   * `amplitudePx` is the requested pixel amplitude (interpreted in
   * native-res space). `durationMs` is the requested duration. Both
   * are silently clamped — no warning logged, the cap is the contract.
   */
  private shakeCamera(amplitudePx: number, durationMs: number): void {
    const ampPx = Math.max(0, Math.min(amplitudePx, GAMEPLAY.shakeMaxAmplitudePx));
    const dur = Math.max(0, Math.min(durationMs, GAMEPLAY.shakeMaxDurationMs));
    if (ampPx === 0 || dur === 0) return;
    // Phaser's camera.shake takes a *normalized* intensity (fraction of
    // viewport height). Convert the pixel-amplitude into the equivalent
    // normalized value against NATIVE_HEIGHT so the budget is honoured
    // in source pixels regardless of the integer scale factor applied
    // by the Scale Manager.
    const intensity = ampPx / NATIVE_HEIGHT;
    this.cameras.main.shake(dur, intensity, true);
  }

  // --- feedback / HUD paint ------------------------------------------

  private popAt(x: number, y: number, color: number): void {
    const r = this.add.rectangle(x, y, 8, 8, color, 1).setDepth(5);
    this.tweens.add({
      targets: r,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 220,
      ease: "Quad.easeOut",
      onComplete: () => r.destroy(),
    });
  }

  private pulseGeoCounter(): void {
    this.geoText.setScale(1.4);
    this.tweens.add({
      targets: this.geoText,
      scaleX: 1.0,
      scaleY: 1.0,
      duration: 180,
      ease: "Quad.easeOut",
    });
  }

  private flashHero(color: number): void {
    this.hero.setTint(color);
    this.time.delayedCall(120, () => this.hero.clearTint());
  }

  private refreshInventoryHud(): void {
    this.walletHud.refreshInventory(this.inventory);
  }

  private paintHud(): void {
    const nowMs = this.timeMs();
    const boostRemainSec = (this.speedBoostUntilMs - nowMs) / 1000;
    const boostActive = boostRemainSec > 0;

    // Shared visuals — HP rail, geo counter, skill icons, boot slot,
    // inventory contents. paintHud is called every frame so the update
    // call is the single point of truth for the readouts.
    this.walletHud.update({
      hp: this.hp,
      geo: this.geoCollected,
      inventory: this.inventory,
      skills: this.skills,
      bootBoostActive: boostActive,
    });
    // The boot pulse animation is the runner's concern (continuous alpha
    // pulse while a boost is in flight). WalletHud's update() already
    // restores the static tint/alpha when boost ends, so we only need to
    // tear the pulse tween down here.
    if (!boostActive && this.bootIconPulse) {
      this.bootIconPulse.stop();
      this.bootIconPulse = undefined;
    }

    const presetLabel = this.cfg.backgroundId && this.cfg.backgroundId !== DEFAULT_BACKGROUND_ID
      ? this.lookupPresetLabel(this.cfg.backgroundId)
      : "";
    const variantSuffix = this.cfg.variant === "B" && presetLabel
      ? `  ·  ${t("hud.bottom.bg")}: ${presetLabel}`
      : "";
    const boostSuffix = boostActive
      ? `  ·  ${t("hud.bottom.boost")} ${boostRemainSec.toFixed(1)}s`
      : "";
    const depthSuffix = this.levelDepth > 0
      ? `  ·  ${t("hud.bottom.depth")} ${this.levelDepth}`
      : "";
    const puzzleSuffix = this.puzzle
      ? `  ·  ${this.puzzle.solved ? t("hud.bottom.solved") : `${t(this.puzzle.labelKey)} ${this.puzzle.progress}/${this.puzzle.target}`}`
      : "";
    const featherSuffix = this.escapeCharges > 0
      ? `  ·  ♟︎${t("hud.bottom.feather")} ×${this.escapeCharges}`
      : "";
    this.bottomRailText.setText(
      `${t("hud.bottom.chunk")}: ${this.chunkIndex}  ·  ${t("hud.bottom.defeated")}: ${this.gegnerDefeated}  ·  ${t("hud.bottom.sidequests")}: ${this.sidequestsAccepted}${depthSuffix}${puzzleSuffix}${featherSuffix}${boostSuffix}  ·  ${t("hud.bottom.abort")}${variantSuffix}`,
    );
    if (this.tutorialTag) {
      const remaining = Math.max(0, Math.ceil(this.durationLimitSec - this.elapsedSec));
      this.tutorialTag.setAlpha(remaining > 0 ? 1 : 0);
    }
    this.paintMiniMap();
    this.paintDebugOverlay();
  }

  // --- tooltips -------------------------------------------------------

  private buildTooltipPanel(): void {
    this.tooltipBg = this.add
      .rectangle(0, 0, 4, 12, PALETTE.hudPanel, 0.96)
      .setOrigin(0, 0)
      .setStrokeStyle(1, PALETTE.hudDim)
      .setDepth(30)
      .setVisible(false);
    this.tooltipText = this.add
      .text(0, 0, "", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0, 0)
      .setDepth(31)
      .setVisible(false);
  }

  private attachTooltip(
    target: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image,
    textProvider: () => string,
  ): void {
    target.setInteractive({ useHandCursor: false });
    target.on("pointerover", () => this.showTooltipFor(target, textProvider()));
    target.on("pointermove", () => this.showTooltipFor(target, textProvider()));
    target.on("pointerout", () => this.hideTooltip());
  }

  private showTooltipFor(
    target: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image,
    text: string,
  ): void {
    if (!this.tooltipBg || !this.tooltipText) return;
    this.tooltipText.setText(text);
    const padding = 4;
    const w = Math.ceil(this.tooltipText.width) + padding * 2;
    const h = 12;
    // Anchor: just below the target's centre. Clamp to viewport.
    const ax = target.x + (target.displayWidth ?? 0) / 2;
    const ay = target.y + (target.displayHeight ?? 0) / 2;
    let x = Math.round(ax - w / 2);
    let y = Math.round(ay + 10);
    if (x < 2) x = 2;
    if (x + w > NATIVE_WIDTH - 2) x = NATIVE_WIDTH - w - 2;
    if (y + h > NATIVE_HEIGHT - 14) y = Math.round(ay - h - 4);
    this.tooltipBg.setSize(w, h).setPosition(x, y).setVisible(true);
    this.tooltipText.setPosition(x + padding, y + 2).setVisible(true);
  }

  private hideTooltip(): void {
    this.tooltipBg?.setVisible(false);
    this.tooltipText?.setVisible(false);
  }

  private tooltipForSkill(s: Skill): string {
    switch (s) {
      case "jump":
        return t("tooltip.skill.jump");
      case "doubleJump":
        return this.skills.doubleJump
          ? t("tooltip.skill.double_jump")
          : t("tooltip.skill.double_jump.locked", { geo: GAMEPLAY.doubleJumpUnlockGeo });
      case "attack":
        return t("tooltip.skill.attack");
      case "interact":
        return t("tooltip.skill.interact");
    }
  }

  private tooltipForBoot(): string {
    const remaining = (this.speedBoostUntilMs - this.timeMs()) / 1000;
    return remaining > 0
      ? t("tooltip.boot.active", { sec: remaining.toFixed(1) })
      : t("tooltip.boot.inactive");
  }

  private lookupPresetLabel(id: string): string {
    return BACKGROUND_PRESETS.find((p) => p.id === id)?.label ?? id;
  }

  // --- banner ---------------------------------------------------------

  private showBanner(
    title: string,
    subtitle: string,
    opts?: { avatar?: AuthorIdentity; authorSeed?: number },
  ): void {
    if (this.banner) {
      this.banner.destroy();
      ProfileTooltipService.get(this.game)?.hide("forced");
    }
    const cx = NATIVE_WIDTH / 2;
    const c = this.add.container(cx, 50).setDepth(10);
    const bg = this.add
      .rectangle(0, 0, NATIVE_WIDTH - 80, 28, PALETTE.bannerBg, 0.92)
      .setStrokeStyle(1, PALETTE.bannerText);
    // If an author avatar was supplied, render it on the left and shift the
    // title text rightward to make room. The avatar uses the same procedural
    // pixel grid the AuthorBadge does.
    const textOffsetX = opts?.avatar ? 16 : 0;
    if (opts?.avatar && this.authorIdentityService) {
      const halfWidth = (NATIVE_WIDTH - 80) / 2;
      const avatar = this.authorIdentityService.drawAvatar(this, -halfWidth + 4, -10, opts.avatar, 1);
      avatar.setDisplaySize(20, 20);
      c.add(avatar);
      // UGC corner mark on the banner's right edge.
      const mark = this.add.rectangle(halfWidth - 4, -10, 3, 3, UGC_CYAN, 1).setOrigin(0, 0);
      c.add(mark);
    }
    const titleText = this.add
      .text(textOffsetX, -6, title, {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(PALETTE.bannerText),
      })
      .setOrigin(0.5, 0.5);
    c.add([bg, titleText]);
    // Author banners render the subtitle as prefix + inline 8×8 avatar +
    // handle so the avatar reads as part of "erstellt von [pic] @name".
    // The i18n template is always "... @{name}", so splitting at "@" yields
    // a clean prefix / handle pair.
    const atIdx = opts?.avatar && this.authorIdentityService ? subtitle.indexOf("@") : -1;
    if (opts?.avatar && this.authorIdentityService && atIdx >= 0) {
      const prefixStr = subtitle.slice(0, atIdx);
      const handleStr = subtitle.slice(atIdx);
      const subtitleStyle = {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudText),
      };
      const prefixText = this.add.text(0, 7, prefixStr, subtitleStyle).setOrigin(0, 0.5);
      const handleText = this.add.text(0, 7, handleStr, subtitleStyle).setOrigin(0, 0.5);
      const inlineAvatar = this.authorIdentityService.drawAvatar(this, 0, 7, opts.avatar, 1);
      inlineAvatar.setDisplaySize(8, 8).setOrigin(0, 0.5);
      const GAP = 2;
      const totalW = prefixText.width + GAP + 8 + GAP + handleText.width;
      const leftEdge = textOffsetX - totalW / 2;
      prefixText.setX(leftEdge);
      inlineAvatar.setX(leftEdge + prefixText.width + GAP);
      handleText.setX(leftEdge + prefixText.width + GAP + 8 + GAP);
      c.add([prefixText, inlineAvatar, handleText]);
    } else {
      const subText = this.add
        .text(textOffsetX, 7, subtitle, {
          fontFamily: "monospace",
          fontSize: "7px",
          color: this.toHex(PALETTE.hudText),
        })
        .setOrigin(0.5, 0.5);
      c.add(subText);
    }
    c.setAlpha(0);
    this.tweens.add({
      targets: c,
      alpha: 1,
      duration: 180,
      ease: "Quad.easeOut",
    });
    this.banner = c;
    this.bannerHideAtMs = this.timeMs() + GAMEPLAY.bannerDurationMs;
    this.telemetry.emit(1, "ui.banner.shown", { title, subtitle });

    // Author banners expose a hover hit zone routing to the profile tooltip.
    // Variant A banners and non-author banners (puzzles, skill-unlock) just
    // skip this branch.
    if (opts?.avatar && opts.authorSeed !== undefined) {
      const profileSvc = ProfileTooltipService.get(this.game);
      if (profileSvc) {
        const bw = NATIVE_WIDTH - 80;
        const bh = 28;
        bg.setInteractive(new Phaser.Geom.Rectangle(-bw / 2, -bh / 2, bw, bh), Phaser.Geom.Rectangle.Contains);
        const seed = opts.authorSeed;
        const cx = NATIVE_WIDTH / 2;
        bg.on("pointerover", () => {
          profileSvc.show(seed, {
            source: "banner",
            trigger: "hover",
            anchor: { x: cx - bw / 2, y: 50 - bh / 2, width: bw, height: bh },
          });
        });
        bg.on("pointerout", () => {
          profileSvc.cancelPending();
          profileSvc.hide("mouse_leave");
        });
      }
    }

    // Audio duck stub — real ducking lands with audio in v0.4. Recording the
    // intent here so the analyst pipeline (PRD §10) sees where the duck
    // would have fired even before sound is wired.
    this.telemetry.emit(1, "audio.duck.stub", { reason: "banner", db: -6 });
  }

  // --- Variant B UGC plumbing -----------------------------------------

  /**
   * Boots up the shop service + wallet + author badge for Variant B. Only
   * called from `create()` when `cfg.variant === "B"`. Idempotent at the
   * shop-service level (ShopService.getOrCreate dedupes via registry).
   */
  private setupUgcFeatures(): void {
    this.authorIdentityService = this.registry.get("authorIdentityService") as
      | AuthorIdentityService
      | undefined;
    if (!this.authorIdentityService) {
      this.authorIdentityService = new AuthorIdentityService();
      this.registry.set("authorIdentityService", this.authorIdentityService);
    }
    // Stash telemetry into the registry so ShopScene can find it.
    this.registry.set("telemetryService", this.telemetry);

    this.incomeTracker = new WalletIncomeTracker();
    const host: RunnerWalletHost = {
      getGeo: () => this.getGeo(),
      addGeo: (delta, src) => this.addGeo(delta, src),
      getHp: () => this.getHp(),
      getMaxHp: () => this.getMaxHp(),
      setHp: (v, src) => this.setHp(v, src),
      getFeatherCharges: () => this.getFeatherCharges(),
      setFeatherCharges: (v, src) => this.setFeatherCharges(v, src),
      getInventory: () => this.getInventory(),
      addInventoryItem: (id, src) => this.addInventoryItem(id, src),
      removeInventoryItem: (id, src) => this.removeInventoryItem(id, src),
      isSkillUnlocked: (id) => this.isSkillUnlocked(id),
      relockSkill: (id, src) => this.relockSkill(id, src),
      reunlockSkill: (id, src) => this.reunlockSkill(id, src),
    };
    this.runnerWallet = new RunnerWallet(host, this.incomeTracker);
    this.registry.set("runnerWallet", this.runnerWallet);

    // ShopService is a registry-held singleton; we drop the reference once
    // the service is wired up — RunnerScene doesn't call back into it
    // directly (ShopScene reads from the registry on entry).
    ShopService.getOrCreate({
      game: this.game,
      identity: this.authorIdentityService,
      getCurrentWallet: (): Wallet | undefined => this.runnerWallet,
      emitTelemetry: (l, t, d) => this.telemetry.emit(l, t, d),
      now: () => this.timeMs(),
    });

    // Author badge below the skills strip on the left side (skills strip
    // ends at y=32). Compact 14×14 box + name; doesn't collide with HP,
    // skills, or pause button.
    this.authorBadge = new AuthorBadge(this, 4, 36, this.authorIdentityService);
    this.authorBadge.container.setDepth(15);
    this.authorBadge.setOnClick(() => this.openAuthorProfile("hud"));

    // Main run author seed.
    this.currentLevelAuthorSeed = mainLevelAuthorSeed(
      this.cfg.participantId,
      Math.round(this.cfg.startedAt),
    );
    const identity = this.authorIdentityService.forSeed(this.currentLevelAuthorSeed);
    this.authorBadge.setIdentity(identity);
    this.showAuthorBanner(identity, t("banner.main_level"));
    this.telemetry.emit(1, "ugc.author.banner.shown", {
      levelKey: "main",
      authorUsername: identity.username,
      avatarSeed: this.currentLevelAuthorSeed,
    });

    // Shop button in the HUD, opposite side from the author badge.
    this.buildShopButton();

    // Authored-sidequest plumbing — service + RESUME listener that
    // drains pending sidequests into world portals every time a child
    // scene (editor / shop / future modal) hands control back.
    this.authoredService = AuthoredSidequestService.getOrCreate({
      game: this.game,
      emitTelemetry: (l, tag, d) => this.telemetry.emit(l, tag, d),
      now: () => this.timeMs(),
    });
    this.events.on(Phaser.Scenes.Events.RESUME, this.onSceneResumed, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.RESUME, this.onSceneResumed, this);
      this.authorTowerOverlays.forEach((o) => o.destroy());
      this.authorTowerOverlays.clear();
    });
  }

  /** Drain any pending authored sidequests into world portals. Called on
   *  every `Phaser.Scenes.Events.RESUME` so editor saves and scroll
   *  shop visits both have a chance to react. */
  private onSceneResumed(): void {
    let placedSomething = false;
    if (this.isUgcVariant) {
      const authored = this.authoredService;
      if (authored) {
        const pending = authored.pendingForDepth(this.levelDepth);
        for (const sidequest of pending) {
          const worldX = this.spawnAuthoredTower(sidequest);
          authored.markPlaced(sidequest.id, worldX, this.levelDepth);
          placedSomething = true;
        }
      }
    }
    if (placedSomething) {
      // After save+place: clear pause AND drop the hero back into the
      // same `idle` state they started the run in. The world freezes
      // (parallax, scroll, spawners) until the player presses jump /
      // attack / interact / move, at which point transitionToRunning
      // resumes the auto-runner and they can step into their fresh
      // portal. Without this, the world would auto-scroll past the
      // newly-placed tower in a couple of seconds.
      this.paused = false;
      this.hidePauseOverlay();
      this.paintPauseButton();
      this.heroState = "idle";
      if (this.anims.exists("knight.idle")) this.hero.play("knight.idle", true);
      this.telemetry.emit(1, "ugc.sidequest.placed_idle_gate", {});
    } else if (this.paused) {
      // Cancel / Market visit returned us to a paused runner with no
      // overlay (openAuthor/openMarketFromPause hid it). Re-show it so
      // the player sees the menu and can resume or take another action.
      this.showPauseOverlay();
    }
  }

  /**
   * Spawn an entry portal for an authored sidequest. Returns the chosen
   * world x. Position is biased forward of the hero so the player sees
   * the portal appear and can run into it (auto-runner can't backtrack);
   * we scan up to `authoredTowerSpawnLookaheadX` for a spawn x that
   * avoids the current abyss / swamp segment, falling back to the
   * lookahead end if everything is blocked.
   */
  private spawnAuthoredTower(sidequest: AuthoredSidequest): number {
    const offset = GAMEPLAY.authoredTowerSpawnOffsetX;
    const step = GAMEPLAY.authoredTowerSpawnStepX;
    const maxScan = GAMEPLAY.authoredTowerSpawnLookaheadX;
    let x = this.hero.x + offset;
    let scanned = 0;
    while (scanned < maxScan && (this.isWorldXInAbyss(x) || this.isWorldXInSwamp(x))) {
      x += step;
      scanned += step;
    }
    const t = this.towerGroup.create(x, this.groundTopY, "tower.down") as SidequestTowerSprite;
    t.setOrigin(0.5, 1);
    t.setScale(0.5);
    t.sidequestId = sidequest.id;
    t.authoredSidequestId = sidequest.id;
    t.accepted = false;

    // Overlay: avatar + handle floating above the portal. Anchored y at
    // the tower's visual top (origin 0.5, 1 → top is groundTopY - height).
    if (this.authorIdentityService) {
      const identity = this.authorIdentityService.forSeed(sidequest.authorAvatarSeed);
      const towerTopY = this.groundTopY - GAMEPLAY.authorTowerOverlayOffsetY - 24;
      const overlay = new AuthorTowerOverlay(this, x, towerTopY, this.authorIdentityService, identity);
      this.authorTowerOverlays.set(t, overlay);
    }
    this.telemetry.emit(2, "sidequest.tower.spawn", { id: t.sidequestId, authored: true });
    return x;
  }

  /**
   * Variant of `enterSubLevel` that overrides the sub-level's name,
   * biome, puzzle, and spawns the authored set pieces at predictable
   * positions just ahead of the hero. Shares the existing context push
   * / parallax swap / banner machinery wherever possible.
   */
  private enterAuthoredSubLevel(sidequest: AuthoredSidequest, tower: SidequestTowerSprite): void {
    // Run the standard entry first — pushes context, increments depth,
    // resets timers, applies the depth-based parallax pack, rolls a
    // puzzle. We then OVERRIDE the puzzle + banner with the authored
    // values, and spawn the set pieces.
    this.enterSubLevel(tower);
    // Override the puzzle kind: replace whatever rollPuzzleForSubLevel
    // chose with the authored kind (still in the same goal-target
    // configuration as the existing puzzle types).
    this.puzzle = this.makePuzzleForKind(sidequest.puzzleKind);
    // Spawn set pieces at hero-relative positions so they appear within
    // the entry viewport and scroll left naturally.
    const baseWorldX = this.hero.x + 32;
    for (const id of sidequest.setPieceIds) {
      const sp = setPieceById(id);
      if (!sp) continue;
      spawnSetPiece(sp, baseWorldX, (kind, worldX, dy) => this.spawnSetPieceEntity(kind, worldX, dy));
    }
    // Author-attributed banner using the player's identity (not the AI
    // procedural seed used by enterSubLevel via announceLevelAuthor).
    if (this.authorIdentityService) {
      const identity = this.authorIdentityService.forSeed(sidequest.authorAvatarSeed);
      this.showAuthorBanner(identity, sidequest.name);
    }
    this.authoredService?.markEntered(sidequest.id);
  }

  /** Resolve a puzzle config from a PuzzleKind picked by the editor. */
  private makePuzzleForKind(kind: PuzzleKind): typeof this.puzzle {
    if (kind === "kills") {
      return { kind, progress: 0, target: GAMEPLAY.puzzleGoalKills, labelKey: "puzzle.label.kills", solved: false };
    }
    if (kind === "crystals") {
      return { kind, progress: 0, target: GAMEPLAY.puzzleGoalCrystals, labelKey: "puzzle.label.crystals", solved: false };
    }
    return { kind, progress: 0, target: 1, labelKey: "puzzle.label.lever", solved: false };
  }

  /**
   * Keep AuthorTowerOverlay positions in sync with their towers each
   * frame. Translates the overlay x along with the world scroll and
   * culls overlays whose tower has been destroyed (accepted /
   * off-screen left).
   */
  private syncAuthorTowerOverlays(scrollPx: number): void {
    for (const [tower, overlay] of this.authorTowerOverlays) {
      if (!tower.active || tower.x < -32) {
        overlay.destroy();
        this.authorTowerOverlays.delete(tower);
        continue;
      }
      overlay.translateX(-scrollPx);
    }
  }

  /** Dispatch one set-piece entity to the right spawn method. */
  private spawnSetPieceEntity(kind: "bird" | "gegner" | "lever" | "crystal" | "heart" | "boot" | "feather" | "portal", worldX: number, dy: number): void {
    switch (kind) {
      case "bird":
        this.spawnBird(worldX, this.groundTopY - GAMEPLAY.birdBaseHeightPx + dy);
        break;
      case "gegner":
        this.spawnGegner(worldX);
        break;
      case "lever":
        this.maybeSpawnLever(worldX);
        break;
      case "crystal":
        this.spawnCrystal(worldX, this.groundTopY + dy);
        break;
      case "heart":
        this.spawnHeart(worldX, this.groundTopY + dy);
        break;
      case "boot":
        this.spawnBoot(worldX, this.groundTopY + dy);
        break;
      case "feather":
        this.spawnFeather(worldX, this.groundTopY + dy);
        break;
      case "portal":
        this.spawnTower(worldX);
        break;
    }
    void SET_PIECE_CATALOG; // touch import to keep tree-shaking out of the way
  }

  private showAuthorBanner(identity: AuthorIdentity, levelName: string): void {
    this.showBanner(
      levelName,
      t("banner.author.subtitle", { name: identity.username }),
      { avatar: identity, authorSeed: this.currentLevelAuthorSeed },
    );
  }

  /** Open the click-modal profile tooltip from the runner HUD AuthorBadge. */
  private openAuthorProfile(source: "hud"): void {
    if (!this.isUgcVariant) return;
    const profileSvc = ProfileTooltipService.get(this.game);
    if (!profileSvc) return;
    const badgeX = this.authorBadge?.container.x ?? 4;
    const badgeY = this.authorBadge?.container.y ?? 36;
    profileSvc.show(this.currentLevelAuthorSeed, {
      source,
      trigger: "click",
      immediate: true,
      modalForSceneKey: "runner",
      anchor: { x: badgeX, y: badgeY, width: AUTHOR_BADGE_WIDTH, height: AUTHOR_BADGE_HEIGHT },
    });
  }

  private buildShopButton(): void {
    // Aligned directly below the inventory rail (inventory: x=NATIVE_WIDTH-50,
    // y=18, w=46, h=14). Button matches the inventory rail's width so the
    // two read as a stacked column instead of a stray icon.
    const x = NATIVE_WIDTH - 50;
    const y = 36;
    const w = 46;
    const h = 14;
    const c = this.add.container(x, y).setDepth(15);
    const bg = this.add
      .rectangle(0, 0, w, h, PALETTE.shopWall, 0.95)
      .setOrigin(0, 0)
      .setStrokeStyle(1, UGC_CYAN);
    const label = this.add
      .text(w / 2, h / 2, `$ ${t("shop.title")}`, {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.geo),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    const corner = this.add.rectangle(w - 4, 2, 2, 2, UGC_CYAN, 1).setOrigin(0, 0);
    c.add([bg, label, corner]);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", () => this.openShopFromRunner());
  }

  private openShopFromRunner(): void {
    if (!this.isUgcVariant) return;
    this.telemetry.emit(1, "ugc.shop.button.click", { source: "runner_hud" });
    this.scene.pause();
    this.scene.launch("ShopScene", { returnTo: "runner", walletSource: "runner" });
  }

  /** Called by sub-level push/pop to refresh banner + badge + telemetry. */
  private announceLevelAuthor(levelKey: string, biome: string, levelSeed: number, displayName: string): void {
    if (!this.isUgcVariant || !this.authorIdentityService) return;
    const depth = this.levelDepth;
    this.currentLevelAuthorSeed = subLevelAuthorSeed(depth, biome, levelSeed);
    const identity = this.authorIdentityService.forSeed(this.currentLevelAuthorSeed);
    this.authorBadge?.setIdentity(identity);
    this.showAuthorBanner(identity, displayName);
    this.telemetry.emit(1, "ugc.author.banner.shown", {
      levelKey,
      authorUsername: identity.username,
      avatarSeed: this.currentLevelAuthorSeed,
    });
  }

  /** Reset to the main-run author after a sub-level pop. */
  private restoreMainAuthor(): void {
    if (!this.isUgcVariant || !this.authorIdentityService) return;
    this.currentLevelAuthorSeed = mainLevelAuthorSeed(
      this.cfg.participantId,
      Math.round(this.cfg.startedAt),
    );
    const identity = this.authorIdentityService.forSeed(this.currentLevelAuthorSeed);
    this.authorBadge?.setIdentity(identity);
  }

  // --- session flow ---------------------------------------------------

  private abortToLauncher(): void {
    this.telemetry.emit(1, "scene.exit", { scene: "runner", reason: "pause.abort" });
    this.telemetry.flush();
    this.scene.start("launcher");
  }

  private endSession(reason: "timeup" | "hp_down"): void {
    this.telemetry.emit(1, "scene.exit", { scene: "runner", reason });
    this.telemetry.flush();
    const variant: Variant = this.cfg.variant;
    // Author identity of the level just played — drives the Variant-B
    // post-session Review-System rating screen.
    const levelAuthorName =
      variant === "B"
        ? this.authorIdentityService?.forSeed(this.currentLevelAuthorSeed).username
        : undefined;
    const summary: SessionSummary = {
      variant,
      participantId: this.cfg.participantId,
      durationSec: Math.round(this.elapsedSec),
      geoCollected: this.geoCollected,
      sidequestsCleared: this.sidequestsCleared,
      sidequestsAccepted: this.sidequestsAccepted,
      gegnerEncounters: this.gegnerSeen,
      gegnerDefeated: this.gegnerDefeated,
      attacksLanded: this.attacksLanded,
      inventoryItems: this.inventoryItemsCollected,
      doubleJumpsPerformed: this.doubleJumpsPerformed,
      heartsCollected: this.heartsCollected,
      crystalsCollected: this.crystalsCollected,
      bootsCollected: this.bootsCollected,
      towersAuthored: 0,
      backgroundUploadsApplied:
        variant === "B" && (this.cfg.backgroundId ?? DEFAULT_BACKGROUND_ID) !== DEFAULT_BACKGROUND_ID
          ? 1
          : 0,
      backgroundId: this.cfg.backgroundId ?? DEFAULT_BACKGROUND_ID,
      endReason: reason,
      levelAuthorSeed: this.currentLevelAuthorSeed,
      levelAuthorName,
    };
    // Variant B routes through the Review-System rating screen first; the
    // rating screen then hands off to the EndScene summary. Launch it OVER the
    // paused runner (rather than start, which would stop the runner) so the
    // game stays visible around the rating popup frame; RatingScene stops the
    // runner when it advances to the EndScene.
    if (variant === "B") {
      this.scene.pause();
      this.scene.launch("RatingScene", { summary, runnerKey: this.scene.key });
    } else {
      this.scene.start("end", summary);
    }
  }

  // --- utilities ------------------------------------------------------

  private timeMs(): number {
    return this.time.now;
  }

  private toHex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }
}

// Silence "unused import" warnings for tooling that doesn't catch type-only re-exports.
void UGC_CYAN;
